/**
 * Admin dashboard - institute-scoped (ADMIN) or institute selector (SUPER_ADMIN)
 */

import { Fragment, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../context/AuthContext';
import { useLocation, useSearchParams } from 'react-router-dom';
import api, { API_URL } from '../api/axios';
import Layout from '../components/Layout';
import AssignSubjectFacultyPanel from '../components/AssignSubjectFacultyPanel';
import InitiateFeedbackCyclePanel from '../components/InitiateFeedbackCyclePanel';
import FeedbackCyclesTable from '../components/FeedbackCyclesTable';
import StudentMasterFilters, {
  filterDivisionsForStudentContext,
} from '../components/StudentMasterFilters';
import {
  OPERATIONAL_ACADEMIC_YEAR,
  OPERATIONAL_ODD_SEMESTERS,
  filterOperationalDivisions,
  filterOperationalSemesters,
} from '../config/operationalAcademicContext';
import StudentMasterTable from '../components/StudentMasterTable';
import StudentBulkUpload from '../components/StudentBulkUpload';
import SubjectMasterFilters from '../components/SubjectMasterFilters';
import FacultyMasterFilters, { FACULTY_ALL_PROGRAMS } from '../components/FacultyMasterFilters';
import FacultyMasterTable from '../components/FacultyMasterTable';
import FacultyFeedbackCyclePanel from '../components/FacultyFeedbackCyclePanel';
import { adminMenuGroups, DEFAULT_ADMIN_MENU_ID, isValidAdminMenuId } from '../config/adminMenuItems';

const currentYear = new Date().getFullYear();
const ACADEMIC_YEARS = Array.from({ length: 99 }, (_, i) => {
  const y = currentYear - i;
  return `${y}-${y + 1}`;
});

const academicYearLabel = (year) => {
  if (!year || !/^\d{4}-\d{4}$/.test(year)) return year;
  return `${year.slice(0, 4)}-${year.slice(7, 9)}`;
};

export default function AdminDashboard() {
  const { user } = useAuth();
  const location = useLocation();
  const isSuperAdmin = user?.role === 'SUPER_ADMIN';

  const [institutes, setInstitutes] = useState([]);
  const [selectedInstituteId, setSelectedInstituteId] = useState(
    () => location.state?.instituteId || null
  );
  const [users, setUsers] = useState([]);
  const [degrees, setDegrees] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [cycles, setCycles] = useState([]);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const defaultCycleEnd = () => {
    const d = new Date();
    d.setDate(d.getDate() + 90);
    return d.toISOString().split('T')[0];
  };
  const [cycleForm, setCycleForm] = useState({
    academicYear: ACADEMIC_YEARS[0],
    termType: 'ODD',
    semester: 5,
    startDate: new Date().toISOString().split('T')[0],
    endDate: defaultCycleEnd(),
    isAnonymous: false,
  });
  const [cycleSubmitting, setCycleSubmitting] = useState(false);
  const [cycleActionId, setCycleActionId] = useState(null);
  const [academicTerms, setAcademicTerms] = useState([]);
  const [showAddFaculty, setShowAddFaculty] = useState(false);
  const [facultyForm, setFacultyForm] = useState({
    employeeId: '',
    firstName: '',
    lastName: '',
    department: 'CSE',
    designation: '',
    qualification: '',
    specialization: '',
    contactNumber: '',
  });
  const [facultySubmitting, setFacultySubmitting] = useState(false);
  const [generatedCredentials, setGeneratedCredentials] = useState(null);
  const [showAddSubject, setShowAddSubject] = useState(false);
  const [subjectForm, setSubjectForm] = useState({
    code: '',
    name: '',
    degree: 'B.Tech',
    degreeId: '',
    programCode: '',
    departmentCode: '',
    semesterCode: '',
    divisionCode: '',
    department: 'CSE',
    semester: 5,
    credits: 3,
    subjectType: 'Core',
    workloadTheoryHrs: 3,
    workloadPracticalHrs: 2,
    numberOfUnits: 5,
    syllabusContent: '',
    isElective: false,
    description: '',
  });
  const [subjectSubmitting, setSubjectSubmitting] = useState(false);
  const [editingSubjectId, setEditingSubjectId] = useState(null);
  const [editSubjectForm, setEditSubjectForm] = useState({
    name: '',
    degree: 'B.Tech',
    programCode: '',
    departmentCode: '',
    semesterCode: '',
    divisionCode: '',
    department: 'CSE',
    semester: 5,
    credits: 3,
    subjectType: 'Core',
    workloadTheoryHrs: 3,
    workloadPracticalHrs: 2,
    numberOfUnits: 5,
    syllabusContent: '',
    isElective: false,
    description: '',
  });
  const [subjectEditSubmitting, setSubjectEditSubmitting] = useState(false);
  const [deletingSubjectId, setDeletingSubjectId] = useState(null);
  const [subjectFilters, setSubjectFilters] = useState({ department: '', degreeId: '', semester: '' });
  const [subjectAppliedFilters, setSubjectAppliedFilters] = useState(null);
  const [subjectRosterShown, setSubjectRosterShown] = useState(false);
  const [subjectRosterLoading, setSubjectRosterLoading] = useState(false);
  const [subjectRosterList, setSubjectRosterList] = useState([]);
  const subjectInitialLoadRef = useRef(false);
  const [allocationFilters, setAllocationFilters] = useState({
    academicYear: OPERATIONAL_ACADEMIC_YEAR,
    department: '',
    degreeId: '',
    semester: '',
  });
  const [allocationAppliedFilters, setAllocationAppliedFilters] = useState(null);
  const [allocationListShown, setAllocationListShown] = useState(false);
  const [allocationListLoading, setAllocationListLoading] = useState(false);
  const [allocationRoster, setAllocationRoster] = useState([]);
  const allocationInitialLoadRef = useRef(false);
  const [showAddDegree, setShowAddDegree] = useState(false);
  const emptyDegreeForm = {
    name: '',
    code: '',
    programType: '',
    programTitle: '',
    degreeLevel: 'UG',
    affiliatedUniversity: '',
    departmentId: '',
    numberOfSemesters: 8,
    durationYears: 4,
    mode: 'Full Time',
    intakeCapacity: '',
    regulatoryBody: '',
    eligibility: '',
    description: '',
  };
  const [degreeForm, setDegreeForm] = useState(emptyDegreeForm);
  const [degreeSubmitting, setDegreeSubmitting] = useState(false);
  const [editingDegreeId, setEditingDegreeId] = useState(null);
  const [editDegreeForm, setEditDegreeForm] = useState(emptyDegreeForm);
  const [degreeEditSubmitting, setDegreeEditSubmitting] = useState(false);
  const [deletingDegreeId, setDeletingDegreeId] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [showAddDepartment, setShowAddDepartment] = useState(false);
  const emptyDepartmentForm = {
    name: '',
    code: '',
    establishmentYear: '',
    hodFacultyId: '',
    hodFacultyCode: '',
    programsOfferedText: '',
    programIds: [],
    description: '',
  };
  const [departmentForm, setDepartmentForm] = useState(emptyDepartmentForm);
  const [departmentSubmitting, setDepartmentSubmitting] = useState(false);
  const [editingDepartmentId, setEditingDepartmentId] = useState(null);
  const [editDepartmentForm, setEditDepartmentForm] = useState(emptyDepartmentForm);
  const [departmentEditSubmitting, setDepartmentEditSubmitting] = useState(false);
  const [deletingDepartmentId, setDeletingDepartmentId] = useState(null);
  const [divisions, setDivisions] = useState([]);
  const [showAddDivision, setShowAddDivision] = useState(false);
  const [divisionForm, setDivisionForm] = useState({
    degreeId: '',
    academicYear: ACADEMIC_YEARS[0],
    semester: 1,
    name: '',
    code: '',
  });
  const [divisionSubmitting, setDivisionSubmitting] = useState(false);
  const [editingDivisionId, setEditingDivisionId] = useState(null);
  const [editDivisionForm, setEditDivisionForm] = useState({ name: '', code: '' });
  const [divisionEditSubmitting, setDivisionEditSubmitting] = useState(false);
  const [deletingDivisionId, setDeletingDivisionId] = useState(null);
  const [divisionFilters, setDivisionFilters] = useState({ degreeId: '', academicYear: '', semester: '' });
  const [selectedDivisionForStudents, setSelectedDivisionForStudents] = useState('');
  const [divisionStudents, setDivisionStudents] = useState([]);
  const [addStudentsToDivisionIds, setAddStudentsToDivisionIds] = useState([]);
  const [addStudentsToDivisionSubmitting, setAddStudentsToDivisionSubmitting] = useState(false);
  const [removingStudentFromDivisionId, setRemovingStudentFromDivisionId] = useState(null);
  const [studentsList, setStudentsList] = useState([]);
  const [studentSearchInput, setStudentSearchInput] = useState('');
  const [studentHighlightTerm, setStudentHighlightTerm] = useState('');
  const [studentFilters, setStudentFilters] = useState({
    department: '',
    academicYear: OPERATIONAL_ACADEMIC_YEAR,
    degreeId: '',
    semester: '',
    divisionId: '',
  });
  const [studentAppliedFilters, setStudentAppliedFilters] = useState(null);
  const [studentRosterShown, setStudentRosterShown] = useState(false);
  const [studentRosterLoading, setStudentRosterLoading] = useState(false);
  const [studentDivisionsCatalog, setStudentDivisionsCatalog] = useState([]);
  const [studentPagination, setStudentPagination] = useState({ page: 1, limit: 50, total: 0, totalPages: 1 });
  const [facultyRosterList, setFacultyRosterList] = useState([]);
  const [facultySearchInput, setFacultySearchInput] = useState('');
  const [facultyHighlightTerm, setFacultyHighlightTerm] = useState('');
  const [facultyFilters, setFacultyFilters] = useState({
    department: '',
    academicYear: OPERATIONAL_ACADEMIC_YEAR,
    degreeId: FACULTY_ALL_PROGRAMS,
    semester: '',
    divisionId: '',
  });
  const facultyInitialLoadRef = useRef(false);
  const [facultyAppliedFilters, setFacultyAppliedFilters] = useState(null);
  const [facultyRosterShown, setFacultyRosterShown] = useState(false);
  const [facultyRosterLoading, setFacultyRosterLoading] = useState(false);
  const [facultyDivisionsCatalog, setFacultyDivisionsCatalog] = useState([]);
  const [facultyPagination, setFacultyPagination] = useState({ page: 1, limit: 50, total: 0, totalPages: 1 });
  const [facultySeedSubmitting, setFacultySeedSubmitting] = useState(false);
  const [facultySeedNotice, setFacultySeedNotice] = useState('');
  const [showStudentUploadPanel, setShowStudentUploadPanel] = useState(false);
  const [editingStudentId, setEditingStudentId] = useState(null);
  const [editStudentForm, setEditStudentForm] = useState({
    rollNumber: '',
    firstName: '',
    lastName: '',
    department: 'CSE',
    yearOfStudy: 2,
    section: '',
    batch: '',
    contactNumber: '',
  });
  const [editStudentSubmitting, setEditStudentSubmitting] = useState(false);
  const [deletingStudentId, setDeletingStudentId] = useState(null);
  const [studentUploadFile, setStudentUploadFile] = useState(null);
  const [studentUploadSubmitting, setStudentUploadSubmitting] = useState(false);
  const [studentUploadResult, setStudentUploadResult] = useState(null);
  const [studentUploadError, setStudentUploadError] = useState('');
  const [masterUploadFiles, setMasterUploadFiles] = useState({});
  const [masterUploadResults, setMasterUploadResults] = useState({});
  const [masterUploadSubmitting, setMasterUploadSubmitting] = useState('');
  const [resettingPasswordUserId, setResettingPasswordUserId] = useState(null);
  const [resetPasswordResult, setResetPasswordResult] = useState(null);
  const [semesterChangeApplications, setSemesterChangeApplications] = useState([]);
  const [semesterChangeActionId, setSemesterChangeActionId] = useState(null);
  const [activeSemesterAssignments, setActiveSemesterAssignments] = useState([]);
  const [assignSubjectFacultyForm, setAssignSubjectFacultyForm] = useState({
    subjectId: '',
    facultyId: '',
    divisionId: '',
    section: '',
    isPrimary: true,
  });
  const [assignSubmitting, setAssignSubmitting] = useState(false);
  const [assignError, setAssignError] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab');
  const [activeMenu, setActiveMenuState] = useState(() =>
    isValidAdminMenuId(tabFromUrl) ? tabFromUrl : DEFAULT_ADMIN_MENU_ID
  );
  const findMenuGroup = (menuId) =>
    adminMenuGroups.find((group) => group.items.some((item) => item.id === menuId))?.label || adminMenuGroups[0]?.label;
  const [expandedAdminGroups, setExpandedAdminGroups] = useState(() => ({
    [findMenuGroup(isValidAdminMenuId(tabFromUrl) ? tabFromUrl : DEFAULT_ADMIN_MENU_ID)]: true,
  }));
  const setActiveMenu = (id) => {
    if (!isValidAdminMenuId(id)) return;
    setActiveMenuState(id);
    const groupLabel = findMenuGroup(id);
    setExpandedAdminGroups((prev) => ({ ...prev, [groupLabel]: true }));
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', id);
      return next;
    });
  };
  useEffect(() => {
    const t = searchParams.get('tab');
    if (isValidAdminMenuId(t)) {
      setActiveMenuState(t);
      const groupLabel = findMenuGroup(t);
      setExpandedAdminGroups((prev) => ({ ...prev, [groupLabel]: true }));
    }
  }, [searchParams]);
  const [userManagementRoleFilter, setUserManagementRoleFilter] = useState(''); // '' = All, FACULTY, STUDENT
  const [pendingFeedback, setPendingFeedback] = useState({ items: [], total: 0, page: 1, limit: 20, totalPages: 1 });
  const [pendingCycleId, setPendingCycleId] = useState('');
  const [pendingPage, setPendingPage] = useState(1);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [approveSubmitting, setApproveSubmitting] = useState(false);
  const [selectedPendingIds, setSelectedPendingIds] = useState([]);
  const [submittedFeedbackReport, setSubmittedFeedbackReport] = useState({ items: [], total: 0, page: 1, limit: 20, totalPages: 1 });
  const [submittedReportCycleId, setSubmittedReportCycleId] = useState('');
  const [submittedReportStatus, setSubmittedReportStatus] = useState('');
  const [submittedReportPage, setSubmittedReportPage] = useState(1);
  const [submittedReportLoading, setSubmittedReportLoading] = useState(false);
  const [billingSubscription, setBillingSubscription] = useState(null);
  const [billingPayments, setBillingPayments] = useState([]);
  const [billingLoading, setBillingLoading] = useState(false);
  const [razorpayAmount, setRazorpayAmount] = useState('');
  const [razorpayPaying, setRazorpayPaying] = useState(false);
  const [admissionCycleForm, setAdmissionCycleForm] = useState({
    name: '',
    academicYear: ACADEMIC_YEARS[0],
    semester: 1,
    startDate: new Date().toISOString().split('T')[0],
    endDate: defaultCycleEnd(),
  });
  const [admissionCycleSubmitting, setAdmissionCycleSubmitting] = useState(false);
  const [selectedAdmissionCycleId, setSelectedAdmissionCycleId] = useState('');
  const [admissionQuestions, setAdmissionQuestions] = useState([]);
  const [admissionQuestionsLoading, setAdmissionQuestionsLoading] = useState(false);
  const [admissionQuestionForm, setAdmissionQuestionForm] = useState({
    questionText: '',
    optionsText: '',
    displayOrder: 0,
  });
  const [addQuestionSubmitting, setAddQuestionSubmitting] = useState(false);
  const [admissionExcelFile, setAdmissionExcelFile] = useState(null);
  const [uploadExcelSubmitting, setUploadExcelSubmitting] = useState(false);
  const [admissionQuestionsError, setAdmissionQuestionsError] = useState('');
  const [parentAlumniCycleForm, setParentAlumniCycleForm] = useState({
    type: 'PARENT_FEEDBACK',
    name: '',
    academicYear: ACADEMIC_YEARS[0],
    semester: 1,
    startDate: new Date().toISOString().split('T')[0],
    endDate: defaultCycleEnd(),
    alumniTaskType: 'PROGRAM_STRUCTURE',
    expertTaskType: 'PROGRAM_STRUCTURE',
    parentTaskType: 'PROGRAM',
  });
  const [parentAlumniCycleSubmitting, setParentAlumniCycleSubmitting] = useState(false);
  const [selectedTemplateType, setSelectedTemplateType] = useState('admission');
  const [studentFeedbackReportCycleId, setStudentFeedbackReportCycleId] = useState('');
  const [studentFeedbackReportStatus, setStudentFeedbackReportStatus] = useState('');
  const [studentFeedbackReport, setStudentFeedbackReport] = useState({ students: [], total: 0, page: 1, limit: 50, totalPages: 1 });
  const [studentFeedbackReportLoading, setStudentFeedbackReportLoading] = useState(false);
  const [studentFeedbackReportPage, setStudentFeedbackReportPage] = useState(1);
  const [pdfReportType, setPdfReportType] = useState('overview');
  const [pdfReportFilters, setPdfReportFilters] = useState({ cycleId: '', department: '', academicYear: '', facultyId: '', subjectId: '', status: '' });
  const [pdfReportDownloading, setPdfReportDownloading] = useState(false);
  const [reportPreview, setReportPreview] = useState(null);
  const [reportPreviewLoading, setReportPreviewLoading] = useState(false);
  const PDF_REPORT_OPTIONS = [
    { value: 'overview', label: 'System overview' },
    { value: 'faculty', label: 'Faculty performance' },
    { value: 'subject', label: 'Subject analysis' },
    { value: 'department', label: 'Department summary' },
    { value: 'students-submitted', label: 'Student feedback submission' },
    { value: 'odd-semesters', label: 'Odd semester consolidated' },
    { value: 'even-semesters', label: 'Even semester consolidated' },
    { value: 'complete', label: 'Complete institute report' },
  ];
  const FEEDBACK_TEMPLATE_OPTIONS = [
    { value: 'admission', label: 'Admission feedback' },
    { value: 'internal_examination', label: 'Internal examination feedback' },
    { value: 'semester', label: 'Semester feedback' },
    { value: 'result', label: 'Result feedback' },
    { value: 'faculty_student', label: 'Student to faculty feedback' },
  ];
  const TEMPLATE_FILENAMES = {
    admission: 'admission-questions-template.xlsx',
    internal_examination: 'internal-examination-questions-template.xlsx',
    semester: 'semester-feedback-questions-template.xlsx',
    result: 'result-feedback-questions-template.xlsx',
    faculty_student: 'faculty-student-feedback-questions-template.xlsx',
  };
  const [lifecycleStages, setLifecycleStages] = useState([]);
  const [questionnaires, setQuestionnaires] = useState([]);
  const [lifecycleLoading, setLifecycleLoading] = useState(false);
  const [lifecycleMessage, setLifecycleMessage] = useState('');
  const [overviewAcademicYear, setOverviewAcademicYear] = useState(ACADEMIC_YEARS[0]);
  const [overviewSemester, setOverviewSemester] = useState('');
  const [overviewNotice, setOverviewNotice] = useState('');
  const [showActiveFormsPanel, setShowActiveFormsPanel] = useState(false);
  const [activeFormsList, setActiveFormsList] = useState([]);
  const [activeFormsLoading, setActiveFormsLoading] = useState(false);
  const [reminderSendingKey, setReminderSendingKey] = useState('');
  const [instituteProfile, setInstituteProfile] = useState(null);
  const [instituteProfileForm, setInstituteProfileForm] = useState({
    name: '',
    title: '',
    email: '',
    contactName: '',
    instituteCode: '',
    parentSociety: '',
    establishedOn: '',
    naacAccredited: false,
    naacGrade: '',
    naacAccreditationYear: '',
    nbaAccredited: false,
    nbaAccreditationYear: '',
    address: '',
    city: '',
    state: '',
    country: 'India',
    pincode: '',
    logoUrl: '',
    brandingSentence: '',
    shortProfile: '',
    programsOfferedText: '',
  });
  const [instituteProfileLoading, setInstituteProfileLoading] = useState(false);
  const [instituteProfileMessage, setInstituteProfileMessage] = useState('');

  const instituteId = isSuperAdmin ? selectedInstituteId : (user?.institute?.id ?? user?.instituteId);
  const admissionCycles = (cycles || []).filter((c) => c.type === 'ADMISSION');
  const parentCycles = (cycles || []).filter((c) => c.type === 'PARENT_FEEDBACK');
  const alumniCycles = (cycles || []).filter((c) => c.type === 'ALUMNI_FEEDBACK');
  const industryExpertCycles = (cycles || []).filter((c) => c.type === 'INDUSTRY_EXPERT_FEEDBACK');

  /** Extract users array from /admin/users response (handles { data: { users, pagination } } or { data: [] }) */
  const getUsersFromResponse = (res) => {
    const d = res?.data;
    if (!d) return [];
    const inner = d.data;
    if (Array.isArray(inner)) return inner;
    if (inner && Array.isArray(inner.users)) return inner.users;
    if (Array.isArray(d.users)) return d.users;
    return [];
  };

  const hydrateInstituteProfileForm = (profile) => {
    if (!profile) return;
    setInstituteProfile(profile);
    setInstituteProfileForm({
      name: profile.name || '',
      title: profile.title || profile.name || '',
      email: profile.email || '',
      contactName: profile.contact_name || '',
      instituteCode: profile.institute_code || '',
      parentSociety: profile.parent_society || '',
      establishedOn: profile.established_on ? String(profile.established_on).slice(0, 10) : '',
      naacAccredited: profile.naac_accredited === true,
      naacGrade: profile.naac_grade || '',
      naacAccreditationYear: profile.naac_accreditation_year || '',
      nbaAccredited: profile.nba_accredited === true,
      nbaAccreditationYear: profile.nba_accreditation_year || '',
      address: profile.address || '',
      city: profile.city || '',
      state: profile.state || '',
      country: profile.country || 'India',
      pincode: profile.pincode || '',
      logoUrl: profile.logo_url || '',
      brandingSentence: profile.branding_sentence || '',
      shortProfile: profile.short_profile || '',
      programsOfferedText: Array.isArray(profile.programs_offered) ? profile.programs_offered.join('\n') : '',
    });
  };

  const activeCycle = cycles.find((c) => c.is_currently_open && c.type === 'SEMESTER')
    || cycles.find((c) => c.is_active && c.type === 'SEMESTER')
    || cycles.find((c) => c.is_currently_open)
    || cycles.find((c) => c.is_active)
    || null;
  const termFilteredSemesters = academicTerms
    .filter((term) => term.academic_year === cycleForm.academicYear && term.term_type === cycleForm.termType)
    .flatMap((term) => Array.isArray(term.semester_numbers) ? term.semester_numbers : [])
    .map(Number)
    .filter(Boolean)
    .sort((a, b) => a - b);
  const availableCycleSemesters = termFilteredSemesters.length > 0
    ? termFilteredSemesters
    : (cycleForm.termType === 'EVEN' ? [2, 4, 6, 8] : [1, 3, 5, 7]);
  const nowForTerm = new Date();
  const currentAcademicTerms = academicTerms.filter((term) => (
    term.is_active !== false
    && term.start_at
    && term.end_at
    && new Date(term.start_at) <= nowForTerm
    && new Date(term.end_at) >= nowForTerm
  ));
  const currentTerm = currentAcademicTerms[0] || null;
  const currentSemesterNumbers = currentTerm && Array.isArray(currentTerm.semester_numbers)
    ? currentTerm.semester_numbers.map(Number)
    : [];
  const currentSemesterCycles = (cycles || []).filter((cycle) => (
    cycle.type === 'SEMESTER'
    && currentTerm
    && cycle.academic_year === currentTerm.academic_year
    && currentSemesterNumbers.includes(Number(cycle.semester))
  ));
  const facultyFeedbackSemesters = Array.from(new Set([
    ...filterOperationalSemesters(currentSemesterNumbers),
    ...(cycles || [])
      .filter((cycle) => (
        cycle.type === 'SEMESTER'
        && cycle.academic_year === OPERATIONAL_ACADEMIC_YEAR
        && OPERATIONAL_ODD_SEMESTERS.includes(Number(cycle.semester))
      ))
      .map((cycle) => Number(cycle.semester)),
    ...OPERATIONAL_ODD_SEMESTERS,
  ].map(Number).filter(Boolean))).sort((a, b) => a - b);
  useEffect(() => {
    if (!currentTerm) return;
    setCycleForm((f) => ({
      ...f,
      academicYear: currentTerm.academic_year,
      termType: currentTerm.term_type,
    }));
  }, [currentTerm?.id]);
  useEffect(() => {
    if (!availableCycleSemesters.includes(Number(cycleForm.semester))) {
      setCycleForm((f) => ({ ...f, semester: availableCycleSemesters[0] || 1 }));
    }
  }, [cycleForm.academicYear, cycleForm.termType, cycleForm.semester, availableCycleSemesters.join(',')]);
  const overviewCycles = (cycles || []).filter((cycle) => {
    if (overviewAcademicYear && cycle.academic_year !== overviewAcademicYear) return false;
    if (overviewSemester && Number(cycle.semester) !== Number(overviewSemester)) return false;
    return true;
  });
  const availableOverviewYears = Array.from(new Set([
    ...ACADEMIC_YEARS.slice(0, 6),
    ...(cycles || []).map((cycle) => cycle.academic_year).filter(Boolean),
  ]));
  const availableOverviewSemesters = Array.from(new Set(
    (cycles || [])
      .filter((cycle) => !overviewAcademicYear || cycle.academic_year === overviewAcademicYear)
      .map((cycle) => Number(cycle.semester))
      .filter(Boolean)
  )).sort((a, b) => a - b);
  const availableReportYears = Array.from(new Set([
    ...(cycles || []).map((cycle) => cycle.academic_year).filter(Boolean),
    ...ACADEMIC_YEARS.slice(0, 6),
  ])).sort((a, b) => b.localeCompare(a));
  const reportCycles = (cycles || []).filter((cycle) => (
    !pdfReportFilters.academicYear || cycle.academic_year === pdfReportFilters.academicYear
  ));
  const activeOverviewCycles = Array.isArray(overview?.activeForms) && overview.activeForms.length > 0
    ? overview.activeForms
    : overviewCycles.filter((cycle) => cycle.is_active);
  const upcomingOverviewCycles = overviewCycles
    .filter((cycle) => !cycle.is_active && cycle.start_date && new Date(cycle.start_date) >= new Date(new Date().toDateString()))
    .sort((a, b) => new Date(a.start_date) - new Date(b.start_date))
    .slice(0, 6);
  const overviewStats = overview?.statistics || {};
  const totalStudents = Number(overviewStats.total_students || 0);
  const totalUsers = Number(overviewStats.total_users || 0);
  const totalFeedback = Number(overviewStats.total_feedback || 0);
  const participationBase = totalStudents || totalUsers || 1;
  const participationPercentage = Math.min(100, Math.round((totalFeedback / participationBase) * 100));
  const stakeholderStats = Array.isArray(overview?.stakeholderStats) ? overview.stakeholderStats : [];
  const semesterSubjects = Array.isArray(overview?.semesterSubjects) ? overview.semesterSubjects : [];
  const facultyList = (users || []).filter((u) => u.role === 'FACULTY' && u.details?.id).map((u) => ({
    id: u.details.id,
    employee_id: u.details.employee_id,
    name: u.details.first_name && u.details.last_name ? `${u.details.first_name} ${u.details.last_name}` : u.email,
  }));

  const DEPARTMENTS_FALLBACK = ['CSE', 'ECE', 'EEE', 'MECH', 'CIVIL', 'IT', 'AIDS', 'CSBS', 'OTHER'];
  const DEGREES_FALLBACK = ['B.Tech', 'M.Tech', 'B.E.', 'M.E.', 'BCA', 'MCA', 'B.Sc.', 'M.Sc.', 'BBA', 'MBA', 'OTHER'];
  const degreeOptions = degrees.length > 0 ? degrees.map((d) => d.name) : DEGREES_FALLBACK;
  const departmentOptions = departments.length > 0 ? departments.map((d) => d.name) : DEPARTMENTS_FALLBACK;
  const getProgramDepartmentName = (program) =>
    departments.find((dept) => dept.id === program?.department_id || dept.code === program?.department_code)?.name || program?.department_code || '';
  const semesterNumbersForProgram = (program) => {
    const count = Number(program?.number_of_semesters || 8);
    return Array.from({ length: Math.max(1, Math.min(20, count)) }, (_, i) => i + 1);
  };
  const applyProgramToSubjectForm = (programId, setter) => {
    const program = degrees.find((d) => d.id === programId);
    setter((prev) => {
      const semester = Number(prev.semester || 1);
      const maxSemester = Number(program?.number_of_semesters || 8);
      const nextSemester = Math.min(Math.max(1, semester), maxSemester);
      return {
        ...prev,
        degreeId: programId,
        degree: program?.name || prev.degree,
        programCode: program?.program_code || program?.code || '',
        departmentCode: program?.department_code || '',
        department: getProgramDepartmentName(program) || prev.department,
        semester: nextSemester,
        semesterCode: program?.program_code ? `${program.program_code}-S${nextSemester}` : '',
      };
    });
  };
  const applySemesterToSubjectForm = (semester, setter) => {
    setter((prev) => ({
      ...prev,
      semester: Number(semester),
      semesterCode: prev.programCode ? `${prev.programCode}-S${Number(semester)}` : prev.semesterCode,
    }));
  };

  const subjectCanShowRoster = Boolean(subjectFilters.department && subjectFilters.degreeId);
  const allocationCanShowList = Boolean(
    allocationFilters.academicYear && allocationFilters.department && allocationFilters.degreeId
  );

  const fetchSubjectRoster = useCallback(async (filterSource) => {
    const filters = filterSource;
    if (!filters?.department || !filters?.degreeId) return;
    if (isSuperAdmin && !instituteId) return;
    const params = isSuperAdmin && instituteId ? { instituteId } : {};
    params.department = filters.department;
    params.degreeId = filters.degreeId;
    if (filters.semester !== '' && filters.semester != null) {
      params.semester = filters.semester;
    }
    setSubjectRosterLoading(true);
    try {
      const res = await api.get('/admin/subjects', { params });
      setSubjectRosterList(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (err) {
      setSubjectRosterList([]);
      setError(err.response?.data?.error?.message || err.message || 'Failed to load subjects');
    } finally {
      setSubjectRosterLoading(false);
    }
  }, [instituteId, isSuperAdmin]);

  const handleShowSubjectRoster = () => {
    if (!subjectCanShowRoster) return;
    const applied = { ...subjectFilters };
    setSubjectAppliedFilters(applied);
    setSubjectRosterShown(true);
    fetchSubjectRoster(applied);
  };

  const refreshSubjectRosterIfShown = useCallback(() => {
    if (subjectRosterShown && subjectAppliedFilters) {
      fetchSubjectRoster(subjectAppliedFilters);
    }
  }, [subjectRosterShown, subjectAppliedFilters, fetchSubjectRoster]);

  const fetchAllocationRoster = useCallback(async (filterSource) => {
    const filters = filterSource;
    if (!filters?.academicYear || !filters?.department || !filters?.degreeId) return;
    if (isSuperAdmin && !instituteId) return;
    const params = isSuperAdmin && instituteId ? { instituteId } : {};
    params.academicYear = filters.academicYear;
    params.department = filters.department;
    params.degreeId = filters.degreeId;
    if (filters.semester !== '' && filters.semester != null) {
      params.semester = filters.semester;
    }
    setAllocationListLoading(true);
    try {
      const res = await api.get('/admin/subject-faculty-assignments', { params });
      setAllocationRoster(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (err) {
      setAllocationRoster([]);
      setError(err.response?.data?.error?.message || err.message || 'Failed to load allocations');
    } finally {
      setAllocationListLoading(false);
    }
  }, [instituteId, isSuperAdmin]);

  const handleShowAllocationList = () => {
    if (!allocationCanShowList) return;
    const applied = { ...allocationFilters };
    setAllocationAppliedFilters(applied);
    setAllocationListShown(true);
    fetchAllocationRoster(applied);
  };

  const refreshAllocationRosterIfShown = useCallback(() => {
    if (allocationListShown && allocationAppliedFilters) {
      fetchAllocationRoster(allocationAppliedFilters);
    }
  }, [allocationListShown, allocationAppliedFilters, fetchAllocationRoster]);
  const renderProgramMasterFields = (formValue, setFormValue) => (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
      <div>
        <label className="input-label">Program title *</label>
        <input type="text" value={formValue.name} onChange={(e) => setFormValue((f) => ({ ...f, name: e.target.value, programTitle: f.programTitle || e.target.value }))} required maxLength={100} className="input text-base text-slate-900 text-left" placeholder="e.g. B.Tech" />
      </div>
      <div>
        <label className="input-label">Program code *</label>
        <input type="text" value={formValue.code} onChange={(e) => setFormValue((f) => ({ ...f, code: e.target.value.toUpperCase() }))} maxLength={20} className="input text-base text-slate-900 text-left" placeholder="e.g. BTECH" />
      </div>
      <div>
        <label className="input-label">Degree level</label>
        <select value={formValue.degreeLevel} onChange={(e) => setFormValue((f) => ({ ...f, degreeLevel: e.target.value }))} className="input text-base text-slate-900 text-left">
          {['UG', 'PG', 'MPhil', 'PhD', 'Post Graduate Diploma', 'Diploma', 'Certificate', 'Other'].map((level) => <option key={level} value={level}>{level}</option>)}
        </select>
      </div>
      <div>
        <label className="input-label">Program type</label>
        <input value={formValue.programType} onChange={(e) => setFormValue((f) => ({ ...f, programType: e.target.value }))} className="input text-base text-slate-900 text-left" placeholder="Professional Degree" />
      </div>
      <div>
        <label className="input-label">Department *</label>
        <select value={formValue.departmentId} onChange={(e) => setFormValue((f) => ({ ...f, departmentId: e.target.value }))} className="input text-base text-slate-900 text-left">
          <option value="">Select department</option>
          {departments.map((dept) => <option key={dept.id} value={dept.id}>{dept.code || 'DEPT'} - {dept.name}</option>)}
        </select>
      </div>
      <div>
        <label className="input-label">Affiliated university</label>
        <input value={formValue.affiliatedUniversity} onChange={(e) => setFormValue((f) => ({ ...f, affiliatedUniversity: e.target.value }))} className="input text-base text-slate-900 text-left" />
      </div>
      <div>
        <label className="input-label">Semesters</label>
        <input type="number" min="1" max="20" value={formValue.numberOfSemesters} onChange={(e) => setFormValue((f) => ({ ...f, numberOfSemesters: e.target.value }))} className="input text-base text-slate-900 text-left" />
      </div>
      <div>
        <label className="input-label">Duration years</label>
        <input type="number" min="0.5" step="0.5" value={formValue.durationYears} onChange={(e) => setFormValue((f) => ({ ...f, durationYears: e.target.value }))} className="input text-base text-slate-900 text-left" />
      </div>
      <div>
        <label className="input-label">Intake</label>
        <input type="number" min="0" value={formValue.intakeCapacity} onChange={(e) => setFormValue((f) => ({ ...f, intakeCapacity: e.target.value }))} className="input text-base text-slate-900 text-left" />
      </div>
      <div>
        <label className="input-label">Regulatory body</label>
        <input value={formValue.regulatoryBody} onChange={(e) => setFormValue((f) => ({ ...f, regulatoryBody: e.target.value }))} className="input text-base text-slate-900 text-left" placeholder="AICTE / UGC" />
      </div>
      <div>
        <label className="input-label">Mode</label>
        <input value={formValue.mode} onChange={(e) => setFormValue((f) => ({ ...f, mode: e.target.value }))} className="input text-base text-slate-900 text-left" />
      </div>
      <div className="md:col-span-2">
        <label className="input-label">Eligibility</label>
        <textarea value={formValue.eligibility} onChange={(e) => setFormValue((f) => ({ ...f, eligibility: e.target.value }))} className="input text-base text-slate-900 text-left min-h-[80px]" />
      </div>
      <div className="md:col-span-2">
        <label className="input-label">Program profile</label>
        <textarea value={formValue.description} onChange={(e) => setFormValue((f) => ({ ...f, description: e.target.value }))} className="input text-base text-slate-900 text-left min-h-[80px]" />
      </div>
    </div>
  );
  const renderDepartmentMasterFields = (formValue, setFormValue) => (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        <div>
          <label className="input-label">Department name *</label>
          <input type="text" value={formValue.name} onChange={(e) => setFormValue((f) => ({ ...f, name: e.target.value }))} required maxLength={100} className="input text-base text-slate-900 text-left" placeholder="e.g. Computer Science and Engineering" />
        </div>
        <div>
          <label className="input-label">Department code *</label>
          <input type="text" value={formValue.code} onChange={(e) => setFormValue((f) => ({ ...f, code: e.target.value }))} maxLength={20} className="input text-base text-slate-900 text-left" placeholder="e.g. CSE" />
        </div>
        <div>
          <label className="input-label">Establishment year</label>
          <input type="number" value={formValue.establishmentYear} onChange={(e) => setFormValue((f) => ({ ...f, establishmentYear: e.target.value }))} className="input text-base text-slate-900 text-left" placeholder="e.g. 2009" />
        </div>
        <div>
          <label className="input-label">Head of Department</label>
          <select value={formValue.hodFacultyId} onChange={(e) => {
            const faculty = facultyList.find((f) => f.id === e.target.value);
            setFormValue((prev) => ({ ...prev, hodFacultyId: e.target.value, hodFacultyCode: faculty?.employee_id || prev.hodFacultyCode }));
          }} className="input text-base text-slate-900 text-left">
            <option value="">Select faculty</option>
            {users.filter((u) => u.role === 'FACULTY' && u.details?.id).map((u) => (
              <option key={u.details.id} value={u.details.id}>{u.details.employee_id || 'FAC'} - {[u.details.first_name, u.details.last_name].filter(Boolean).join(' ') || u.email}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="input-label">HOD faculty code</label>
          <input value={formValue.hodFacultyCode} onChange={(e) => setFormValue((f) => ({ ...f, hodFacultyCode: e.target.value }))} className="input text-base text-slate-900 text-left" placeholder="Faculty code" />
        </div>
      </div>
      <div>
        <label className="input-label">Programs under department</label>
        <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
          {degrees.map((degree) => (
            <label key={degree.id} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={formValue.programIds.includes(degree.id)}
                onChange={(e) => setFormValue((prev) => ({
                  ...prev,
                  programIds: e.target.checked ? [...prev.programIds, degree.id] : prev.programIds.filter((id) => id !== degree.id),
                }))}
              />
              {degree.name} ({degree.code || 'Code NA'})
            </label>
          ))}
        </div>
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        <div>
          <label className="input-label">Programs offered text</label>
          <textarea value={formValue.programsOfferedText} onChange={(e) => setFormValue((f) => ({ ...f, programsOfferedText: e.target.value }))} className="input text-base text-slate-900 text-left min-h-[96px]" placeholder="One program per line" />
        </div>
        <div>
          <label className="input-label">Department profile</label>
          <textarea value={formValue.description} onChange={(e) => setFormValue((f) => ({ ...f, description: e.target.value }))} className="input text-base text-slate-900 text-left min-h-[96px]" />
        </div>
      </div>
    </>
  );

  const masterUploadLabels = {
    programs: 'Programs',
    departments: 'Departments',
    divisions: 'Divisions',
    subjects: 'Subjects',
    allocations: 'Subject-Faculty Allocations',
  };

  const downloadMasterTemplate = async (type) => {
    try {
      const params = isSuperAdmin && instituteId ? { instituteId } : {};
      const res = await api.get(`/admin/master-upload/${type}/template`, { responseType: 'blob', params });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.setAttribute('download', `${type}-template.xlsx`);
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message || 'Failed to download template');
    }
  };

  const uploadMasterData = async (type) => {
    const file = masterUploadFiles[type];
    if (!file) return;
    setError('');
    setMasterUploadSubmitting(type);
    setMasterUploadResults((prev) => ({ ...prev, [type]: null }));
    try {
      const params = isSuperAdmin && instituteId ? { instituteId } : {};
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post(`/admin/master-upload/${type}`, formData, {
        params,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setMasterUploadResults((prev) => ({ ...prev, [type]: res.data?.data || null }));
      setMasterUploadFiles((prev) => ({ ...prev, [type]: null }));
      await fetchData();
    } catch (err) {
      setMasterUploadResults((prev) => ({
        ...prev,
        [type]: {
          created: [],
          failed: 1,
          errors: [{ row: '-', message: err.response?.data?.error?.message || err.message || 'Upload failed' }],
        },
      }));
    } finally {
      setMasterUploadSubmitting('');
    }
  };

  const renderMasterUploadPanel = (type) => {
    const result = masterUploadResults[type];
    return (
      <div className="admin-master-form">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h3 className="text-sm font-semibold text-slate-800 m-0">Bulk upload {masterUploadLabels[type]}</h3>
            <p className="text-xs text-slate-500 mt-1 mb-0">Download the template, fill rows, then upload. Errors are reported row-wise.</p>
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <button type="button" onClick={() => downloadMasterTemplate(type)} className="btn-secondary text-sm">Download template</button>
            <input
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={(e) => setMasterUploadFiles((prev) => ({ ...prev, [type]: e.target.files?.[0] || null }))}
              className="text-sm text-slate-600"
            />
            <button
              type="button"
              onClick={() => uploadMasterData(type)}
              disabled={!masterUploadFiles[type] || masterUploadSubmitting === type}
              className="btn-primary text-sm disabled:opacity-50"
            >
              {masterUploadSubmitting === type ? 'Uploading...' : 'Upload'}
            </button>
          </div>
        </div>
        {result && (
          <div className={`mt-3 rounded-lg border px-3 py-2 text-sm ${result.failed ? 'border-amber-200 bg-amber-50 text-amber-900' : 'border-emerald-200 bg-emerald-50 text-emerald-800'}`}>
            <p className="font-medium">Created: {result.created?.length || 0} | Failed: {result.failed || 0}</p>
            {Array.isArray(result.errors) && result.errors.length > 0 && (
              <ul className="mt-2 list-disc list-inside max-h-36 overflow-y-auto text-xs">
                {result.errors.map((err, index) => (
                  <li key={index}>Row {err.row}: {err.message}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    );
  };

  // Keep Add Subject form degree in sync with available options so the selected degree is always valid
  useEffect(() => {
    if (!showAddSubject || degreeOptions.length === 0) return;
    if (!degreeOptions.includes(subjectForm.degree)) {
      setSubjectForm((prev) => ({ ...prev, degree: degreeOptions[0] }));
    }
  }, [showAddSubject, degreeOptions.join(',')]);

  useEffect(() => {
    if (isSuperAdmin) {
      api.get('/super-admin/institutes?limit=500')
        .then((res) => {
          const list = res.data?.data?.institutes ?? [];
          setInstitutes(list);
          if (list.length === 1) setSelectedInstituteId(list[0].id);
        })
        .catch(() => setInstitutes([]));
    }
  }, [isSuperAdmin]);

  const fetchData = async () => {
    if (isSuperAdmin && !instituteId) {
      setLoading(false);
      return;
    }
    const params = isSuperAdmin && instituteId ? { instituteId } : {};
    const userParams = { ...params, limit: 500, _t: Date.now() };
    if (userManagementRoleFilter) userParams.role = userManagementRoleFilter;
    const divisionParams = { ...params };
    if (divisionFilters.degreeId) divisionParams.degreeId = divisionFilters.degreeId;
    if (divisionFilters.academicYear) divisionParams.academicYear = divisionFilters.academicYear;
    if (divisionFilters.semester !== '' && divisionFilters.semester != null) divisionParams.semester = divisionFilters.semester;
    const endpoints = [
      () => api.get('/admin/users', { params: userParams }),
      () => api.get('/admin/degrees', { params }),
      () => api.get('/admin/departments', { params }),
      () => api.get('/admin/divisions', { params: divisionParams }),
      () => api.get('/admin/subjects', { params }),
      () => api.get('/admin/cycles', { params }),
      () => api.get('/admin/academic-term-calendar', { params }),
      () => api.get('/admin/reports/overview', { params }),
      () => api.get('/admin/institute-profile', { params }),
      () => api.get('/admin/semester-change-applications', { params }),
    ];
    try {
      setError('');
      const results = await Promise.allSettled(endpoints.map((fn) => fn()));
      const [usersRes, degreesRes, departmentsRes, divisionsRes, subjectsRes, cyclesRes, termsRes, overviewRes, instituteProfileRes, semesterChangeRes] = results;
      if (usersRes.status === 'fulfilled') {
        const usersList = getUsersFromResponse(usersRes.value);
        setUsers(Array.isArray(usersList) ? usersList : []);
      }
      if (degreesRes.status === 'fulfilled' && degreesRes.value?.data?.data != null) {
        const d = degreesRes.value.data.data;
        setDegrees(Array.isArray(d) ? d : []);
      }
      if (departmentsRes.status === 'fulfilled' && departmentsRes.value?.data?.data != null) {
        const d = departmentsRes.value.data.data;
        setDepartments(Array.isArray(d) ? d : []);
      }
      if (divisionsRes.status === 'fulfilled' && divisionsRes.value?.data?.data != null) {
        const d = divisionsRes.value.data.data;
        setDivisions(Array.isArray(d) ? d : []);
      }
      if (subjectsRes.status === 'fulfilled' && subjectsRes.value?.data?.data != null) {
        const d = subjectsRes.value.data.data;
        setSubjects(Array.isArray(d) ? d : []);
      }
      if (cyclesRes.status === 'fulfilled' && cyclesRes.value?.data?.data != null) {
        const d = cyclesRes.value.data.data;
        setCycles(Array.isArray(d) ? d : []);
      }
      if (termsRes.status === 'fulfilled' && termsRes.value?.data?.data != null) {
        const d = termsRes.value.data.data;
        setAcademicTerms(Array.isArray(d) ? d : []);
      }
      if (overviewRes.status === 'fulfilled' && overviewRes.value?.data?.data != null) {
        setOverview(overviewRes.value.data.data);
      }
      if (instituteProfileRes.status === 'fulfilled' && instituteProfileRes.value?.data?.data != null) {
        hydrateInstituteProfileForm(instituteProfileRes.value.data.data);
      }
      if (semesterChangeRes.status === 'fulfilled' && semesterChangeRes.value?.data?.data != null) {
        const d = semesterChangeRes.value.data.data;
        setSemesterChangeApplications(Array.isArray(d) ? d : []);
      }
      // Only show error when critical data (users) failed; optional endpoints can fail without alarming
      if (usersRes.status === 'rejected') {
        const msg = usersRes.reason?.response?.data?.error?.message || usersRes.reason?.message;
        setError(msg || 'Failed to load users.');
      } else if (results.some((r) => r.status === 'rejected')) {
        // Optional data failed; don't show error so list is still usable
      }
    } catch (err) {
      setError(err?.response?.data?.error?.message || err?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    fetchData();
  }, [instituteId, userManagementRoleFilter]);

  useEffect(() => {
    if (isSuperAdmin && !instituteId) return;
    const params = {
      ...(isSuperAdmin && instituteId ? { instituteId } : {}),
      academicYear: overviewAcademicYear,
    };
    if (overviewSemester) params.semester = overviewSemester;
    api.get('/admin/reports/overview', { params })
      .then((res) => {
        if (res.data?.data != null) setOverview(res.data.data);
      })
      .catch(() => {});
  }, [instituteId, isSuperAdmin, overviewAcademicYear, overviewSemester]);

  const fetchActiveFormsList = async () => {
    if (isSuperAdmin && !instituteId) return;
    const params = {
      ...(isSuperAdmin && instituteId ? { instituteId } : {}),
      academicYear: overviewAcademicYear,
    };
    if (overviewSemester) params.semester = overviewSemester;
    setActiveFormsLoading(true);
    try {
      const res = await api.get('/admin/reports/active-feedback-forms', { params });
      setActiveFormsList(Array.isArray(res.data?.data) ? res.data.data : []);
      setShowActiveFormsPanel(true);
    } catch (err) {
      setOverviewNotice(err?.response?.data?.error?.message || 'Unable to load active feedback forms.');
    } finally {
      setActiveFormsLoading(false);
    }
  };

  const submitInstituteProfile = async (e) => {
    e.preventDefault();
    if (isSuperAdmin && !instituteId) return;
    const params = isSuperAdmin && instituteId ? { instituteId } : {};
    const payload = {
      name: instituteProfileForm.name,
      title: instituteProfileForm.title,
      email: instituteProfileForm.email,
      contactName: instituteProfileForm.contactName,
      instituteCode: instituteProfileForm.instituteCode,
      parentSociety: instituteProfileForm.parentSociety,
      establishedOn: instituteProfileForm.establishedOn || null,
      naacAccredited: instituteProfileForm.naacAccredited,
      naacGrade: instituteProfileForm.naacGrade,
      naacAccreditationYear: instituteProfileForm.naacAccreditationYear ? Number(instituteProfileForm.naacAccreditationYear) : null,
      nbaAccredited: instituteProfileForm.nbaAccredited,
      nbaAccreditationYear: instituteProfileForm.nbaAccreditationYear ? Number(instituteProfileForm.nbaAccreditationYear) : null,
      address: instituteProfileForm.address,
      city: instituteProfileForm.city,
      state: instituteProfileForm.state,
      country: instituteProfileForm.country,
      pincode: instituteProfileForm.pincode,
      logoUrl: instituteProfileForm.logoUrl,
      brandingSentence: instituteProfileForm.brandingSentence,
      shortProfile: instituteProfileForm.shortProfile,
      programsOffered: instituteProfileForm.programsOfferedText.split('\n').map((x) => x.trim()).filter(Boolean),
      ...params,
    };
    setInstituteProfileLoading(true);
    setInstituteProfileMessage('');
    try {
      const res = await api.patch('/admin/institute-profile', payload);
      hydrateInstituteProfileForm(res.data?.data);
      setInstituteProfileMessage('Institute profile saved.');
    } catch (err) {
      setInstituteProfileMessage(err?.response?.data?.error?.message || 'Unable to save institute profile.');
    } finally {
      setInstituteProfileLoading(false);
    }
  };

  const sendActiveFormReminder = async (form) => {
    const key = `${form.id}-${form.questionnaire_id || 'cycle'}`;
    setReminderSendingKey(key);
    try {
      const body = {
        cycleId: form.id,
        questionnaireId: form.questionnaire_id,
        stakeholder: form.stakeholder,
        ...(isSuperAdmin && instituteId ? { instituteId } : {}),
      };
      const res = await api.post('/admin/reports/active-feedback-forms/reminder', body);
      setOverviewNotice(res.data?.message || 'Reminder sent.');
      await fetchActiveFormsList();
      const params = {
        ...(isSuperAdmin && instituteId ? { instituteId } : {}),
        academicYear: overviewAcademicYear,
      };
      if (overviewSemester) params.semester = overviewSemester;
      const overviewRes = await api.get('/admin/reports/overview', { params });
      if (overviewRes.data?.data != null) setOverview(overviewRes.data.data);
    } catch (err) {
      setOverviewNotice(err?.response?.data?.error?.message || 'Unable to send reminder.');
    } finally {
      setReminderSendingKey('');
    }
  };

  const fetchLifecycle = async () => {
    if (isSuperAdmin && !instituteId) return;
    const params = isSuperAdmin && instituteId ? { instituteId } : {};
    setLifecycleLoading(true);
    try {
      const [stagesRes, questionnairesRes] = await Promise.all([
        api.get('/admin/feedback/lifecycle', { params }),
        api.get('/admin/feedback/questionnaires', { params }),
      ]);
      const stages = stagesRes.data?.data || [];
      const qs = questionnairesRes.data?.data || [];
      setLifecycleStages(stages);
      setQuestionnaires(qs);
    } catch (err) {
      setLifecycleMessage(err?.response?.data?.error?.message || 'Unable to load feedback lifecycle.');
    } finally {
      setLifecycleLoading(false);
    }
  };

  useEffect(() => {
    if (activeMenu === 'feedback-lifecycle') fetchLifecycle();
  }, [activeMenu, instituteId]);

  const downloadLifecycleTemplate = async () => {
    const res = await api.get('/admin/feedback/questionnaires/template', { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'feedback-questionnaire-template.xlsx');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const openQuestionnairePage = (mode, stageId, questionnaireId = '') => {
    const params = new URLSearchParams({ mode, stageId });
    if (questionnaireId) params.set('questionnaireId', questionnaireId);
    if (isSuperAdmin && instituteId) params.set('instituteId', instituteId);
    const base = process.env.PUBLIC_URL || '';
    window.open(`${base}/dashboard/admin/questionnaire?${params.toString()}`, '_blank', 'noopener,noreferrer');
  };

  const downloadFeedbackQuestionsTemplate = async (type = 'faculty_student') => {
    const res = await api.get('/admin/feedback/questions/template', {
      params: { type },
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', TEMPLATE_FILENAMES[type] || 'questions-template.xlsx');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  useEffect(() => {
    if (isSuperAdmin && !instituteId) return;
    const params = isSuperAdmin && instituteId ? { instituteId } : {};
    if (divisionFilters.degreeId) params.degreeId = divisionFilters.degreeId;
    if (divisionFilters.academicYear) params.academicYear = divisionFilters.academicYear;
    if (divisionFilters.semester !== '' && divisionFilters.semester != null) params.semester = divisionFilters.semester;
    api.get('/admin/divisions', { params })
      .then((res) => setDivisions(Array.isArray(res.data?.data) ? res.data.data : []))
      .catch(() => setDivisions([]));
  }, [instituteId, divisionFilters.degreeId, divisionFilters.academicYear, divisionFilters.semester, isSuperAdmin]);

  useEffect(() => {
    if (!selectedDivisionForStudents || !instituteId) {
      setDivisionStudents([]);
      return;
    }
    const params = isSuperAdmin && instituteId ? { instituteId } : {};
    api.get(`/admin/divisions/${selectedDivisionForStudents}/students`, { params })
      .then((res) => setDivisionStudents(Array.isArray(res.data?.data) ? res.data.data : []))
      .catch(() => setDivisionStudents([]));
  }, [selectedDivisionForStudents, instituteId, isSuperAdmin]);

  const fetchPendingFeedback = async () => {
    if (!instituteId) return;
    setPendingLoading(true);
    const params = isSuperAdmin ? { instituteId } : {};
    if (pendingCycleId) params.cycleId = pendingCycleId;
    params.page = pendingPage;
    params.limit = 20;
    try {
      const res = await api.get('/admin/feedback/pending', { params });
      const d = res.data?.data;
      setPendingFeedback({
        items: Array.isArray(d?.items) ? d.items : [],
        total: d?.total ?? 0,
        page: d?.page ?? 1,
        limit: d?.limit ?? 20,
        totalPages: d?.totalPages ?? 1,
      });
    } catch (err) {
      setPendingFeedback({ items: [], total: 0, page: 1, limit: 20, totalPages: 1 });
    } finally {
      setPendingLoading(false);
    }
  };

  useEffect(() => {
    if (activeMenu === 'feedback-approval' && instituteId) fetchPendingFeedback();
  }, [activeMenu, instituteId, pendingCycleId, pendingPage]);

  const fetchSubmittedFeedbackReport = async () => {
    if (!instituteId) return;
    setSubmittedReportLoading(true);
    const params = isSuperAdmin ? { instituteId } : {};
    if (submittedReportCycleId) params.cycleId = submittedReportCycleId;
    if (submittedReportStatus) params.status = submittedReportStatus;
    params.page = submittedReportPage;
    params.limit = 20;
    try {
      const res = await api.get('/admin/feedback/submitted', { params });
      const d = res.data?.data;
      setSubmittedFeedbackReport({
        items: Array.isArray(d?.items) ? d.items : [],
        total: d?.total ?? 0,
        page: d?.page ?? 1,
        limit: d?.limit ?? 20,
        totalPages: d?.totalPages ?? 1,
      });
    } catch (err) {
      setSubmittedFeedbackReport({ items: [], total: 0, page: 1, limit: 20, totalPages: 1 });
    } finally {
      setSubmittedReportLoading(false);
    }
  };

  useEffect(() => {
    if (activeMenu === 'feedback-approval' && instituteId) fetchSubmittedFeedbackReport();
  }, [activeMenu, instituteId, submittedReportCycleId, submittedReportStatus, submittedReportPage]);

  const fetchStudentFeedbackReport = async () => {
    if (!instituteId) return;
    setStudentFeedbackReportLoading(true);
    const params = isSuperAdmin ? { instituteId } : {};
    if (studentFeedbackReportCycleId) params.cycleId = studentFeedbackReportCycleId;
    if (studentFeedbackReportStatus) params.status = studentFeedbackReportStatus;
    if (pdfReportFilters.academicYear) params.academicYear = pdfReportFilters.academicYear;
    params.page = studentFeedbackReportPage;
    params.limit = 50;
    try {
      const res = await api.get('/admin/reports/students-submitted-feedback', { params });
      const d = res.data?.data;
      setStudentFeedbackReport({
        students: Array.isArray(d?.students) ? d.students : [],
        total: d?.total ?? 0,
        page: d?.page ?? 1,
        limit: d?.limit ?? 50,
        totalPages: d?.totalPages ?? 1,
      });
    } catch (err) {
      setStudentFeedbackReport({ students: [], total: 0, page: 1, limit: 50, totalPages: 1 });
    } finally {
      setStudentFeedbackReportLoading(false);
    }
  };

  useEffect(() => {
    if (activeMenu === 'reports' && instituteId) fetchStudentFeedbackReport();
  }, [activeMenu, instituteId, studentFeedbackReportCycleId, studentFeedbackReportStatus, studentFeedbackReportPage, pdfReportFilters.academicYear]);

  const downloadStudentFeedbackReport = async (format) => {
    if (!instituteId) return;
    const params = isSuperAdmin ? { instituteId } : {};
    if (studentFeedbackReportCycleId) params.cycleId = studentFeedbackReportCycleId;
    if (studentFeedbackReportStatus) params.status = studentFeedbackReportStatus;
    if (pdfReportFilters.academicYear) params.academicYear = pdfReportFilters.academicYear;
    params.format = format;
    params.limit = 5000;
    try {
      const res = await api.get('/admin/reports/students-submitted-feedback', { params: { ...params }, responseType: 'blob' });
      const filename = format === 'csv' ? 'student-feedback-report.csv' : 'student-feedback-report.xlsx';
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.setAttribute('download', filename);
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (_) {}
  };

  const downloadPdfReport = async () => {
    if (!instituteId) return;
    setPdfReportDownloading(true);
    try {
      const params = { reportType: pdfReportType };
      if (isSuperAdmin) params.instituteId = instituteId;
      if (pdfReportFilters.cycleId) params.cycleId = pdfReportFilters.cycleId;
      if (pdfReportFilters.department) params.department = pdfReportFilters.department;
      if (pdfReportFilters.academicYear) params.academicYear = pdfReportFilters.academicYear;
      if (pdfReportFilters.facultyId) params.facultyId = pdfReportFilters.facultyId;
      if (pdfReportFilters.subjectId) params.subjectId = pdfReportFilters.subjectId;
      if (pdfReportFilters.status) params.status = pdfReportFilters.status;
      const res = await api.get('/admin/reports/pdf', { params, responseType: 'blob' });
      const blob = res.data instanceof Blob ? res.data : new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `report-${pdfReportType}-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      let msg = 'Failed to download PDF';
      const data = err.response?.data;
      if (data && typeof data === 'object' && !(data instanceof Blob)) {
        msg = data?.error?.message || msg;
      } else if (data instanceof Blob && data.type?.includes('json')) {
        try {
          const json = JSON.parse(await data.text());
          msg = json?.error?.message || msg;
        } catch (_) {}
      }
      setError(msg);
    } finally {
      setPdfReportDownloading(false);
    }
  };

  const previewReport = async () => {
    if (!instituteId) return;
    setReportPreviewLoading(true);
    try {
      const params = { reportType: pdfReportType };
      if (isSuperAdmin) params.instituteId = instituteId;
      if (pdfReportFilters.cycleId) params.cycleId = pdfReportFilters.cycleId;
      if (pdfReportFilters.department) params.department = pdfReportFilters.department;
      if (pdfReportFilters.academicYear) params.academicYear = pdfReportFilters.academicYear;
      if (pdfReportFilters.facultyId) params.facultyId = pdfReportFilters.facultyId;
      if (pdfReportFilters.subjectId) params.subjectId = pdfReportFilters.subjectId;
      if (pdfReportFilters.status) params.status = pdfReportFilters.status;
      const res = await api.get('/admin/reports/preview', { params });
      setReportPreview(res.data?.data || null);
    } catch (err) {
      setReportPreview({ error: err?.response?.data?.error?.message || 'Unable to preview report.' });
    } finally {
      setReportPreviewLoading(false);
    }
  };

  const fetchAdmissionQuestions = async () => {
    if (!selectedAdmissionCycleId) {
      setAdmissionQuestions([]);
      return;
    }
    setAdmissionQuestionsLoading(true);
    setAdmissionQuestionsError('');
    try {
      const params = isSuperAdmin && instituteId ? { instituteId } : {};
      const res = await api.get(`/admin/feedback/admission/cycles/${selectedAdmissionCycleId}/questions`, { params });
      setAdmissionQuestions(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (err) {
      setAdmissionQuestions([]);
      setAdmissionQuestionsError(err.response?.data?.error?.message || 'Failed to load questions');
    } finally {
      setAdmissionQuestionsLoading(false);
    }
  };

  useEffect(() => {
    if (activeMenu === 'admission-feedback' && selectedAdmissionCycleId) fetchAdmissionQuestions();
    else if (activeMenu === 'admission-feedback' && !selectedAdmissionCycleId) setAdmissionQuestions([]);
  }, [activeMenu, selectedAdmissionCycleId, instituteId]);

  const initiateAdmissionCycle = async () => {
    if (!instituteId) return;
    setAdmissionCycleSubmitting(true);
    setError('');
    try {
      const name = admissionCycleForm.name?.trim() || `Admission Feedback ${admissionCycleForm.academicYear}`;
      const body = {
        name,
        type: 'ADMISSION',
        academicYear: admissionCycleForm.academicYear,
        semester: Number(admissionCycleForm.semester) || 1,
        startDate: admissionCycleForm.startDate || new Date().toISOString().split('T')[0],
        endDate: admissionCycleForm.endDate || defaultCycleEnd(),
        isAnonymous: false,
      };
      const params = isSuperAdmin && instituteId ? { instituteId } : {};
      const { data } = await api.post('/admin/cycles', body, { params });
      const cycleId = data?.data?.id;
      if (cycleId) {
        await api.post(`/admin/cycles/${cycleId}/activate`, isSuperAdmin && instituteId ? { instituteId } : {});
        setCycles((prev) => {
          const created = { ...data.data, type: 'ADMISSION' };
          return [created, ...(prev || [])];
        });
        setSelectedAdmissionCycleId(cycleId);
        setAdmissionCycleForm((f) => ({ ...f, name: '' }));
      }
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message || 'Failed to create admission cycle');
    } finally {
      setAdmissionCycleSubmitting(false);
    }
  };

  const initiateParentAlumniCycle = async () => {
    if (!instituteId) return;
    const t = parentAlumniCycleForm.type;
    const label = t === 'PARENT_FEEDBACK' ? 'Parent' : t === 'INDUSTRY_EXPERT_FEEDBACK' ? 'Industry expert' : 'Alumni';
    setParentAlumniCycleSubmitting(true);
    setError('');
    try {
      const name =
        parentAlumniCycleForm.name?.trim() ||
        `${label} Feedback ${parentAlumniCycleForm.academicYear}`;
      const body = {
        name,
        type: t,
        academicYear: parentAlumniCycleForm.academicYear,
        semester: Number(parentAlumniCycleForm.semester) || 1,
        startDate: parentAlumniCycleForm.startDate || new Date().toISOString().split('T')[0],
        endDate: parentAlumniCycleForm.endDate || defaultCycleEnd(),
        isAnonymous: false,
        alumniTaskType: t === 'ALUMNI_FEEDBACK' ? parentAlumniCycleForm.alumniTaskType : undefined,
        expertTaskType: t === 'INDUSTRY_EXPERT_FEEDBACK' ? parentAlumniCycleForm.expertTaskType : undefined,
        parentTaskType: t === 'PARENT_FEEDBACK' ? parentAlumniCycleForm.parentTaskType : undefined,
      };
      const params = isSuperAdmin && instituteId ? { instituteId } : {};
      const { data } = await api.post('/admin/cycles', body, { params });
      const cycleId = data?.data?.id;
      if (cycleId) {
        await api.post(`/admin/cycles/${cycleId}/activate`, params);
        setCycles((prev) => [data.data, ...(prev || [])]);
        setParentAlumniCycleForm((f) => ({ ...f, name: '' }));
      }
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message || `Failed to create ${label.toLowerCase()} cycle`);
    } finally {
      setParentAlumniCycleSubmitting(false);
    }
  };

  const addAdmissionQuestion = async (e) => {
    e.preventDefault();
    if (!selectedAdmissionCycleId) return;
    const questionText = (admissionQuestionForm.questionText || '').trim();
    const options = (admissionQuestionForm.optionsText || '')
      .split(/[,;]/)
      .map((o) => o.trim())
      .filter(Boolean);
    if (!questionText || options.length < 2) {
      setAdmissionQuestionsError('Question text and at least 2 options (comma- or semicolon-separated) are required.');
      return;
    }
    setAddQuestionSubmitting(true);
    setAdmissionQuestionsError('');
    try {
      const params = isSuperAdmin && instituteId ? { instituteId } : {};
      await api.post(
        `/admin/feedback/admission/cycles/${selectedAdmissionCycleId}/questions`,
        { questions: [{ questionText, options, displayOrder: admissionQuestions.length }] },
        { params }
      );
      setAdmissionQuestionForm({ questionText: '', optionsText: '', displayOrder: admissionQuestions.length + 1 });
      await fetchAdmissionQuestions();
    } catch (err) {
      setAdmissionQuestionsError(err.response?.data?.error?.message || 'Failed to add question');
    } finally {
      setAddQuestionSubmitting(false);
    }
  };

  const uploadAdmissionExcel = async (e) => {
    e.preventDefault();
    if (!selectedAdmissionCycleId || !admissionExcelFile) return;
    setUploadExcelSubmitting(true);
    setAdmissionQuestionsError('');
    try {
      const formData = new FormData();
      formData.append('file', admissionExcelFile);
      const params = isSuperAdmin && instituteId ? { instituteId } : {};
      await api.post(
        `/admin/feedback/admission/cycles/${selectedAdmissionCycleId}/questions/upload`,
        formData,
        { params, headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setAdmissionExcelFile(null);
      await fetchAdmissionQuestions();
    } catch (err) {
      setAdmissionQuestionsError(err.response?.data?.error?.message || 'Failed to upload Excel');
    } finally {
      setUploadExcelSubmitting(false);
    }
  };

  const fetchBilling = async () => {
    if (!instituteId) return;
    setBillingLoading(true);
    try {
      const params = isSuperAdmin && instituteId ? { instituteId } : {};
      const [subRes, payRes] = await Promise.all([
        api.get('/admin/subscription', { params }),
        api.get('/admin/payments', { params: { ...params, limit: 100 } }),
      ]);
      setBillingSubscription(subRes.data?.data ?? null);
      setBillingPayments(Array.isArray(payRes.data?.data) ? payRes.data.data : []);
    } catch (err) {
      setBillingSubscription(null);
      setBillingPayments([]);
    } finally {
      setBillingLoading(false);
    }
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve();
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = resolve;
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handlePayWithRazorpay = async () => {
    const amount = parseFloat(razorpayAmount);
    if (!amount || amount < 1) {
      setError('Please enter a valid amount (minimum â‚¹1)');
      return;
    }
    setRazorpayPaying(true);
    setError('');
    try {
      const params = isSuperAdmin && instituteId ? { instituteId } : {};
      const sub = billingSubscription;
      const { data } = await api.post('/admin/payments/razorpay/create-order', {
        amount,
        currency: 'INR',
        subscriptionId: sub?.id || null,
        planName: sub?.plan_name || sub?.plan_slug || null,
      }, { params });
      const { orderId, keyId } = data.data;
      const loaded = await loadRazorpayScript();
      if (!loaded || !window.Razorpay) {
        setError('Failed to load Razorpay. Check your connection.');
        return;
      }
      const options = {
        key: keyId,
        amount: data.data.amount,
        currency: data.data.currency,
        name: '360 Feedback',
        description: sub?.plan_name ? `Payment for ${sub.plan_name}` : 'Subscription payment',
        order_id: orderId,
        handler: async (response) => {
          try {
            await api.post('/admin/payments/razorpay/verify', {
              razorpayOrderId: response.razorpay_order_id,
              razorpayPaymentId: response.razorpay_payment_id,
              razorpaySignature: response.razorpay_signature,
            }, { params });
            setRazorpayAmount('');
            await fetchBilling();
          } catch (err) {
            setError(err.response?.data?.error?.message || 'Payment verification failed');
          } finally {
            setRazorpayPaying(false);
          }
        },
        prefill: { email: user?.email || '' },
        theme: { color: '#0f172a' },
      };
      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', () => {
        setError('Payment failed. Please try again.');
        setRazorpayPaying(false);
      });
      rzp.on('modal.close', () => setRazorpayPaying(false));
      rzp.open();
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to create payment order');
      setRazorpayPaying(false);
    }
  };

  useEffect(() => {
    if (activeMenu === 'billing' && instituteId) fetchBilling();
  }, [activeMenu, instituteId]);

  const handleApproveFeedback = async () => {
    const ids = selectedPendingIds.length ? selectedPendingIds : pendingFeedback.items.map((i) => i.id);
    if (!ids.length) return;
    setApproveSubmitting(true);
    try {
      const params = isSuperAdmin && instituteId ? { instituteId } : {};
      await api.post('/admin/feedback/approve', { submissionIds: ids }, { params });
      setSelectedPendingIds([]);
      await fetchPendingFeedback();
      await fetchSubmittedFeedbackReport();
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to approve feedback');
    } finally {
      setApproveSubmitting(false);
    }
  };

  const studentOperationalDivisions = useMemo(
    () => filterOperationalDivisions(studentDivisionsCatalog),
    [studentDivisionsCatalog]
  );

  const studentDivisionOptions = useMemo(
    () => filterDivisionsForStudentContext(studentOperationalDivisions, studentFilters),
    [studentOperationalDivisions, studentFilters.academicYear, studentFilters.degreeId, studentFilters.semester]
  );

  const studentCanShowRoster = Boolean(
    studentFilters.academicYear
    && studentFilters.department
    && studentFilters.degreeId
    && studentFilters.semester !== ''
    && studentFilters.semester != null
    && (
      studentDivisionOptions.length === 0
      || studentFilters.divisionId
    )
  );

  const studentAcademicYears = useMemo(() => [OPERATIONAL_ACADEMIC_YEAR], []);

  const studentSemesterOptions = useMemo(() => {
    const degreeId = studentFilters.degreeId;
    const program = degrees.find((d) => d.id === degreeId);
    const max = Number(program?.number_of_semesters || 8);
    const fromCatalog = studentOperationalDivisions
      .filter((d) => {
        const deg = d.degree_id ?? d.degreeId;
        return !degreeId || !deg || String(deg) === String(degreeId);
      })
      .map((d) => Number(d.semester))
      .filter(Boolean);
    const catalogOdd = filterOperationalSemesters(Array.from(new Set(fromCatalog)));
    const allowed = filterOperationalSemesters(OPERATIONAL_ODD_SEMESTERS.filter((s) => s <= max));
    return Array.from(new Set([...allowed, ...catalogOdd])).sort((a, b) => a - b);
  }, [studentOperationalDivisions, studentFilters.degreeId, degrees]);

  const studentHighlightMatchCount = studentHighlightTerm
    ? studentsList.filter((s) => {
      const t = String(studentHighlightTerm).trim().toLowerCase();
      if (!t) return false;
      const full = `${s.first_name || ''} ${s.last_name || ''}`.trim().toLowerCase();
      return full.includes(t)
        || String(s.roll_number || '').toLowerCase().includes(t)
        || String(s.email || '').toLowerCase().includes(t);
    }).length
    : 0;

  const fetchStudents = useCallback((filterSource, overrides = {}) => {
    if (isSuperAdmin && !instituteId) return Promise.resolve();
    const filters = filterSource || studentAppliedFilters;
    const divisionsForApply = filterDivisionsForStudentContext(studentOperationalDivisions, filters);
    const ready = filters
      && filters.academicYear
      && filters.department
      && filters.degreeId
      && filters.semester !== ''
      && filters.semester != null
      && (filters.divisionId || divisionsForApply.length === 0);
    if (!ready) {
      setStudentsList([]);
      setStudentPagination((prev) => ({ ...prev, total: 0, totalPages: 1, page: 1 }));
      return Promise.resolve();
    }
    const params = isSuperAdmin && instituteId ? { instituteId } : {};
    params.degreeId = filters.degreeId;
    params.academicYear = filters.academicYear;
    params.semester = filters.semester;
    params.divisionId = filters.divisionId;
    if (!filters.divisionId && filters.department) {
      params.department = filters.department;
    }
    params.page = overrides.page ?? studentPagination.page;
    params.limit = overrides.limit ?? 500;
    setStudentRosterLoading(true);
    return api.get('/admin/students', { params })
      .then((res) => {
        setStudentsList(Array.isArray(res.data?.data) ? res.data.data : []);
        setStudentPagination((prev) => ({ ...prev, ...(res.data?.pagination || {}) }));
      })
      .catch(() => setStudentsList([]))
      .finally(() => setStudentRosterLoading(false));
  }, [instituteId, isSuperAdmin, studentAppliedFilters, studentPagination.page, studentOperationalDivisions]);

  const handleShowStudentRoster = () => {
    if (!studentCanShowRoster) return;
    const applied = { ...studentFilters };
    setStudentAppliedFilters(applied);
    setStudentRosterShown(true);
    setStudentPagination((p) => ({ ...p, page: 1 }));
    fetchStudents(applied, { page: 1 });
  };

  useEffect(() => {
    if (activeMenu !== 'students' || (isSuperAdmin && !instituteId)) return;
    const params = isSuperAdmin && instituteId ? { instituteId } : {};
    api.get('/admin/divisions', { params: { ...params, academicYear: OPERATIONAL_ACADEMIC_YEAR } })
      .then((res) => setStudentDivisionsCatalog(Array.isArray(res.data?.data) ? res.data.data : []))
      .catch(() => setStudentDivisionsCatalog([]));
  }, [activeMenu, instituteId, isSuperAdmin]);

  useEffect(() => {
    if (activeMenu !== 'students') return;
    setStudentFilters((f) => {
      const sem = f.semester;
      const semInvalid = sem !== '' && sem != null && !OPERATIONAL_ODD_SEMESTERS.includes(Number(sem));
      if (f.academicYear === OPERATIONAL_ACADEMIC_YEAR && !semInvalid) return f;
      return {
        ...f,
        academicYear: OPERATIONAL_ACADEMIC_YEAR,
        semester: semInvalid ? '' : f.semester,
        divisionId: semInvalid ? '' : f.divisionId,
      };
    });
  }, [activeMenu]);

  const facultyOperationalDivisions = useMemo(
    () => filterOperationalDivisions(facultyDivisionsCatalog),
    [facultyDivisionsCatalog]
  );

  const facultyCanShowRoster = Boolean(
    facultyFilters.academicYear
    && facultyFilters.department
    && facultyFilters.degreeId
  );

  const facultyAppliedProgramLabel = useMemo(() => {
    if (!facultyAppliedFilters?.degreeId) return '';
    if (facultyAppliedFilters.degreeId === FACULTY_ALL_PROGRAMS) return '';
    const deg = degrees.find((d) => d.id === facultyAppliedFilters.degreeId);
    return deg?.program_title || deg?.name || '';
  }, [facultyAppliedFilters, degrees]);

  const facultyAcademicYears = useMemo(() => [OPERATIONAL_ACADEMIC_YEAR], []);

  const facultySemesterOptions = useMemo(() => {
    const degreeId = facultyFilters.degreeId;
    const deptName = facultyFilters.department;
    let max = 8;
    if (degreeId && degreeId !== FACULTY_ALL_PROGRAMS) {
      max = Number(degrees.find((d) => d.id === degreeId)?.number_of_semesters || 8);
    } else if (deptName) {
      max = degrees
        .filter((d) => d.department_code && departments.some((dep) => dep.name === deptName && dep.code === d.department_code))
        .reduce((m, d) => Math.max(m, Number(d.number_of_semesters || 8)), 8);
    }
    const fromCatalog = facultyOperationalDivisions
      .filter((d) => {
        const deg = d.degree_id ?? d.degreeId;
        if (degreeId && degreeId !== FACULTY_ALL_PROGRAMS) {
          return !deg || String(deg) === String(degreeId);
        }
        if (deptName && deg) {
          const prog = degrees.find((p) => String(p.id) === String(deg));
          if (prog?.department_code) {
            const dep = departments.find((x) => x.name === deptName);
            return dep && String(prog.department_code) === String(dep.code);
          }
        }
        return true;
      })
      .map((d) => Number(d.semester))
      .filter(Boolean);
    const catalogOdd = filterOperationalSemesters(Array.from(new Set(fromCatalog)));
    const allowed = filterOperationalSemesters(OPERATIONAL_ODD_SEMESTERS.filter((s) => s <= max));
    return Array.from(new Set([...allowed, ...catalogOdd])).sort((a, b) => a - b);
  }, [facultyOperationalDivisions, facultyFilters.degreeId, facultyFilters.department, degrees, departments]);

  const facultyHighlightMatchCount = facultyHighlightTerm
    ? facultyRosterList.filter((f) => {
      const t = String(facultyHighlightTerm).trim().toLowerCase();
      if (!t) return false;
      const full = `${f.first_name || ''} ${f.last_name || ''}`.trim().toLowerCase();
      return full.includes(t)
        || String(f.employee_id || '').toLowerCase().includes(t)
        || String(f.email || '').toLowerCase().includes(t)
        || String(f.specialization || '').toLowerCase().includes(t);
    }).length
    : 0;

  const fetchFacultyRoster = useCallback((filterSource, overrides = {}) => {
    if (isSuperAdmin && !instituteId) return Promise.resolve();
    const filters = filterSource || facultyAppliedFilters;
    const ready = filters && filters.academicYear && filters.department && filters.degreeId;
    if (!ready) {
      setFacultyRosterList([]);
      setFacultyPagination((prev) => ({ ...prev, total: 0, totalPages: 1, page: 1 }));
      return Promise.resolve();
    }
    const params = isSuperAdmin && instituteId ? { instituteId } : {};
    params.academicYear = filters.academicYear;
    params.department = filters.department;
    if (filters.degreeId && filters.degreeId !== FACULTY_ALL_PROGRAMS) {
      params.degreeId = filters.degreeId;
    }
    if (filters.semester !== '' && filters.semester != null) {
      params.semester = filters.semester;
    }
    if (filters.divisionId) {
      params.divisionId = filters.divisionId;
    }
    params.page = overrides.page ?? facultyPagination.page;
    params.limit = overrides.limit ?? 500;
    setFacultyRosterLoading(true);
    return api.get('/admin/faculty', { params })
      .then((res) => {
        setFacultyRosterList(Array.isArray(res.data?.data) ? res.data.data : []);
        setFacultyPagination((prev) => ({ ...prev, ...(res.data?.pagination || {}) }));
      })
      .catch((err) => {
        setFacultyRosterList([]);
        const msg = err.response?.data?.error?.message || err.message || 'Failed to load faculty roster';
        if (err.response?.status === 404) {
          setError(
            `Faculty API not found at ${API_URL}/admin/faculty. `
            + 'Stop every old backend on port 5000, then run start-backend.bat (or npm run dev in backend). '
            + 'REACT_APP_API_URL must end with /api (e.g. http://localhost:5000/api).'
          );
        } else {
          setError(msg);
        }
      })
      .finally(() => setFacultyRosterLoading(false));
  }, [instituteId, isSuperAdmin, facultyPagination.page]);

  const handleShowFacultyRoster = () => {
    if (!facultyCanShowRoster) return;
    const applied = { ...facultyFilters };
    setFacultyAppliedFilters(applied);
    setFacultyRosterShown(true);
    setFacultyPagination((p) => ({ ...p, page: 1 }));
    fetchFacultyRoster(applied, { page: 1 });
  };

  const handleSeedProgramFaculty = async () => {
    if (isSuperAdmin && !instituteId) return;
    setFacultySeedSubmitting(true);
    setError('');
    try {
      const params = isSuperAdmin && instituteId ? { instituteId } : {};
      const { data } = await api.post('/admin/faculty/seed-program-roster', {}, { params });
      const msg = data?.message || 'Program faculty roster updated.';
      setError('');
      setFacultySeedNotice(msg);
      await fetchData();
      if (facultyCanShowRoster) {
        handleShowFacultyRoster();
      }
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message || 'Failed to create program faculty roster');
    } finally {
      setFacultySeedSubmitting(false);
    }
  };

  useEffect(() => {
    if (activeMenu !== 'faculty' || (isSuperAdmin && !instituteId)) return;
    const params = isSuperAdmin && instituteId ? { instituteId } : {};
    api.get('/admin/divisions', { params: { ...params, academicYear: OPERATIONAL_ACADEMIC_YEAR } })
      .then((res) => setFacultyDivisionsCatalog(Array.isArray(res.data?.data) ? res.data.data : []))
      .catch(() => setFacultyDivisionsCatalog([]));
  }, [activeMenu, instituteId, isSuperAdmin]);

  useEffect(() => {
    if (activeMenu !== 'faculty') return;
    setFacultyFilters((f) => {
      const sem = f.semester;
      const semInvalid = sem !== '' && sem != null && !OPERATIONAL_ODD_SEMESTERS.includes(Number(sem));
      if (f.academicYear === OPERATIONAL_ACADEMIC_YEAR && !semInvalid) return f;
      return {
        ...f,
        academicYear: OPERATIONAL_ACADEMIC_YEAR,
        semester: semInvalid ? '' : f.semester,
        divisionId: semInvalid ? '' : f.divisionId,
      };
    });
  }, [activeMenu]);

  useEffect(() => {
    if (activeMenu !== 'faculty') {
      facultyInitialLoadRef.current = false;
      return;
    }
    if (loading || (isSuperAdmin && !instituteId) || facultyInitialLoadRef.current) return;

    const deptList = departments.length > 0
      ? departments
      : departmentOptions.map((name) => ({ name, code: name }));
    if (deptList.length === 0) return;

    const filters = {
      academicYear: OPERATIONAL_ACADEMIC_YEAR,
      department: deptList[0].name,
      degreeId: FACULTY_ALL_PROGRAMS,
      semester: '',
      divisionId: '',
    };
    facultyInitialLoadRef.current = true;
    setFacultyFilters(filters);
  }, [
    activeMenu,
    loading,
    departments,
    departmentOptions,
    instituteId,
    isSuperAdmin,
  ]);

  useEffect(() => {
    if (activeMenu !== 'faculty' || (isSuperAdmin && !instituteId)) return;
    const f = facultyFilters;
    if (!f.academicYear || !f.department || !f.degreeId) return;
    setFacultyAppliedFilters(f);
    setFacultyRosterShown(true);
    setFacultyPagination((p) => ({ ...p, page: 1 }));
    fetchFacultyRoster(f, { page: 1 });
  }, [
    activeMenu,
    instituteId,
    isSuperAdmin,
    facultyFilters.academicYear,
    facultyFilters.department,
    facultyFilters.degreeId,
    facultyFilters.semester,
    facultyFilters.divisionId,
    fetchFacultyRoster,
  ]);

  useEffect(() => {
    if (activeMenu !== 'subjects') {
      subjectInitialLoadRef.current = false;
      return;
    }
    if (loading || (isSuperAdmin && !instituteId) || subjectInitialLoadRef.current) return;
    const deptList = departments.length > 0
      ? departments
      : departmentOptions.map((name) => ({ id: name, name, code: name }));
    if (deptList.length === 0 || degrees.length === 0) return;
    const firstDept = deptList[0].name;
    const firstProg = degrees.find((p) => {
      const dept = departments.find((d) => d.name === firstDept);
      if (!dept) return true;
      return p.department_id === dept.id
        || p.department_code === dept.code
        || p.department === firstDept;
    }) || degrees[0];
    const filters = {
      department: firstDept,
      degreeId: firstProg.id,
      semester: '',
    };
    subjectInitialLoadRef.current = true;
    setSubjectFilters(filters);
    setSubjectAppliedFilters(filters);
    setSubjectRosterShown(true);
    fetchSubjectRoster(filters);
  }, [
    activeMenu,
    loading,
    departments,
    departmentOptions,
    degrees,
    instituteId,
    isSuperAdmin,
    fetchSubjectRoster,
  ]);

  useEffect(() => {
    if (activeMenu !== 'assign') {
      allocationInitialLoadRef.current = false;
      return;
    }
    if (loading || (isSuperAdmin && !instituteId) || allocationInitialLoadRef.current) return;
    const deptList = departments.length > 0
      ? departments
      : departmentOptions.map((name) => ({ id: name, name, code: name }));
    if (deptList.length === 0 || degrees.length === 0) return;
    const firstDept = deptList[0].name;
    const firstProg = degrees.find((p) => {
      const dept = departments.find((d) => d.name === firstDept);
      if (!dept) return true;
      return p.department_id === dept.id
        || p.department_code === dept.code
        || p.department === firstDept;
    }) || degrees[0];
    const filters = {
      academicYear: OPERATIONAL_ACADEMIC_YEAR,
      department: firstDept,
      degreeId: firstProg.id,
      semester: '',
    };
    allocationInitialLoadRef.current = true;
    setAllocationFilters(filters);
    setAllocationAppliedFilters(filters);
    setAllocationListShown(true);
    fetchAllocationRoster(filters);
  }, [
    activeMenu,
    loading,
    departments,
    departmentOptions,
    degrees,
    instituteId,
    isSuperAdmin,
    fetchAllocationRoster,
  ]);

  useEffect(() => {
    if (!activeCycle || !instituteId) {
      setActiveSemesterAssignments([]);
      return;
    }
    const params = isSuperAdmin && instituteId ? { instituteId } : {};
    api
      .get('/admin/subject-faculty-assignments', {
        params: {
          ...params,
          academicYear: activeCycle.academic_year,
          semester: activeCycle.semester,
        },
      })
      .then((res) => setActiveSemesterAssignments(Array.isArray(res.data?.data) ? res.data.data : []))
      .catch(() => setActiveSemesterAssignments([]));
  }, [activeCycle?.id, activeCycle?.academic_year, activeCycle?.semester, instituteId, isSuperAdmin]);

  const initiateFeedbackCycle = async () => {
    setError('');
    setCycleSubmitting(true);
    try {
      const startDate = cycleForm.startDate || new Date().toISOString().split('T')[0];
      let endDate = cycleForm.endDate;
      if (!endDate) {
        const end = new Date(startDate);
        end.setDate(end.getDate() + 90);
        endDate = end.toISOString().split('T')[0];
      }
      if (endDate <= startDate) {
        setError('End date must be after start date.');
        setCycleSubmitting(false);
        return;
      }
      const name = `Student Feedback Schedule ${cycleForm.academicYear} | Semester ${cycleForm.semester}`;
      const body = {
        name,
        type: 'SEMESTER',
        academicYear: cycleForm.academicYear,
        semester: cycleForm.semester,
        startDate,
        endDate,
        isAnonymous: Boolean(cycleForm.isAnonymous),
      };
      if (isSuperAdmin && instituteId) body.instituteId = instituteId;
      await api.post('/admin/cycles', body);
      await fetchData();
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message || 'Failed to save feedback schedule');
    } finally {
      setCycleSubmitting(false);
    }
  };

  const activateCycle = async (cycleId) => {
    setCycleActionId(cycleId);
    try {
      const body = isSuperAdmin && instituteId ? { instituteId } : {};
      await api.post(`/admin/cycles/${cycleId}/activate`, body);
      await fetchData();
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to activate');
    } finally {
      setCycleActionId(null);
    }
  };

  const deactivateCycle = async (cycleId) => {
    setCycleActionId(cycleId);
    try {
      const body = isSuperAdmin && instituteId ? { instituteId } : {};
      await api.post(`/admin/cycles/${cycleId}/deactivate`, body);
      await fetchData();
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to deactivate');
    } finally {
      setCycleActionId(null);
    }
  };

  const approveSemesterChangeApplication = async (applicationId) => {
    setSemesterChangeActionId(applicationId);
    try {
      const body = isSuperAdmin && instituteId ? { instituteId } : {};
      await api.post(`/admin/semester-change-applications/${applicationId}/approve`, body);
      await fetchData();
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to approve');
    } finally {
      setSemesterChangeActionId(null);
    }
  };

  const rejectSemesterChangeApplication = async (applicationId) => {
    setSemesterChangeActionId(applicationId);
    try {
      const body = isSuperAdmin && instituteId ? { instituteId } : {};
      await api.post(`/admin/semester-change-applications/${applicationId}/reject`, body);
      await fetchData();
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to reject');
    } finally {
      setSemesterChangeActionId(null);
    }
  };

  const submitAssignSubjectFaculty = async (e) => {
    e.preventDefault();
    if (!activeCycle || !assignSubjectFacultyForm.subjectId || !assignSubjectFacultyForm.facultyId) return;
    setAssignError('');
    setAssignSubmitting(true);
    try {
      const body = {
        facultyId: assignSubjectFacultyForm.facultyId,
        academicYear: activeCycle.academic_year,
        semester: activeCycle.semester,
        divisionId: (assignSubjectFacultyForm.divisionId || '').trim() || undefined,
        section: (assignSubjectFacultyForm.section || '').trim() || undefined,
        isPrimary: Boolean(assignSubjectFacultyForm.isPrimary),
      };
      if (isSuperAdmin && instituteId) body.instituteId = instituteId;
      await api.post(`/admin/subjects/${assignSubjectFacultyForm.subjectId}/assign-faculty`, body);
      setAssignSubjectFacultyForm({ subjectId: '', facultyId: '', divisionId: '', section: '', isPrimary: true });
      const params = isSuperAdmin && instituteId ? { instituteId } : {};
      const res = await api.get('/admin/subject-faculty-assignments', {
        params: { ...params, academicYear: activeCycle.academic_year, semester: activeCycle.semester },
      });
      setActiveSemesterAssignments(Array.isArray(res.data?.data) ? res.data.data : []);
      refreshAllocationRosterIfShown();
    } catch (err) {
      setAssignError(err.response?.data?.error?.message || err.message || 'Failed to assign');
    } finally {
      setAssignSubmitting(false);
    }
  };

  const submitAddSubject = async (e) => {
    e.preventDefault();
    setError('');
    setSubjectSubmitting(true);
    try {
      const body = {
        code: subjectForm.code.trim(),
        name: subjectForm.name.trim(),
        degreeId: subjectForm.degreeId || undefined,
        degree: subjectForm.degree,
        department: subjectForm.department,
        departmentCode: subjectForm.departmentCode || undefined,
        programCode: subjectForm.programCode || undefined,
        semesterCode: subjectForm.semesterCode || undefined,
        divisionCode: subjectForm.divisionCode || undefined,
        semester: Number(subjectForm.semester),
        credits: Number(subjectForm.credits),
        subjectType: subjectForm.subjectType,
        workloadTheoryHrs: subjectForm.workloadTheoryHrs === '' ? undefined : Number(subjectForm.workloadTheoryHrs),
        workloadPracticalHrs: subjectForm.workloadPracticalHrs === '' ? undefined : Number(subjectForm.workloadPracticalHrs),
        numberOfUnits: subjectForm.numberOfUnits === '' ? undefined : Number(subjectForm.numberOfUnits),
        syllabusContent: subjectForm.syllabusContent || undefined,
        isElective: subjectForm.isElective,
        description: subjectForm.description.trim() || undefined,
      };
      if (isSuperAdmin && instituteId) body.instituteId = instituteId;
      await api.post('/admin/subjects', body);
      setShowAddSubject(false);
      const defaultDegree = degreeOptions.length > 0 ? degreeOptions[0] : 'B.Tech';
      setSubjectForm({
        code: '',
        name: '',
        degree: defaultDegree,
        degreeId: degrees[0]?.id || '',
        programCode: degrees[0]?.program_code || degrees[0]?.code || '',
        departmentCode: degrees[0]?.department_code || '',
        semesterCode: degrees[0]?.program_code ? `${degrees[0].program_code}-S1` : '',
        divisionCode: '',
        department: getProgramDepartmentName(degrees[0]) || departmentOptions[0] || 'CSE',
        semester: 1,
        credits: 3,
        subjectType: 'Core',
        workloadTheoryHrs: 3,
        workloadPracticalHrs: 2,
        numberOfUnits: 5,
        syllabusContent: '',
        isElective: false,
        description: '',
      });
      await fetchData();
      refreshSubjectRosterIfShown();
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message || 'Failed to add subject');
    } finally {
      setSubjectSubmitting(false);
    }
  };

  const startEditSubject = (s) => {
    setEditingSubjectId(s.id);
    setEditSubjectForm({
      name: s.name || '',
      degreeId: degrees.find((d) => d.name === s.degree || d.program_code === s.program_code || d.code === s.program_code)?.id || '',
      degree: s.degree || 'B.Tech',
      programCode: s.program_code || '',
      departmentCode: s.department_code || '',
      semesterCode: s.semester_code || '',
      divisionCode: s.division_code || '',
      department: s.department || 'CSE',
      semester: s.semester ?? 5,
      credits: s.credits ?? 3,
      subjectType: s.subject_type || 'Core',
      workloadTheoryHrs: s.workload_theory_hrs ?? 3,
      workloadPracticalHrs: s.workload_practical_hrs ?? 2,
      numberOfUnits: s.number_of_units ?? 5,
      syllabusContent: s.syllabus_content || '',
      isElective: s.is_elective ?? false,
      description: s.description || '',
    });
  };

  const submitEditSubject = async (e) => {
    e.preventDefault();
    setError('');
    setSubjectEditSubmitting(true);
    try {
      const params = isSuperAdmin && instituteId ? { instituteId } : {};
      const semester = Math.min(10, Math.max(1, parseInt(editSubjectForm.semester, 10) || 1));
      const credits = Math.max(1, parseInt(editSubjectForm.credits, 10) || 1);
      const body = {
        name: String(editSubjectForm.name || '').trim(),
        degree: String(editSubjectForm.degree || 'B.Tech'),
        department: String(editSubjectForm.department || 'CSE'),
        departmentCode: editSubjectForm.departmentCode || undefined,
        programCode: editSubjectForm.programCode || undefined,
        semesterCode: editSubjectForm.semesterCode || undefined,
        divisionCode: editSubjectForm.divisionCode || undefined,
        semester,
        credits,
        subjectType: editSubjectForm.subjectType,
        workloadTheoryHrs: editSubjectForm.workloadTheoryHrs === '' ? undefined : Number(editSubjectForm.workloadTheoryHrs),
        workloadPracticalHrs: editSubjectForm.workloadPracticalHrs === '' ? undefined : Number(editSubjectForm.workloadPracticalHrs),
        numberOfUnits: editSubjectForm.numberOfUnits === '' ? undefined : Number(editSubjectForm.numberOfUnits),
        syllabusContent: editSubjectForm.syllabusContent || undefined,
        isElective: Boolean(editSubjectForm.isElective),
        description: String(editSubjectForm.description ?? '').trim(),
      };
      await api.put(`/admin/subjects/${editingSubjectId}`, body, { params });
      setEditingSubjectId(null);
      await fetchData();
      refreshSubjectRosterIfShown();
    } catch (err) {
      const msg = err.response?.data?.error?.message || err.response?.data?.error?.details?.[0]?.message || err.message || 'Failed to update subject';
      setError(msg);
    } finally {
      setSubjectEditSubmitting(false);
    }
  };

  const submitAddDegree = async (e) => {
    e.preventDefault();
    setError('');
    const name = (degreeForm.name || '').trim();
    if (!name) {
      setError('Degree name is required');
      return;
    }
    setDegreeSubmitting(true);
    try {
      const params = isSuperAdmin && instituteId ? { instituteId } : {};
      const dept = departments.find((d) => d.id === degreeForm.departmentId);
      const body = {
        name,
        code: (degreeForm.code || '').trim().toUpperCase() || undefined,
        programType: degreeForm.programType || undefined,
        programTitle: degreeForm.programTitle || name,
        degreeLevel: degreeForm.degreeLevel || undefined,
        affiliatedUniversity: degreeForm.affiliatedUniversity || undefined,
        departmentId: degreeForm.departmentId || undefined,
        departmentCode: dept?.code || undefined,
        numberOfSemesters: degreeForm.numberOfSemesters ? Number(degreeForm.numberOfSemesters) : undefined,
        durationYears: degreeForm.durationYears ? Number(degreeForm.durationYears) : undefined,
        mode: degreeForm.mode || undefined,
        intakeCapacity: degreeForm.intakeCapacity ? Number(degreeForm.intakeCapacity) : undefined,
        regulatoryBody: degreeForm.regulatoryBody || undefined,
        eligibility: degreeForm.eligibility || undefined,
        description: degreeForm.description || undefined,
      };
      await api.post('/admin/degrees', body, { params });
      setShowAddDegree(false);
      setDegreeForm(emptyDegreeForm);
      await fetchData();
    } catch (err) {
      const msg = err.response?.data?.error?.message || err.response?.data?.error?.details?.[0]?.message || err.message || 'Failed to add degree';
      setError(msg);
    } finally {
      setDegreeSubmitting(false);
    }
  };

  const startEditDegree = (d) => {
    setEditingDegreeId(d.id);
    setEditDegreeForm({
      name: d.name || '',
      code: d.code || '',
      programType: d.program_type || '',
      programTitle: d.program_title || d.name || '',
      degreeLevel: d.degree_level || 'UG',
      affiliatedUniversity: d.affiliated_university || '',
      departmentId: d.department_id || '',
      numberOfSemesters: d.number_of_semesters || 8,
      durationYears: d.duration_years || 4,
      mode: d.mode || 'Full Time',
      intakeCapacity: d.intake_capacity || '',
      regulatoryBody: d.regulatory_body || '',
      eligibility: d.eligibility || '',
      description: d.description || '',
    });
  };

  const submitEditDegree = async (e) => {
    e.preventDefault();
    setError('');
    setDegreeEditSubmitting(true);
    try {
      const params = isSuperAdmin && instituteId ? { instituteId } : {};
      const dept = departments.find((d) => d.id === editDegreeForm.departmentId);
      const body = {
        name: editDegreeForm.name.trim(),
        code: editDegreeForm.code.trim().toUpperCase() || undefined,
        programType: editDegreeForm.programType || null,
        programTitle: editDegreeForm.programTitle || editDegreeForm.name.trim(),
        degreeLevel: editDegreeForm.degreeLevel || null,
        affiliatedUniversity: editDegreeForm.affiliatedUniversity || null,
        departmentId: editDegreeForm.departmentId || null,
        departmentCode: dept?.code || null,
        numberOfSemesters: editDegreeForm.numberOfSemesters ? Number(editDegreeForm.numberOfSemesters) : null,
        durationYears: editDegreeForm.durationYears ? Number(editDegreeForm.durationYears) : null,
        mode: editDegreeForm.mode || null,
        intakeCapacity: editDegreeForm.intakeCapacity ? Number(editDegreeForm.intakeCapacity) : null,
        regulatoryBody: editDegreeForm.regulatoryBody || null,
        eligibility: editDegreeForm.eligibility || null,
        description: editDegreeForm.description || null,
      };
      await api.put(`/admin/degrees/${editingDegreeId}`, body, { params });
      setEditingDegreeId(null);
      await fetchData();
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message || 'Failed to update degree');
    } finally {
      setDegreeEditSubmitting(false);
    }
  };

  const deleteDegree = async (degreeId) => {
    if (!window.confirm('Delete this degree? Subjects using it will keep the degree name as text.')) return;
    setError('');
    setDeletingDegreeId(degreeId);
    try {
      const params = isSuperAdmin && instituteId ? { instituteId } : {};
      await api.delete(`/admin/degrees/${degreeId}`, { params });
      if (editingDegreeId === degreeId) setEditingDegreeId(null);
      await fetchData();
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message || 'Failed to delete degree');
    } finally {
      setDeletingDegreeId(null);
    }
  };

  const submitAddDepartment = async (e) => {
    e.preventDefault();
    setError('');
    const name = (departmentForm.name || '').trim();
    if (!name) {
      setError('Department name is required');
      return;
    }
    setDepartmentSubmitting(true);
    try {
      const params = isSuperAdmin && instituteId ? { instituteId } : {};
      const body = {
        name,
        code: (departmentForm.code || '').trim() || undefined,
        establishmentYear: departmentForm.establishmentYear ? Number(departmentForm.establishmentYear) : undefined,
        hodFacultyId: departmentForm.hodFacultyId || undefined,
        hodFacultyCode: departmentForm.hodFacultyCode || undefined,
        programsOffered: departmentForm.programsOfferedText.split('\n').map((x) => x.trim()).filter(Boolean),
        programIds: departmentForm.programIds,
        description: departmentForm.description || undefined,
      };
      await api.post('/admin/departments', body, { params });
      setShowAddDepartment(false);
      setDepartmentForm(emptyDepartmentForm);
      await fetchData();
    } catch (err) {
      const msg = err.response?.data?.error?.message || err.response?.data?.error?.details?.[0]?.message || err.message || 'Failed to add department';
      setError(msg);
    } finally {
      setDepartmentSubmitting(false);
    }
  };

  const startEditDepartment = (d) => {
    setEditingDepartmentId(d.id);
    setEditDepartmentForm({
      name: d.name || '',
      code: d.code || '',
      establishmentYear: d.establishment_year || '',
      hodFacultyId: d.hod_faculty_id || '',
      hodFacultyCode: d.hod_faculty_code || '',
      programsOfferedText: Array.isArray(d.programs_offered) ? d.programs_offered.join('\n') : '',
      programIds: Array.isArray(d.linked_programs) ? d.linked_programs.map((p) => p.id).filter(Boolean) : [],
      description: d.description || '',
    });
  };

  const submitEditDepartment = async (e) => {
    e.preventDefault();
    setError('');
    setDepartmentEditSubmitting(true);
    try {
      const params = isSuperAdmin && instituteId ? { instituteId } : {};
      const body = {
        name: editDepartmentForm.name.trim(),
        code: editDepartmentForm.code.trim() || undefined,
        establishmentYear: editDepartmentForm.establishmentYear ? Number(editDepartmentForm.establishmentYear) : null,
        hodFacultyId: editDepartmentForm.hodFacultyId || null,
        hodFacultyCode: editDepartmentForm.hodFacultyCode || null,
        programsOffered: editDepartmentForm.programsOfferedText.split('\n').map((x) => x.trim()).filter(Boolean),
        programIds: editDepartmentForm.programIds,
        description: editDepartmentForm.description || null,
      };
      await api.put(`/admin/departments/${editingDepartmentId}`, body, { params });
      setEditingDepartmentId(null);
      await fetchData();
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message || 'Failed to update department');
    } finally {
      setDepartmentEditSubmitting(false);
    }
  };

  const deleteDepartment = async (departmentId) => {
    if (!window.confirm('Delete this department? Subjects/faculty/students using it will keep the department name as text.')) return;
    setError('');
    setDeletingDepartmentId(departmentId);
    try {
      const params = isSuperAdmin && instituteId ? { instituteId } : {};
      await api.delete(`/admin/departments/${departmentId}`, { params });
      if (editingDepartmentId === departmentId) setEditingDepartmentId(null);
      await fetchData();
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message || 'Failed to delete department');
    } finally {
      setDeletingDepartmentId(null);
    }
  };

  const submitAddDivision = async (e) => {
    e.preventDefault();
    setError('');
    const name = (divisionForm.name || '').trim();
    if (!name) {
      setError('Division name is required');
      return;
    }
    if (!divisionForm.degreeId) {
      setError('Course (degree) is required');
      return;
    }
    setDivisionSubmitting(true);
    try {
      const params = isSuperAdmin && instituteId ? { instituteId } : {};
      const body = {
        degreeId: divisionForm.degreeId,
        academicYear: divisionForm.academicYear,
        semester: Number(divisionForm.semester),
        name,
        code: (divisionForm.code || '').trim() || undefined,
      };
      await api.post('/admin/divisions', body, { params });
      setShowAddDivision(false);
      setDivisionForm({
        degreeId: degrees[0]?.id ?? '',
        academicYear: ACADEMIC_YEARS[0],
        semester: 1,
        name: '',
        code: '',
      });
      const divParams = { ...params };
      if (divisionFilters.degreeId) divParams.degreeId = divisionFilters.degreeId;
      if (divisionFilters.academicYear) divParams.academicYear = divisionFilters.academicYear;
      if (divisionFilters.semester !== '' && divisionFilters.semester != null) divParams.semester = divisionFilters.semester;
      const res = await api.get('/admin/divisions', { params: divParams });
      setDivisions(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (err) {
      const msg = err.response?.data?.error?.message || err.response?.data?.error?.details?.[0]?.message || err.message || 'Failed to add division';
      setError(msg);
    } finally {
      setDivisionSubmitting(false);
    }
  };

  const startEditDivision = (d) => {
    setEditingDivisionId(d.id);
    setEditDivisionForm({ name: d.name || '', code: d.code || '' });
  };

  const submitEditDivision = async (e) => {
    e.preventDefault();
    setError('');
    setDivisionEditSubmitting(true);
    try {
      const params = isSuperAdmin && instituteId ? { instituteId } : {};
      const body = { name: editDivisionForm.name.trim(), code: editDivisionForm.code.trim() || undefined };
      await api.put(`/admin/divisions/${editingDivisionId}`, body, { params });
      setEditingDivisionId(null);
      await fetchData();
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message || 'Failed to update division');
    } finally {
      setDivisionEditSubmitting(false);
    }
  };

  const deleteDivision = async (divisionId) => {
    if (!window.confirm('Delete this division? Assignments using it will keep the division/section as text.')) return;
    setError('');
    setDeletingDivisionId(divisionId);
    try {
      const params = isSuperAdmin && instituteId ? { instituteId } : {};
      await api.delete(`/admin/divisions/${divisionId}`, { params });
      if (editingDivisionId === divisionId) setEditingDivisionId(null);
      if (selectedDivisionForStudents === divisionId) setSelectedDivisionForStudents('');
      await fetchData();
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message || 'Failed to delete division');
    } finally {
      setDeletingDivisionId(null);
    }
  };

  const studentListForDivision = (users || []).filter((u) => u.role === 'STUDENT' && u.details?.id).map((u) => ({
    id: u.details.id,
    name: [u.details.first_name, u.details.last_name].filter(Boolean).join(' ') || u.email,
    roll_number: u.details.roll_number,
  }));

  const addStudentsToDivision = async (e) => {
    e.preventDefault();
    if (!selectedDivisionForStudents || addStudentsToDivisionIds.length === 0) return;
    setError('');
    setAddStudentsToDivisionSubmitting(true);
    try {
      const params = isSuperAdmin && instituteId ? { instituteId } : {};
      await api.post(`/admin/divisions/${selectedDivisionForStudents}/students`, { studentIds: addStudentsToDivisionIds }, { params });
      setAddStudentsToDivisionIds([]);
      const res = await api.get(`/admin/divisions/${selectedDivisionForStudents}/students`, { params });
      setDivisionStudents(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message || 'Failed to add students');
    } finally {
      setAddStudentsToDivisionSubmitting(false);
    }
  };

  const removeStudentFromDivision = async (studentId) => {
    if (!selectedDivisionForStudents) return;
    setError('');
    setRemovingStudentFromDivisionId(studentId);
    try {
      const params = isSuperAdmin && instituteId ? { instituteId } : {};
      await api.delete(`/admin/divisions/${selectedDivisionForStudents}/students/${studentId}`, { params });
      setDivisionStudents((prev) => prev.filter((s) => s.student_id !== studentId));
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message || 'Failed to remove student');
    } finally {
      setRemovingStudentFromDivisionId(null);
    }
  };

  const startEditStudent = (s) => {
    setEditingStudentId(s.id);
    setEditStudentForm({
      rollNumber: s.roll_number || '',
      firstName: s.first_name || '',
      lastName: s.last_name || '',
      department: s.department || 'CSE',
      yearOfStudy: s.year_of_study ?? 2,
      section: s.section || '',
      batch: s.batch || '',
      contactNumber: s.contact_number || '',
    });
  };

  const submitEditStudent = async (e) => {
    e.preventDefault();
    if (!editingStudentId) return;
    setError('');
    setEditStudentSubmitting(true);
    try {
      const params = isSuperAdmin && instituteId ? { instituteId } : {};
      const body = {
        rollNumber: editStudentForm.rollNumber.trim(),
        firstName: editStudentForm.firstName.trim(),
        lastName: editStudentForm.lastName.trim(),
        department: editStudentForm.department,
        yearOfStudy: Number(editStudentForm.yearOfStudy),
        section: editStudentForm.section.trim() || undefined,
        batch: editStudentForm.batch.trim() || undefined,
        contactNumber: editStudentForm.contactNumber.trim() || undefined,
      };
      await api.put(`/admin/students/${editingStudentId}`, body, { params });
      setEditingStudentId(null);
      if (studentRosterShown && studentAppliedFilters) fetchStudents(studentAppliedFilters);
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message || 'Failed to update student');
    } finally {
      setEditStudentSubmitting(false);
    }
  };

  const deleteStudent = async (studentId) => {
    if (!window.confirm('Delete this student? This will remove their user account and cannot be undone.')) return;
    setError('');
    setDeletingStudentId(studentId);
    try {
      const params = isSuperAdmin && instituteId ? { instituteId } : {};
      await api.delete(`/admin/students/${studentId}`, { params });
      if (editingStudentId === studentId) setEditingStudentId(null);
      if (studentRosterShown && studentAppliedFilters) fetchStudents(studentAppliedFilters);
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message || 'Failed to delete student');
    } finally {
      setDeletingStudentId(null);
    }
  };

  const downloadStudentsTemplate = async () => {
    setError('');
    try {
      const params = isSuperAdmin && instituteId ? { instituteId } : {};
      const res = await api.get('/admin/students/template', { responseType: 'blob', params });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.setAttribute('download', 'students_upload_template.xlsx');
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message || 'Failed to download template');
    }
  };

  const resetUserPassword = async (userId) => {
    if (!window.confirm('Generate a new auto-generated password for this user? Share it with them â€” it will not be shown again.')) return;
    setError('');
    setResettingPasswordUserId(userId);
    setResetPasswordResult(null);
    try {
      const params = isSuperAdmin && instituteId ? { instituteId } : {};
      const { data } = await api.post(`/admin/users/${userId}/set-password`, {}, { params });
      setResetPasswordResult(data?.data ?? { email: '', newPassword: '' });
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message || 'Reset failed');
    } finally {
      setResettingPasswordUserId(null);
    }
  };

  const formatStudentUploadError = (err) => {
    const error = err.response?.data?.error;
    if (!error) return err.message || 'Upload failed';
    const parts = [error.message].filter(Boolean);
    if (Array.isArray(error.details) && error.details.length > 0) {
      parts.push(...error.details.map((d) => d.message || d.field).filter(Boolean));
    }
    return parts.join(' — ') || 'Upload failed';
  };

  const submitUploadStudents = async (e) => {
    e.preventDefault();
    if (!studentUploadFile) {
      setStudentUploadError('Please select an Excel (.xlsx) or CSV file.');
      return;
    }
    setError('');
    setStudentUploadError('');
    setStudentUploadResult(null);
    setStudentUploadSubmitting(true);
    try {
      const params = isSuperAdmin && instituteId ? { instituteId } : {};
      const formData = new FormData();
      formData.append('file', studentUploadFile);
      const res = await api.post('/admin/students/upload', formData, {
        params,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setStudentUploadResult(res.data?.data ?? null);
      setStudentUploadFile(null);
      ['student-master-upload-input', 'student-upload-input'].forEach((id) => {
        const el = document.getElementById(id);
        if (el) el.value = '';
      });
      try {
        const userParams = isSuperAdmin && instituteId ? { instituteId, limit: 500 } : { limit: 500 };
        userParams._t = Date.now();
        const usersRes = await api.get('/admin/users', { params: userParams });
        const nextUsers = getUsersFromResponse(usersRes);
        setUsers(Array.isArray(nextUsers) ? nextUsers : []);
      } catch (_) {}
      if (studentRosterShown && studentAppliedFilters) {
        await fetchStudents(studentAppliedFilters, { page: 1 });
      }
    } catch (err) {
      setStudentUploadError(formatStudentUploadError(err));
    } finally {
      setStudentUploadSubmitting(false);
    }
  };

  const deleteSubject = async (subjectId) => {
    if (!window.confirm('Delete this subject? This cannot be undone.')) return;
    setError('');
    setDeletingSubjectId(subjectId);
    try {
      const params = isSuperAdmin && instituteId ? { instituteId } : {};
      await api.delete(`/admin/subjects/${subjectId}`, { params });
      if (editingSubjectId === subjectId) setEditingSubjectId(null);
      await fetchData();
      refreshSubjectRosterIfShown();
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message || 'Failed to delete subject');
    } finally {
      setDeletingSubjectId(null);
    }
  };

  const submitAddFaculty = async (e) => {
    e.preventDefault();
    setError('');
    setFacultySubmitting(true);
    try {
      const body = {
        employeeId: facultyForm.employeeId.trim(),
        firstName: facultyForm.firstName.trim(),
        lastName: facultyForm.lastName.trim(),
        department: facultyForm.department,
        designation: facultyForm.designation.trim() || undefined,
        qualification: facultyForm.qualification.trim() || undefined,
        specialization: facultyForm.specialization.trim() || undefined,
        contactNumber: facultyForm.contactNumber.trim() || undefined,
      };
      if (isSuperAdmin && instituteId) body.instituteId = instituteId;
      const { data } = await api.post('/admin/users/faculty', body);
      const created = data?.data;
      // Show new faculty in the list immediately (optimistic from create response)
      if (created?.user && created?.faculty) {
        const newUserRow = {
          id: created.user.id,
          email: created.user.email || created.credentials?.userId,
          role: 'FACULTY',
          is_active: true,
          details: {
            id: created.faculty.id,
            employee_id: created.faculty.employee_id,
            first_name: created.faculty.first_name,
            last_name: created.faculty.last_name,
            department: created.faculty.department,
            designation: created.faculty.designation,
          },
        };
        setUsers((prev) => [newUserRow, ...prev]);
      }
      if (created?.credentials) {
        setGeneratedCredentials(created.credentials);
        setShowAddFaculty(false);
        setFacultyForm({
          employeeId: '',
          firstName: '',
          lastName: '',
          department: departmentOptions[0] || 'CSE',
          designation: '',
          qualification: '',
          specialization: '',
          contactNumber: '',
        });
      }
      // Refetch users so the new faculty appears in the list (cache-bust so we get fresh data)
      const newUserRow = created?.user && created?.faculty ? {
        id: created.user.id,
        email: created.user.email || created.credentials?.userId,
        role: 'FACULTY',
        is_active: true,
        details: {
          id: created.faculty.id,
          employee_id: created.faculty.employee_id,
          first_name: created.faculty.first_name,
          last_name: created.faculty.last_name,
          department: created.faculty.department,
          designation: created.faculty.designation,
        },
      } : null;
      const newId = created?.user?.id;
      try {
        const userParams = isSuperAdmin && instituteId ? { instituteId, limit: 500 } : { limit: 500 };
        userParams._t = Date.now(); // avoid cached response
        const usersRes = await api.get('/admin/users', { params: userParams });
        let nextUsers = getUsersFromResponse(usersRes);
        nextUsers = Array.isArray(nextUsers) ? nextUsers : [];
        // Only use refetch result if we got a real list; otherwise keep optimistic list
        if (nextUsers.length > 0) {
          // If refetch didn't include the new user (e.g. cache), prepend so it always shows
          if (newId && newUserRow && !nextUsers.some((u) => String(u.id) === String(newId))) {
            nextUsers = [newUserRow, ...nextUsers];
          }
          setUsers(nextUsers);
        }
        // else: refetch returned empty; keep optimistic update (already done above)
      } catch (_) {
        // Keep optimistic list if refetch fails
      }
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message || 'Failed to add faculty');
    } finally {
      setFacultySubmitting(false);
    }
  };

  const editingSubject = subjects.find((s) => s.id === editingSubjectId);

  return (
    <Layout>
      <div className="admin-page space-y-6">
        <header className="admin-header">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h1 className="admin-title">Admin Dashboard</h1>
              <p className="admin-subtitle">
                {user?.email}
                {isSuperAdmin && <span className="admin-badge ml-2">Super Admin</span>}
                {!isSuperAdmin && user?.institute?.name && <span className="ml-2 text-slate-500"> Â· {user.institute.name}</span>}
              </p>
            </div>
            {isSuperAdmin && institutes.length > 0 && (
              <div className="flex items-center gap-2 shrink-0">
                <label htmlFor="admin-institute-select" className="text-sm font-medium whitespace-nowrap" style={{ color: 'var(--text-secondary)' }}>Institute</label>
                <select
                  id="admin-institute-select"
                  value={selectedInstituteId ?? ''}
                  onChange={(e) => setSelectedInstituteId(e.target.value ? Number(e.target.value) : null)}
                  className="input min-w-[220px] max-w-[320px] text-sm"
                >
                  <option value="">Select an instituteâ€¦</option>
                  {institutes.map((inst) => (
                    <option key={inst.id} value={inst.id}>{inst.name || `Institute ${inst.id}`}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </header>

        {error && (
          <div className="alert-warning rounded-xl" role="alert">
            {error}
          </div>
        )}

        {loading ? (
          <div className="admin-loading">
            <span className="inline-block w-8 h-8 border-2 border-slate-300 border-t-indigo-500 rounded-full animate-spin" />
            <span className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Loading dashboardâ€¦</span>
          </div>
        ) : isSuperAdmin && !instituteId ? (
          <div className="admin-empty">
            <p className="admin-empty-title">Select an institute</p>
            <p className="admin-empty-text">Choose an institute from the list above to view and manage its data.</p>
          </div>
        ) : (
          <div className="admin-shell text-left">
            <aside className="admin-nav-wrap">
              <button type="button" className={`admin-nav-brand ${activeMenu === 'overview' ? 'active' : ''}`} onClick={() => setActiveMenu('overview')}>
                <span className="admin-nav-brand-mark">360</span>
                <div>
                  <p className="admin-nav-brand-title">Admin Console</p>
                  <p className="admin-nav-brand-subtitle">Dashboard overview</p>
                </div>
              </button>
              <nav className="admin-nav" aria-label="Admin sections">
                {adminMenuGroups.map((group, index) => (
                  <Fragment key={group.label}>
                    {index === 0 && (
                      <div className="admin-nav-section-heading">Feedback System Configuration</div>
                    )}
                  <div className="admin-nav-group">
                    <button
                      type="button"
                      className={`admin-nav-group-button ${expandedAdminGroups[group.label] ? 'expanded' : ''}`}
                      onClick={() => setExpandedAdminGroups((prev) => ({ ...prev, [group.label]: !prev[group.label] }))}
                    >
                      <span>{group.label}</span>
                      <span className="admin-nav-chevron" aria-hidden="true">
                        <ChevronDownIcon className="h-4 w-4" />
                      </span>
                    </button>
                    {expandedAdminGroups[group.label] && (
                      <div className="admin-nav-subitems">
                        {group.items.map((item) => {
                          const MenuIcon = item.icon;
                          return (
                            <button
                              key={item.id}
                              type="button"
                              onClick={() => setActiveMenu(item.id)}
                              className={`admin-nav-tab ${activeMenu === item.id ? 'active' : ''}`}
                            >
                              <span className="admin-nav-thumb" aria-hidden="true">
                                <MenuIcon className="h-4 w-4" />
                              </span>
                              <span className="admin-nav-label">{item.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  </Fragment>
                ))}
              </nav>
            </aside>
            <div className="admin-content">

            {(() => {
              switch (activeMenu) {
                case 'overview':
                  return (
            <div className="space-y-6">
              <div className="admin-card">
                <div className="admin-card-body">
                  <div className="flex flex-col xl:flex-row xl:items-end xl:justify-between gap-4">
                    <div>
                      <h2 className="admin-section-title">Overview</h2>
                      <p className="admin-section-subtitle mb-0">Academic year and semester wise snapshot of active feedback work.</p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 min-w-full xl:min-w-[42rem]">
                      <div>
                        <label className="input-label">Academic year</label>
                        <select value={overviewAcademicYear} onChange={(e) => setOverviewAcademicYear(e.target.value)} className="input text-base text-slate-900 text-left">
                          {availableOverviewYears.map((year) => <option key={year} value={year}>{year}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="input-label">Semester</label>
                        <select value={overviewSemester} onChange={(e) => setOverviewSemester(e.target.value)} className="input text-base text-slate-900 text-left">
                          <option value="">All semesters</option>
                          {(availableOverviewSemesters.length > 0 ? availableOverviewSemesters : Array.from({ length: 8 }, (_, i) => i + 1)).map((sem) => <option key={sem} value={sem}>Semester {sem}</option>)}
                        </select>
                      </div>
                      <div className="flex items-end">
                        <button
                          type="button"
                          onClick={fetchActiveFormsList}
                          className="btn-primary w-full"
                          disabled={activeFormsLoading}
                        >
                          {activeFormsLoading ? 'Loading...' : 'Active Feedback Forms'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                <div className="stat-card">
                  <p className="stat-card-label">Active feedback forms</p>
                  <p className="stat-card-value">{activeOverviewCycles.length}</p>
                  <p className="stat-card-meta">Currently open in selected year/semester</p>
                </div>
                <div className="stat-card">
                  <p className="stat-card-label">Stakeholders</p>
                  <p className="stat-card-value">{totalUsers}</p>
                  <p className="stat-card-meta">{totalStudents} students, {overviewStats.total_faculty || 0} faculty</p>
                </div>
                <div className="stat-card">
                  <p className="stat-card-label">Feedback received</p>
                  <p className="stat-card-value">{totalFeedback}</p>
                  <p className="stat-card-meta">{participationPercentage}% participation estimate</p>
                </div>
                <div className="stat-card">
                  <p className="stat-card-label">Average rating</p>
                  <p className="stat-card-value">{overviewStats.avg_rating || '0.00'}</p>
                  <p className="stat-card-meta">Overall response quality indicator</p>
                </div>
              </div>

              {overviewNotice && <div className="alert-success">{overviewNotice}</div>}

              {showActiveFormsPanel && (
                <div className="fixed inset-0 z-50 bg-slate-950/40 px-4 py-6 overflow-y-auto">
                  <div className="mx-auto max-w-6xl rounded-xl bg-white shadow-2xl">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b border-slate-200 px-5 py-4">
                      <div>
                        <h3 className="admin-section-title">Active Feedback Forms</h3>
                        <p className="admin-section-subtitle mb-0">
                          {overviewAcademicYear} {overviewSemester ? `| Semester ${overviewSemester}` : '| All semesters'} reminder control.
                        </p>
                      </div>
                      <button type="button" onClick={() => setShowActiveFormsPanel(false)} className="btn-secondary">
                        Close
                      </button>
                    </div>
                    <div className="max-h-[75vh] overflow-y-auto p-5">
                      {activeFormsLoading ? (
                        <p className="text-sm text-slate-500">Loading active forms...</p>
                      ) : activeFormsList.length === 0 ? (
                        <p className="text-sm text-slate-500">No active feedback forms found for this selection.</p>
                      ) : (
                        <div className="grid gap-4 lg:grid-cols-2">
                          {activeFormsList.map((form) => {
                            const key = `${form.id}-${form.questionnaire_id || 'cycle'}`;
                            const expected = Number(form.expected_responses || 0);
                            const received = Number(form.responses || 0);
                            const percent = expected > 0 ? Math.min(100, Math.round((received / expected) * 100)) : 0;
                            const history = Array.isArray(form.reminderHistory) ? form.reminderHistory : [];
                            return (
                              <div key={key} className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                                  <div>
                                    <p className="text-sm font-semibold text-slate-900">{form.questionnaire_title || form.name}</p>
                                    <p className="text-xs text-slate-500">{form.stakeholder || form.type?.replace(/_/g, ' ')} | Semester {form.semester} | {form.target_type || 'GENERAL'}</p>
                                    {form.stage_name && <p className="mt-1 text-xs text-slate-500">{form.stage_name}</p>}
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => sendActiveFormReminder(form)}
                                    className="btn-primary text-sm"
                                    disabled={reminderSendingKey === key}
                                  >
                                    {reminderSendingKey === key ? 'Sending...' : 'Send Reminder'}
                                  </button>
                                </div>
                                <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                                  <div><span className="text-slate-500">Received</span><p className="font-medium text-slate-800">{received}</p></div>
                                  <div><span className="text-slate-500">Expected</span><p className="font-medium text-slate-800">{expected}</p></div>
                                  <div><span className="text-slate-500">Progress</span><p className="font-medium text-slate-800">{percent}%</p></div>
                                  <div><span className="text-slate-500">Average</span><p className="font-medium text-slate-800">{form.avg_rating || '-'}</p></div>
                                </div>
                                <div className="mt-3 h-2 rounded-full bg-slate-200 overflow-hidden">
                                  <div className="h-full bg-indigo-600" style={{ width: `${percent}%` }} />
                                </div>
                                <div className="mt-4">
                                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Reminder History</p>
                                  {history.length === 0 ? (
                                    <p className="mt-2 text-sm text-slate-500">No reminders sent yet.</p>
                                  ) : (
                                    <div className="mt-2 space-y-2">
                                      {history.map((item) => (
                                        <div key={item.id} className="rounded-md border border-slate-200 bg-white px-3 py-2">
                                          <p className="text-sm text-slate-800">{item.sent_at ? new Date(item.sent_at).toLocaleString() : '-'}</p>
                                          <p className="text-xs text-slate-500">Sent by {item.sent_by_email || 'admin'} to {item.recipient_count || 0} {item.stakeholder || 'stakeholder'} recipient(s)</p>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="grid gap-6 xl:grid-cols-3">
                <div className="admin-card xl:col-span-2">
                  <div className="admin-card-body">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                      <div>
                        <h3 className="admin-section-title">Current Active Feedback Forms</h3>
                        <p className="admin-section-subtitle">Live forms, time windows and response progress.</p>
                      </div>
                      <button type="button" onClick={fetchActiveFormsList} className="btn-secondary text-sm">
                        View Forms
                      </button>
                    </div>
                    {activeOverviewCycles.length === 0 ? (
                      <p className="text-sm text-slate-500">No active feedback forms for the selected academic year and semester.</p>
                    ) : (
                      <div className="grid gap-4 md:grid-cols-2">
                        {activeOverviewCycles.map((cycle) => {
                          const expected = Number(cycle.expected_responses || 0);
                          const received = Number(cycle.responses || 0);
                          const percent = expected > 0 ? Math.min(100, Math.round((received / expected) * 100)) : participationPercentage;
                          return (
                          <div key={`${cycle.id}-${cycle.questionnaire_title || cycle.type}`} className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <p className="text-sm font-semibold text-slate-800">{cycle.questionnaire_title || cycle.name}</p>
                                <p className="text-xs text-slate-500">{cycle.stakeholder || cycle.type?.replace(/_/g, ' ')} | Semester {cycle.semester} | {cycle.target_type || 'GENERAL'}</p>
                              </div>
                              <span className="admin-badge">Active</span>
                            </div>
                            {cycle.stage_name && <p className="mt-2 text-xs text-slate-500">{cycle.stage_name}</p>}
                            <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                              <div><span className="text-slate-500">Start</span><p className="font-medium text-slate-800">{cycle.start_date ? new Date(cycle.start_date).toLocaleDateString() : '-'}</p></div>
                              <div><span className="text-slate-500">End</span><p className="font-medium text-slate-800">{cycle.end_date ? new Date(cycle.end_date).toLocaleDateString() : '-'}</p></div>
                              <div><span className="text-slate-500">Received</span><p className="font-medium text-slate-800">{received}</p></div>
                              <div><span className="text-slate-500">Average</span><p className="font-medium text-slate-800">{cycle.avg_rating || '-'}</p></div>
                            </div>
                            <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
                              <span>{received} of {expected} expected</span>
                              <span>{percent}%</span>
                            </div>
                            <div className="mt-2 h-2 rounded-full bg-slate-200 overflow-hidden">
                              <div className="h-full bg-indigo-600" style={{ width: `${percent}%` }} />
                            </div>
                          </div>
                        );
                        })}
                      </div>
                    )}
                  </div>
                </div>

                <div className="admin-card">
                  <div className="admin-card-body">
                    <h3 className="admin-section-title">Stakeholders</h3>
                    <p className="admin-section-subtitle">Users available for feedback administration.</p>
                    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-1">
                      {stakeholderStats.map((item) => (
                        <div key={item.label} className="rounded-lg border border-slate-200 p-3 flex items-center justify-between">
                          <span className="text-sm text-slate-600">{item.label}</span>
                          <span className="text-lg font-semibold text-slate-900">{item.total}</span>
                        </div>
                      ))}
                    </div>
                    <h3 className="admin-section-title mt-6">Semester Links</h3>
                    <p className="admin-section-subtitle">Program, department and subject coverage for the selected semester.</p>
                    {semesterSubjects.length === 0 ? (
                      <p className="text-sm text-slate-500">No subject links found for this selection.</p>
                    ) : (
                      <div className="space-y-3 max-h-80 overflow-y-auto pr-1">
                        {semesterSubjects.map((row) => (
                          <div key={`${row.degree}-${row.department}-${row.semester}`} className="rounded-lg border border-slate-200 p-3">
                            <p className="text-sm font-medium text-slate-800">{row.degree} | Semester {row.semester}</p>
                            <p className="text-xs text-slate-500">{row.department} | {row.subject_count} subjects</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="admin-card xl:col-span-3">
                  <div className="admin-card-body">
                    <h3 className="admin-section-title">Next Feedback Cycles</h3>
                    <p className="admin-section-subtitle">Upcoming feedback windows for planning.</p>
                    {upcomingOverviewCycles.length === 0 ? (
                      <p className="text-sm text-slate-500">No upcoming cycles scheduled for this filter.</p>
                    ) : (
                      <div className="space-y-3">
                        {upcomingOverviewCycles.map((cycle) => (
                          <div key={cycle.id} className="rounded-lg border border-slate-200 p-3">
                            <p className="text-sm font-medium text-slate-800">{cycle.name}</p>
                            <p className="text-xs text-slate-500">{cycle.type?.replace(/_/g, ' ')} | {cycle.start_date ? new Date(cycle.start_date).toLocaleDateString() : '-'} to {cycle.end_date ? new Date(cycle.end_date).toLocaleDateString() : '-'}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid gap-6 xl:grid-cols-2">
                <div className="admin-card">
                  <div className="admin-card-body">
                    <h3 className="admin-section-title">Recent Feedback Activity</h3>
                    <p className="admin-section-subtitle">Response count over recent submission dates.</p>
                    {Array.isArray(overview?.recentActivity) && overview.recentActivity.length > 0 ? (
                      <div className="table-wrap">
                        <table className="table">
                          <thead><tr><th>Date</th><th>Responses</th></tr></thead>
                          <tbody>
                            {overview.recentActivity.slice(0, 8).map((row, i) => (
                              <tr key={row.date || i}>
                                <td>{row.date ? new Date(row.date).toLocaleDateString() : '-'}</td>
                                <td className="font-medium">{row.count ?? '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-sm text-slate-500">No recent responses yet.</p>
                    )}
                  </div>
                </div>

                <div className="admin-card">
                  <div className="admin-card-body">
                    <h3 className="admin-section-title">Report Readiness</h3>
                    <p className="admin-section-subtitle">Open current year/semester and last five academic year analysis from Reports & Analytics.</p>
                    <div className="grid gap-3 sm:grid-cols-2">
                      {ACADEMIC_YEARS.slice(0, 6).map((year, index) => (
                        <button
                          key={year}
                          type="button"
                          onClick={() => {
                            setPdfReportFilters((prev) => ({ ...prev, academicYear: year }));
                            setActiveMenu('reports');
                          }}
                          className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-left hover:bg-indigo-50 hover:border-indigo-200"
                        >
                          <span className="block text-sm font-semibold text-slate-800">{index === 0 ? 'Current: ' : ''}{year}</span>
                          <span className="block text-xs text-slate-500">Open analysis report</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            );
                case '__removed-overview':
                  return (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            <InitiateFeedbackCyclePanel
              cycleForm={cycleForm}
              setCycleForm={setCycleForm}
              availableSemesters={availableCycleSemesters}
              onSubmit={initiateFeedbackCycle}
              submitting={cycleSubmitting}
              error={error}
            />
            {overview && typeof overview === 'object' && (
              <div className="card card-body md:col-span-2 space-y-6">
                <h2 className="text-lg font-semibold text-slate-800 tracking-tight">
                  System Overview
                </h2>
                {overview.statistics && typeof overview.statistics === 'object' && (
                  <>
                    <div>
                      <h3 className="text-base font-medium text-slate-600 mb-3 text-left">Statistics</h3>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                        {Object.entries(overview.statistics).map(([key, val]) => (
                          <div
                            key={key}
                            className="p-4 rounded-xl bg-slate-50/80 border border-slate-200/60"
                          >
                            <span className="block text-left text-sm font-medium text-slate-500 uppercase tracking-wider">
                              {key.replace(/_/g, ' ')}
                            </span>
                            <span className="text-xl font-semibold text-slate-800 mt-0.5">
                              {val != null && typeof val === 'object' ? 'â€”' : (typeof val === 'number' ? (Number.isFinite(val) ? val : 'â€”') : String(val))}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                    {Array.isArray(overview.recentActivity) && overview.recentActivity.length > 0 && (
                      <div>
                        <h3 className="text-base font-medium text-slate-600 mb-3 text-left">Recent activity (last 30 days)</h3>
                        <div className="table-wrap">
                          <table className="table">
                            <thead>
                              <tr>
                                <th>Date</th>
                                <th>Feedback count</th>
                              </tr>
                            </thead>
                            <tbody>
                              {overview.recentActivity.map((row, i) => (
                                <tr key={row.date || i}>
                                  <td>{row.date ? new Date(row.date).toLocaleDateString() : 'â€”'}</td>
                                  <td className="font-medium">{row.count ?? 'â€”'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                    {Array.isArray(overview.recentActivity) && overview.recentActivity.length === 0 && (
                      <p className="text-slate-500 text-sm">No feedback submissions in the last 30 days.</p>
                    )}
                  </>
                )}
              </div>
            )}
            <AssignSubjectFacultyPanel
              activeCycle={activeCycle}
              assignError={assignError}
              assignSubjectFacultyForm={assignSubjectFacultyForm}
              setAssignSubjectFacultyForm={setAssignSubjectFacultyForm}
              subjects={subjects}
              facultyList={facultyList}
              divisions={divisions}
              submitAssignSubjectFaculty={submitAssignSubjectFaculty}
              assignSubmitting={assignSubmitting}
              activeSemesterAssignments={activeSemesterAssignments}
            />
            <div className="admin-card md:col-span-2">
              <div className="admin-card-body">
                <h2 className="admin-section-title mb-4">Feedback schedule masters</h2>
                <FeedbackCyclesTable
                  cycles={cycles}
                  cycleActionId={cycleActionId}
                  onActivate={activateCycle}
                  onDeactivate={deactivateCycle}
                />
              </div>
            </div>
            <div className="bg-white rounded-xl shadow p-6 border border-slate-200 md:col-span-2">
              <h2 className="text-lg font-semibold text-slate-800 mb-4">
                Semester change applications
              </h2>
              <p className="text-slate-600 text-sm mb-4">
                Students apply for change of semester and assign subjects. Approve to enroll them in the selected subjects for the target semester.
              </p>
              {semesterChangeApplications.length === 0 ? (
                <p className="text-slate-500">No semester change applications.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 text-left text-slate-600">
                        <th className="pb-2 pr-4">Student</th>
                        <th className="pb-2 pr-4">Target semester</th>
                        <th className="pb-2 pr-4">Subjects</th>
                        <th className="pb-2 pr-4">Status</th>
                        <th className="pb-2 pr-4">Date</th>
                        <th className="pb-2">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {semesterChangeApplications.map((app) => (
                        <tr key={app.id} className="border-b border-slate-100">
                          <td className="py-2 pr-4 text-slate-700">
                            {app.first_name} {app.last_name}
                            <span className="text-slate-500 text-xs block">{app.roll_number}</span>
                          </td>
                          <td className="py-2 pr-4 text-slate-600">
                            {app.to_academic_year} Â· Sem {app.to_semester}
                          </td>
                          <td className="py-2 pr-4 text-slate-600">
                            {(app.subject_ids || []).length} subject(s)
                          </td>
                          <td className="py-2 pr-4">
                            <span
                              className={
                                app.status === 'APPROVED'
                                  ? 'text-green-600 font-medium'
                                  : app.status === 'REJECTED'
                                  ? 'text-red-600'
                                  : 'text-amber-600'
                              }
                            >
                              {app.status}
                            </span>
                          </td>
                          <td className="py-2 pr-4 text-slate-500 text-xs">
                            {app.created_at ? new Date(app.created_at).toLocaleDateString() : 'â€”'}
                          </td>
                          <td className="py-2">
                            {app.status === 'PENDING' && (
                              <>
                                <button
                                  type="button"
                                  onClick={() => approveSemesterChangeApplication(app.id)}
                                  disabled={semesterChangeActionId === app.id}
                                  className="text-primary-600 hover:underline text-xs mr-2 disabled:opacity-50"
                                >
                                  Approve
                                </button>
                                <button
                                  type="button"
                                  onClick={() => rejectSemesterChangeApplication(app.id)}
                                  disabled={semesterChangeActionId === app.id}
                                  className="text-amber-600 hover:underline text-xs disabled:opacity-50"
                                >
                                  Reject
                                </button>
                              </>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            </div>
            );
                case 'cycles':
                  return (
            <div className="admin-wide-page">
              <div className="admin-master-header">
                <div>
                  <h1 className="admin-master-title">Feedback Cycle Schedule</h1>
                  <p className="admin-master-subtitle">
                    Control only the current semester feedback cycle. Previous and future semester schedules stay available for reports, but they are not shown here for activation.
                  </p>
                </div>
              </div>
              <div className="space-y-6">
                <InitiateFeedbackCyclePanel
                  cycleForm={cycleForm}
                  setCycleForm={setCycleForm}
                  availableSemesters={availableCycleSemesters}
                  onSubmit={initiateFeedbackCycle}
                  submitting={cycleSubmitting}
                  error={error}
                />
                <div className="admin-card">
                  <div className="admin-card-body">
                    <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                      <div>
                        <h2 className="admin-section-title m-0">Current semester schedule</h2>
                        <p className="text-sm text-slate-500 mt-1">
                          {currentTerm
                            ? `AY ${currentTerm.academic_year} | Current semester group: ${currentSemesterNumbers.join(', ')}`
                            : 'No current academic term is active in the Academic Term Calendar.'}
                        </p>
                      </div>
                    </div>
                    <FeedbackCyclesTable
                      cycles={currentSemesterCycles}
                      cycleActionId={cycleActionId}
                      onActivate={activateCycle}
                      onDeactivate={deactivateCycle}
                    />
                  </div>
                </div>
              </div>
            </div>
            );
                case 'feedback-lifecycle':
                  return (
            <div className="space-y-6">
              <div className="card card-body space-y-5">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-semibold text-slate-800">Feedback Lifecycle</h2>
                    <p className="text-sm text-slate-500">Stage-wise feedback administration for students, parents, faculty, alumni, industry experts, and events.</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button type="button" onClick={downloadLifecycleTemplate} className="btn-secondary text-sm">Download Excel template</button>
                  </div>
                </div>
                {lifecycleMessage && (
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
                    {lifecycleMessage}
                  </div>
                )}
                {lifecycleLoading ? (
                  <p className="text-sm text-slate-500">Loading lifecycle...</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="border-b border-slate-200 text-left text-slate-600">
                          <th className="pb-2 pr-4">Step</th>
                          <th className="pb-2 pr-4">Stakeholder</th>
                          <th className="pb-2 pr-4">Scope</th>
                          <th className="pb-2 pr-4">Questionnaires</th>
                          <th className="pb-2">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {lifecycleStages.map((stage) => {
                          const stageQuestionnaires = questionnaires.filter((q) => q.stage_id === stage.id);
                          return (
                          <tr key={stage.id} className="border-b border-slate-100 align-top">
                            <td className="py-3 pr-4">
                              <p className="font-medium text-slate-800">{stage.sequence_no}. {stage.name}</p>
                              <p className="text-xs text-slate-500 max-w-xl">{stage.description}</p>
                            </td>
                            <td className="py-3 pr-4 text-slate-700">{stage.stakeholder?.replace(/_/g, ' ')}</td>
                            <td className="py-3 pr-4 text-slate-700">{stage.semester_scope?.replace(/_/g, ' ')}</td>
                            <td className="py-3 pr-4 text-slate-700">
                              {stageQuestionnaires.length === 0 ? (
                                <span className="text-slate-500">No questionnaire yet</span>
                              ) : (
                                <div className="space-y-2">
                                  {stageQuestionnaires.map((q) => (
                                    <div key={q.id} className="rounded-lg border border-slate-200 bg-slate-50 p-2">
                                      <p className="text-xs font-semibold text-slate-800">{q.title}</p>
                                      <p className="text-[0.7rem] text-slate-500">{q.question_count || 0} questions | {q.status}</p>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </td>
                            <td className="py-3">
                              <div className="flex flex-col gap-2">
                                {stageQuestionnaires.map((q) => (
                                  <div key={q.id} className="flex flex-wrap gap-2">
                                    <button type="button" onClick={() => openQuestionnairePage('preview', stage.id, q.id)} className="btn-secondary text-xs">Preview</button>
                                    <button type="button" onClick={() => openQuestionnairePage('edit', stage.id, q.id)} className="btn-primary text-xs">Change Questionnaire</button>
                                  </div>
                                ))}
                                {stageQuestionnaires.length === 0 && (
                                  <button type="button" onClick={() => openQuestionnairePage('edit', stage.id)} className="btn-primary text-xs">Change Questionnaire</button>
                                )}
                              </div>
                            </td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
            );
                case 'admission-feedback':
                  return (
            <div className="space-y-6">
              <div className="bg-white rounded-xl shadow p-6 border border-slate-200">
                <h2 className="text-lg font-semibold text-slate-800 mb-2">Initiate admission feedback</h2>
                <p className="text-slate-600 text-sm mb-4">
                  Create and activate an admission feedback cycle. Students can then fill MCQ-based admission feedback once per cycle. Add MCQ questions (form or Excel) after creating the cycle.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-2 mb-4">
                  <label className="input-label">Cycle name (optional)</label>
                  <label className="input-label">Academic year</label>
                  <label className="input-label">Start date</label>
                  <label className="input-label">End date</label>
                  <div>
                    <input
                      type="text"
                      value={admissionCycleForm.name}
                      onChange={(e) => setAdmissionCycleForm((f) => ({ ...f, name: e.target.value }))}
                      className="input text-base text-slate-900 text-left w-full"
                      placeholder="e.g. Admission 2024-2025"
                    />
                  </div>
                  <div>
                    <select
                      value={admissionCycleForm.academicYear}
                      onChange={(e) => setAdmissionCycleForm((f) => ({ ...f, academicYear: e.target.value }))}
                      className="input text-base text-slate-900 text-left w-full"
                    >
                      {ACADEMIC_YEARS.map((y) => (
                        <option key={y} value={y}>{y}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <input
                      type="date"
                      value={admissionCycleForm.startDate || ''}
                      onChange={(e) => setAdmissionCycleForm((f) => ({ ...f, startDate: e.target.value }))}
                      className="input text-base text-slate-900 text-left w-full"
                    />
                  </div>
                  <div>
                    <input
                      type="date"
                      value={admissionCycleForm.endDate || ''}
                      onChange={(e) => setAdmissionCycleForm((f) => ({ ...f, endDate: e.target.value }))}
                      className="input text-base text-slate-900 text-left w-full"
                    />
                  </div>
                </div>
                <button
                  type="button"
                  onClick={initiateAdmissionCycle}
                  disabled={admissionCycleSubmitting}
                  className="btn-primary px-4 py-2.5 text-base font-medium"
                >
                  {admissionCycleSubmitting ? 'Creatingâ€¦' : 'Create & activate admission cycle'}
                </button>
              </div>
              <div className="bg-white rounded-xl shadow p-6 border border-slate-200">
                <h2 className="text-lg font-semibold text-slate-800 mb-4">Admission cycles</h2>
                {admissionCycles.length === 0 ? (
                  <p className="text-slate-500">No admission cycles yet. Create one above.</p>
                ) : (
                  <div className="space-y-4">
                    <label className="text-sm text-slate-600">Select cycle to manage MCQ questions</label>
                    <select
                      value={selectedAdmissionCycleId}
                      onChange={(e) => setSelectedAdmissionCycleId(e.target.value)}
                      className="input text-base text-slate-900 text-left w-full max-w-md"
                    >
                      <option value="">â€” Select cycle â€”</option>
                      {admissionCycles.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name || c.academic_year} Â· Sem {c.semester}
                          {c.is_active ? ' (Active)' : ''}
                        </option>
                      ))}
                    </select>
                    {selectedAdmissionCycleId && (
                      <>
                        <h3 className="text-base font-medium text-slate-800 mt-4">Manage MCQ questions</h3>
                        {admissionQuestionsError && (
                          <div className="p-3 rounded-lg bg-red-50 text-red-700 border border-red-200 text-sm">
                            {admissionQuestionsError}
                          </div>
                        )}
                        <form onSubmit={addAdmissionQuestion} className="space-y-3 p-4 border border-slate-200 rounded-lg">
                          <div>
                            <label className="input-label">Question text</label>
                            <input
                              type="text"
                              value={admissionQuestionForm.questionText}
                              onChange={(e) => setAdmissionQuestionForm((f) => ({ ...f, questionText: e.target.value }))}
                              className="input text-base text-slate-900 text-left w-full"
                              placeholder="e.g. How was the admission process?"
                              maxLength={500}
                            />
                          </div>
                          <div>
                            <label className="input-label">Options (comma- or semicolon-separated)</label>
                            <input
                              type="text"
                              value={admissionQuestionForm.optionsText}
                              onChange={(e) => setAdmissionQuestionForm((f) => ({ ...f, optionsText: e.target.value }))}
                              className="input text-base text-slate-900 text-left w-full"
                              placeholder="e.g. Excellent, Good, Average, Poor"
                            />
                          </div>
                          <button
                            type="submit"
                            disabled={addQuestionSubmitting}
                            className="btn-primary px-4 py-2 text-sm font-medium"
                          >
                            {addQuestionSubmitting ? 'Addingâ€¦' : 'Add question'}
                          </button>
                        </form>
                        <div className="mt-4 p-4 border border-slate-200 rounded-lg">
                          <label className="input-label block mb-2">Or upload Excel (columns: Question, Option 1, Option 2, â€¦)</label>
                          <div className="flex flex-wrap items-center gap-3 mb-2">
                            <label className="text-sm text-slate-600">Download MCQ template:</label>
                            <select
                              value={selectedTemplateType}
                              onChange={(e) => setSelectedTemplateType(e.target.value)}
                              className="input text-sm text-slate-900 text-left w-auto min-w-[200px]"
                            >
                              {FEEDBACK_TEMPLATE_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                              ))}
                            </select>
                            <button
                              type="button"
                              className="text-primary-600 hover:underline font-medium text-left text-sm"
                              onClick={async () => {
                                try {
                                  const res = await api.get('/admin/feedback/questions/template', {
                                    params: { type: selectedTemplateType },
                                    responseType: 'blob',
                                  });
                                  const filename = TEMPLATE_FILENAMES[selectedTemplateType] || 'questions-template.xlsx';
                                  const url = window.URL.createObjectURL(new Blob([res.data]));
                                  const a = document.createElement('a');
                                  a.href = url;
                                  a.setAttribute('download', filename);
                                  document.body.appendChild(a);
                                  a.click();
                                  a.remove();
                                  window.URL.revokeObjectURL(url);
                                } catch (_) {}
                              }}
                            >
                              Download
                            </button>
                          </div>
                          <form onSubmit={uploadAdmissionExcel} className="flex flex-wrap items-end gap-3">
                            <input
                              type="file"
                              accept=".xlsx,.xls"
                              onChange={(e) => setAdmissionExcelFile(e.target.files?.[0] || null)}
                              className="text-sm text-slate-700"
                            />
                            <button
                              type="submit"
                              disabled={!admissionExcelFile || uploadExcelSubmitting}
                              className="btn-secondary px-4 py-2 text-sm font-medium disabled:opacity-50"
                            >
                              {uploadExcelSubmitting ? 'Uploadingâ€¦' : 'Upload Excel'}
                            </button>
                          </form>
                        </div>
                        <div className="mt-4">
                          <h4 className="text-sm font-medium text-slate-700 mb-2">Current questions ({admissionQuestions.length})</h4>
                          {admissionQuestionsLoading ? (
                            <p className="text-slate-500 text-sm">Loadingâ€¦</p>
                          ) : admissionQuestions.length === 0 ? (
                            <p className="text-slate-500 text-sm">No questions yet. Add via form or upload Excel.</p>
                          ) : (
                            <ul className="space-y-2">
                              {admissionQuestions.map((q, i) => (
                                <li key={q.id || i} className="text-sm text-slate-700 border-b border-slate-100 pb-2">
                                  <span className="font-medium">{q.display_order != null ? q.display_order + 1 : i + 1}.</span>{' '}
                                  {q.question_text || q.questionText}
                                  {Array.isArray(q.options) && q.options.length > 0 && (
                                    <span className="text-slate-500 ml-1"> â€” {q.options.join(', ')}</span>
                                  )}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
            );
                case 'parent-alumni-feedback':
                  return (
            <div className="space-y-6">
              <div className="bg-white rounded-xl shadow p-6 border border-slate-200">
                <h2 className="text-lg font-semibold text-slate-800 mb-2">Parent, alumni & industry expert cycles</h2>
                <p className="text-slate-600 text-sm mb-4">
                  Create and activate parent, alumni, or industry expert feedback cycles. Assigned tasks appear only on that stakeholder dashboard.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-x-4 gap-y-2 mb-4">
                  <label className="input-label">Type</label>
                  <label className="input-label">Cycle name (optional)</label>
                  <label className="input-label">Academic year</label>
                  <label className="input-label">Start / End date</label>
                  <label className="input-label">â€”</label>
                  <div>
                    <select
                      value={parentAlumniCycleForm.type}
                      onChange={(e) => setParentAlumniCycleForm((f) => ({ ...f, type: e.target.value }))}
                      className="input text-base text-slate-900 text-left w-full"
                    >
                      <option value="PARENT_FEEDBACK">Parent feedback</option>
                      <option value="ALUMNI_FEEDBACK">Alumni feedback</option>
                      <option value="INDUSTRY_EXPERT_FEEDBACK">Industry expert feedback</option>
                    </select>
                  </div>
                  {parentAlumniCycleForm.type === 'PARENT_FEEDBACK' ? (
                    <div className="sm:col-span-2">
                      <label className="input-label">Parent task type</label>
                      <select
                        value={parentAlumniCycleForm.parentTaskType}
                        onChange={(e) => setParentAlumniCycleForm((f) => ({ ...f, parentTaskType: e.target.value }))}
                        className="input text-base text-slate-900 text-left w-full"
                      >
                        <option value="ADMISSION">Admission feedback</option>
                        <option value="EVENT">Event feedback</option>
                        <option value="DEPARTMENT">Department / organization feedback</option>
                        <option value="PROGRAM">Overall program feedback</option>
                        <option value="SEMINAR">Seminar feedback</option>
                      </select>
                    </div>
                  ) : null}
                  {parentAlumniCycleForm.type === 'ALUMNI_FEEDBACK' ? (
                    <div className="sm:col-span-2">
                      <label className="input-label">Alumni task type</label>
                      <select
                        value={parentAlumniCycleForm.alumniTaskType}
                        onChange={(e) => setParentAlumniCycleForm((f) => ({ ...f, alumniTaskType: e.target.value }))}
                        className="input text-base text-slate-900 text-left w-full"
                      >
                        <option value="PROGRAM_STRUCTURE">Program structure feedback</option>
                        <option value="SUBJECT_SYLLABUS">Specific subject syllabus feedback</option>
                        <option value="INDUSTRY_EXPERT">Industry expert feedback</option>
                        <option value="SEMINAR">Seminar feedback</option>
                        <option value="EVENT">Event related feedback</option>
                      </select>
                    </div>
                  ) : null}
                  {parentAlumniCycleForm.type === 'INDUSTRY_EXPERT_FEEDBACK' ? (
                    <div className="sm:col-span-2">
                      <label className="input-label">Industry expert task type</label>
                      <select
                        value={parentAlumniCycleForm.expertTaskType}
                        onChange={(e) => setParentAlumniCycleForm((f) => ({ ...f, expertTaskType: e.target.value }))}
                        className="input text-base text-slate-900 text-left w-full"
                      >
                        <option value="PROGRAM_STRUCTURE">Program structure feedback</option>
                        <option value="SUBJECT_SYLLABUS">Specific subject syllabus feedback</option>
                        <option value="INDUSTRY_EXPERT">Industry expert feedback</option>
                        <option value="SEMINAR">Seminar feedback</option>
                        <option value="EVENT">Event related feedback</option>
                      </select>
                    </div>
                  ) : null}
                  <div>
                    <input
                      type="text"
                      value={parentAlumniCycleForm.name}
                      onChange={(e) => setParentAlumniCycleForm((f) => ({ ...f, name: e.target.value }))}
                      className="input text-base text-slate-900 text-left w-full"
                      placeholder="e.g. Parent Feedback 2024-2025"
                    />
                  </div>
                  <div>
                    <select
                      value={parentAlumniCycleForm.academicYear}
                      onChange={(e) => setParentAlumniCycleForm((f) => ({ ...f, academicYear: e.target.value }))}
                      className="input text-base text-slate-900 text-left w-full"
                    >
                      {ACADEMIC_YEARS.slice(0, 10).map((y) => (
                        <option key={y} value={y}>{y}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="date"
                      value={parentAlumniCycleForm.startDate || ''}
                      onChange={(e) => setParentAlumniCycleForm((f) => ({ ...f, startDate: e.target.value }))}
                      className="input text-base text-slate-900 text-left w-full"
                    />
                    <input
                      type="date"
                      value={parentAlumniCycleForm.endDate || ''}
                      onChange={(e) => setParentAlumniCycleForm((f) => ({ ...f, endDate: e.target.value }))}
                      className="input text-base text-slate-900 text-left w-full"
                    />
                  </div>
                  <div>
                    <select
                      value={parentAlumniCycleForm.semester}
                      onChange={(e) => setParentAlumniCycleForm((f) => ({ ...f, semester: Number(e.target.value) }))}
                      className="input text-base text-slate-900 text-left w-full"
                    >
                      {[1, 2, 3, 4, 5].map((s) => (
                        <option key={s} value={s}>Sem {s}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={initiateParentAlumniCycle}
                  disabled={parentAlumniCycleSubmitting}
                  className="btn-primary px-4 py-2.5 text-base font-medium"
                >
                  {parentAlumniCycleSubmitting ? 'Creating…' : `Create & activate ${
                    parentAlumniCycleForm.type === 'PARENT_FEEDBACK'
                      ? 'parent'
                      : parentAlumniCycleForm.type === 'INDUSTRY_EXPERT_FEEDBACK'
                        ? 'industry expert'
                        : 'alumni'
                  } cycle`}
                </button>
              </div>
              <div className="grid gap-6 sm:grid-cols-3">
                <div className="bg-white rounded-xl shadow p-6 border border-slate-200">
                  <h3 className="text-base font-semibold text-slate-800 mb-2">Parent feedback cycles</h3>
                  {parentCycles.length === 0 ? (
                    <p className="text-slate-500 text-sm">No parent cycles yet.</p>
                  ) : (
                    <ul className="space-y-1 text-sm text-slate-700">
                      {parentCycles.map((c) => (
                        <li key={c.id}>
                          {c.name || c.academic_year} · {c.is_active ? 'Active' : 'Inactive'}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="bg-white rounded-xl shadow p-6 border border-slate-200">
                  <h3 className="text-base font-semibold text-slate-800 mb-2">Alumni feedback cycles</h3>
                  {alumniCycles.length === 0 ? (
                    <p className="text-slate-500 text-sm">No alumni cycles yet.</p>
                  ) : (
                    <ul className="space-y-1 text-sm text-slate-700">
                      {alumniCycles.map((c) => (
                        <li key={c.id}>
                          {c.name || c.academic_year} · {c.is_active ? 'Active' : 'Inactive'}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="bg-white rounded-xl shadow p-6 border border-slate-200">
                  <h3 className="text-base font-semibold text-slate-800 mb-2">Industry expert cycles</h3>
                  {industryExpertCycles.length === 0 ? (
                    <p className="text-slate-500 text-sm">No industry expert cycles yet.</p>
                  ) : (
                    <ul className="space-y-1 text-sm text-slate-700">
                      {industryExpertCycles.map((c) => (
                        <li key={c.id}>
                          {c.name || c.academic_year} · {c.is_active ? 'Active' : 'Inactive'}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
            );
                case 'feedback-approval':
                  return (
            <>
            <div className="bg-white rounded-xl shadow p-6 border border-slate-200">
              <h2 className="text-lg font-semibold text-slate-800 mb-2">Feedback Approval</h2>
              <p className="text-slate-600 text-sm mb-4">
                Students submit feedback as PENDING. Approve submissions here so they become visible to faculty and appear in reports/PDFs.
              </p>
              <div className="flex flex-wrap gap-3 items-center mb-4">
                <label className="text-sm text-slate-600">Cycle</label>
                <select
                  value={pendingCycleId}
                  onChange={(e) => { setPendingCycleId(e.target.value); setPendingPage(1); }}
                  className="input select-single-line text-base text-slate-900 text-left min-w-[200px] max-w-[320px]"
                >
                  <option value="">All cycles</option>
                  {cycles.map((c) => (
                    <option key={c.id} value={c.id}>{c.name || academicYearLabel(c.academic_year)} | {academicYearLabel(c.academic_year)} | Sem {c.semester}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={handleApproveFeedback}
                  disabled={approveSubmitting || (pendingFeedback.items.length === 0 && !selectedPendingIds.length)}
                  className="btn-primary px-4 py-2.5 text-base font-medium"
                >
                  {approveSubmitting ? 'Approvingâ€¦' : selectedPendingIds.length ? `Approve ${selectedPendingIds.length} selected` : 'Approve all on page'}
                </button>
              </div>
              {pendingLoading ? (
                <p className="text-slate-500">Loading pending feedbackâ€¦</p>
              ) : pendingFeedback.items.length === 0 ? (
                <p className="text-slate-500">No pending feedback. Approved submissions are visible to faculty and included in PDF reports.</p>
              ) : (
                <>
                  <div className="overflow-x-auto rounded-lg border border-slate-200">
                    <table className="min-w-full text-sm">
                      <thead className="bg-slate-50 text-left text-slate-600">
                        <tr>
                          <th className="px-3 py-2 font-medium">
                            <input
                              type="checkbox"
                              checked={selectedPendingIds.length === pendingFeedback.items.length && pendingFeedback.items.length > 0}
                              onChange={(e) => setSelectedPendingIds(e.target.checked ? pendingFeedback.items.map((i) => i.id) : [])}
                              className="rounded border-slate-300"
                            />
                          </th>
                          <th className="px-3 py-2 font-medium">Submitted</th>
                          <th className="px-3 py-2 font-medium">Cycle</th>
                          <th className="px-3 py-2 font-medium">Subject</th>
                          <th className="px-3 py-2 font-medium">Faculty</th>
                          <th className="px-3 py-2 font-medium">Student</th>
                          <th className="px-3 py-2 font-medium">Rating</th>
                        </tr>
                      </thead>
                      <tbody className="text-slate-800">
                        {pendingFeedback.items.map((row) => (
                          <tr key={row.id} className="border-t border-slate-100">
                            <td className="px-3 py-2">
                              <input
                                type="checkbox"
                                checked={selectedPendingIds.includes(row.id)}
                                onChange={(e) => {
                                  if (e.target.checked) setSelectedPendingIds((prev) => [...prev, row.id]);
                                  else setSelectedPendingIds((prev) => prev.filter((id) => id !== row.id));
                                }}
                                className="rounded border-slate-300"
                              />
                            </td>
                            <td className="px-3 py-2">{row.submitted_at ? new Date(row.submitted_at).toLocaleString() : 'â€”'}</td>
                            <td className="px-3 py-2">{row.cycle_name || row.academic_year} Â· {row.semester}</td>
                            <td className="px-3 py-2">{row.subject_code} {row.subject_name}</td>
                          <td className="px-3 py-2">{row.program || '-'}</td>
                            <td className="px-3 py-2">{row.faculty_name}</td>
                            <td className="px-3 py-2">{row.student_roll} Â· {row.student_name}</td>
                            <td className="px-3 py-2">{row.overall_rating != null ? Number(row.overall_rating).toFixed(2) : 'â€”'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {pendingFeedback.totalPages > 1 && (
                    <div className="flex items-center gap-2 mt-3 text-sm text-slate-600">
                      <span>Page {pendingFeedback.page} of {pendingFeedback.totalPages} ({pendingFeedback.total} total)</span>
                      <button
                        type="button"
                        onClick={() => setPendingPage((p) => Math.max(1, p - 1))}
                        disabled={pendingFeedback.page <= 1}
                        className="px-2 py-1 border border-slate-300 rounded disabled:opacity-50"
                      >
                        Previous
                      </button>
                      <button
                        type="button"
                        onClick={() => setPendingPage((p) => Math.min(pendingFeedback.totalPages, p + 1))}
                        disabled={pendingFeedback.page >= pendingFeedback.totalPages}
                        className="px-2 py-1 border border-slate-300 rounded disabled:opacity-50"
                      >
                        Next
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="mt-6 bg-white rounded-xl shadow p-6 border border-slate-200">
              <h2 className="text-lg font-semibold text-slate-800 mb-2">Report: Students who submitted feedback</h2>
              <p className="text-slate-600 text-sm mb-4">
                List of all feedback submissions with student, subject, faculty, cycle, and status. Filter by cycle or status (PENDING/APPROVED).
              </p>
              <div className="flex flex-wrap gap-3 items-center mb-4">
                <label className="text-sm text-slate-600">Cycle</label>
                <select
                  value={submittedReportCycleId}
                  onChange={(e) => { setSubmittedReportCycleId(e.target.value); setSubmittedReportPage(1); }}
                  className="input text-base text-slate-900 text-left bg-white min-w-[200px]"
                >
                  <option value="">All cycles</option>
                  {cycles.map((c) => (
                    <option key={c.id} value={c.id}>{c.name || academicYearLabel(c.academic_year)} | {academicYearLabel(c.academic_year)} | Sem {c.semester}</option>
                  ))}
                </select>
                <label className="text-sm text-slate-600">Status</label>
                <select
                  value={submittedReportStatus}
                  onChange={(e) => { setSubmittedReportStatus(e.target.value); setSubmittedReportPage(1); }}
                  className="input text-base text-slate-900 text-left bg-white min-w-[120px]"
                >
                  <option value="">All</option>
                  <option value="PENDING">PENDING</option>
                  <option value="APPROVED">APPROVED</option>
                </select>
              </div>
              {submittedReportLoading ? (
                <p className="text-slate-500">Loading reportâ€¦</p>
              ) : submittedFeedbackReport.items.length === 0 ? (
                <p className="text-slate-500">No feedback submissions found for the selected filters.</p>
              ) : (
                <>
                  <div className="overflow-x-auto rounded-lg border border-slate-200">
                    <table className="min-w-full text-sm">
                      <thead className="bg-slate-50 text-left text-slate-700">
                        <tr>
                          <th className="px-3 py-2 font-medium">Student (Roll Â· Name)</th>
                          <th className="px-3 py-2 font-medium">Subject</th>
                          <th className="px-3 py-2 font-medium">Faculty</th>
                          <th className="px-3 py-2 font-medium">Cycle</th>
                          <th className="px-3 py-2 font-medium">Submitted at</th>
                          <th className="px-3 py-2 font-medium">Status</th>
                          <th className="px-3 py-2 font-medium">Rating</th>
                        </tr>
                      </thead>
                      <tbody className="text-slate-800">
                        {submittedFeedbackReport.items.map((row) => (
                          <tr key={row.id} className="border-t border-slate-100">
                            <td className="px-3 py-2">{row.student_roll} Â· {row.student_name}</td>
                            <td className="px-3 py-2">{row.subject_code} {row.subject_name}</td>
                          <td className="px-3 py-2">{row.program || '-'}</td>
                            <td className="px-3 py-2">{row.faculty_name}</td>
                            <td className="px-3 py-2">{row.cycle_name || row.academic_year} Â· {row.semester}</td>
                            <td className="px-3 py-2">{row.submitted_at ? new Date(row.submitted_at).toLocaleString() : 'â€”'}</td>
                            <td className="px-3 py-2">
                              <span className={row.status === 'APPROVED' ? 'text-green-600 font-medium' : 'text-amber-600 font-medium'}>
                                {row.status || 'â€”'}
                              </span>
                            </td>
                            <td className="px-3 py-2">{row.overall_rating != null ? Number(row.overall_rating).toFixed(2) : 'â€”'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {submittedFeedbackReport.totalPages > 1 && (
                    <div className="flex flex-wrap items-center gap-2 mt-3 text-sm text-slate-600">
                      <span>Page {submittedFeedbackReport.page} of {submittedFeedbackReport.totalPages} ({submittedFeedbackReport.total} total)</span>
                      <button
                        type="button"
                        onClick={() => setSubmittedReportPage((p) => Math.max(1, p - 1))}
                        disabled={submittedFeedbackReport.page <= 1}
                        className="px-2 py-1 border border-slate-300 rounded bg-white text-slate-800 disabled:opacity-50"
                      >
                        Previous
                      </button>
                      <button
                        type="button"
                        onClick={() => setSubmittedReportPage((p) => Math.min(submittedFeedbackReport.totalPages, p + 1))}
                        disabled={submittedFeedbackReport.page >= submittedFeedbackReport.totalPages}
                        className="px-2 py-1 border border-slate-300 rounded bg-white text-slate-800 disabled:opacity-50"
                      >
                        Next
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
            </>
            );
                case 'reports':
                  return (
            <div className="space-y-6">
              <div className="bg-white rounded-xl shadow p-6 border border-slate-200">
                <h2 className="text-lg font-semibold text-slate-800 mb-2">Advanced analytical reports (PDF)</h2>
                <p className="text-slate-600 text-sm mb-4">
                  Generate analytical reports in PDF format. Select report type, apply filters, and download.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                  <div>
                    <label className="input-label block mb-1">Report type</label>
                    <select
                      value={pdfReportType}
                      onChange={(e) => setPdfReportType(e.target.value)}
                      className="input text-base text-slate-900 text-left w-full"
                    >
                      {PDF_REPORT_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="input-label block mb-1">Cycle (optional)</label>
                    <select
                      value={pdfReportFilters.cycleId}
                      onChange={(e) => setPdfReportFilters((f) => ({ ...f, cycleId: e.target.value }))}
                      className="input text-base text-slate-900 text-left w-full"
                    >
                      <option value="">All cycles</option>
                      {reportCycles.filter((c) => c.type === 'SEMESTER' || c.type === 'ANNUAL').map((c) => (
                        <option key={c.id} value={c.id}>{c.name || academicYearLabel(c.academic_year)} | {academicYearLabel(c.academic_year)} | Sem {c.semester}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="input-label block mb-1">Academic year</label>
                    <select
                      value={pdfReportFilters.academicYear}
                      onChange={(e) => setPdfReportFilters((f) => ({ ...f, academicYear: e.target.value, cycleId: '' }))}
                      className="input text-base text-slate-900 text-left w-full"
                    >
                      <option value="">All available years</option>
                      {availableReportYears.map((y) => (
                        <option key={y} value={y}>{academicYearLabel(y)}</option>
                      ))}
                    </select>
                  </div>
                  {(pdfReportType === 'department' || pdfReportType === 'faculty') && (
                    <div>
                      <label className="input-label block mb-1">{pdfReportType === 'department' ? 'Department (required)' : 'Department (optional)'}</label>
                      <select
                        value={pdfReportFilters.department}
                        onChange={(e) => setPdfReportFilters((f) => ({ ...f, department: e.target.value }))}
                        className="input text-base text-slate-900 text-left w-full"
                      >
                        <option value="">â€” Select â€”</option>
                        {departmentOptions.map((d) => (
                          <option key={d} value={d}>{d}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  {pdfReportType === 'faculty' && (
                    <div>
                      <label className="input-label block mb-1">Faculty (optional)</label>
                      <select
                        value={pdfReportFilters.facultyId}
                        onChange={(e) => setPdfReportFilters((f) => ({ ...f, facultyId: e.target.value }))}
                        className="input text-base text-slate-900 text-left w-full"
                      >
                        <option value="">â€” All â€”</option>
                        {facultyList.map((f) => (
                          <option key={f.id} value={f.id}>{f.name}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  {pdfReportType === 'subject' && (
                    <div>
                      <label className="input-label block mb-1">Subject (optional)</label>
                      <select
                        value={pdfReportFilters.subjectId}
                        onChange={(e) => setPdfReportFilters((f) => ({ ...f, subjectId: e.target.value }))}
                        className="input text-base text-slate-900 text-left w-full"
                      >
                        <option value="">â€” All â€”</option>
                        {(subjects || []).map((s) => (
                          <option key={s.id} value={s.id}>{s.code || s.name}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  {pdfReportType === 'students-submitted' && (
                    <div>
                      <label className="input-label block mb-1">Status (optional)</label>
                      <select
                        value={pdfReportFilters.status}
                        onChange={(e) => setPdfReportFilters((f) => ({ ...f, status: e.target.value }))}
                        className="input text-base text-slate-900 text-left w-full"
                      >
                        <option value="">All</option>
                        <option value="PENDING">PENDING</option>
                        <option value="APPROVED">APPROVED</option>
                      </select>
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={previewReport}
                    disabled={reportPreviewLoading || (pdfReportType === 'department' && !pdfReportFilters.department)}
                    className="btn-secondary px-4 py-2.5 text-base font-medium"
                  >
                    {reportPreviewLoading ? 'Loading preview...' : 'Preview report'}
                  </button>
                  <button
                    type="button"
                    onClick={downloadPdfReport}
                    disabled={pdfReportDownloading || (pdfReportType === 'department' && !pdfReportFilters.department)}
                    className="btn-primary px-4 py-2.5 text-base font-medium"
                  >
                    {pdfReportDownloading ? 'Generating PDF...' : 'Download PDF report'}
                  </button>
                </div>
                <p className="text-slate-500 text-xs mt-2">
                  Preview reports before download. Odd/even consolidated reports summarize department, program, semester, subject, faculty, feedback count, average rating and remarks.
                </p>
                {reportPreview && (
                  <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-4">
                    <h3 className="text-base font-semibold text-slate-800 mb-3">Report Preview</h3>
                    {reportPreview.error ? (
                      <p className="text-sm text-red-600">{reportPreview.error}</p>
                    ) : reportPreview.rows ? (
                      <>
                        {reportPreview.totals && (
                          <div className="grid gap-3 md:grid-cols-4 mb-4">
                            <div className="stat-card"><p className="stat-card-label">Rows</p><p className="stat-card-value">{reportPreview.totals.rows}</p></div>
                            <div className="stat-card"><p className="stat-card-label">Feedback</p><p className="stat-card-value">{reportPreview.totals.feedback_count}</p></div>
                            <div className="stat-card"><p className="stat-card-label">Students</p><p className="stat-card-value">{reportPreview.totals.students_given_feedback}</p></div>
                            <div className="stat-card"><p className="stat-card-label">Average</p><p className="stat-card-value">{reportPreview.totals.average_rating || '-'}</p></div>
                          </div>
                        )}
                        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
                          <table className="min-w-full text-sm">
                            <thead className="bg-slate-100 text-left text-slate-700">
                              <tr>
                                <th className="px-3 py-2 font-medium">AY</th>
                                <th className="px-3 py-2 font-medium">Sem</th>
                                <th className="px-3 py-2 font-medium">Department</th>
                                <th className="px-3 py-2 font-medium">Program</th>
                                <th className="px-3 py-2 font-medium">Subject</th>
                                <th className="px-3 py-2 font-medium">Faculty</th>
                                <th className="px-3 py-2 font-medium">Students</th>
                                <th className="px-3 py-2 font-medium">Average</th>
                                <th className="px-3 py-2 font-medium">Remarks</th>
                              </tr>
                            </thead>
                            <tbody className="text-slate-800">
                              {reportPreview.rows.slice(0, 30).map((row, index) => (
                                <tr key={`${row.academic_year}-${row.semester}-${row.subject_code}-${index}`} className="border-t border-slate-100">
                                  <td className="px-3 py-2">{academicYearLabel(row.academic_year)}</td>
                                  <td className="px-3 py-2">{row.semester}</td>
                                  <td className="px-3 py-2">{row.department || '-'}</td>
                                  <td className="px-3 py-2">{row.program || '-'}</td>
                                  <td className="px-3 py-2">{row.subject_code || row.subject_name || '-'}</td>
                                  <td className="px-3 py-2">{row.faculty_name || '-'}</td>
                                  <td className="px-3 py-2">{row.students_given_feedback ?? row.unique_students ?? '-'}</td>
                                  <td className="px-3 py-2">{row.average_rating ?? row.avg_rating ?? row.avg_overall_rating ?? '-'}</td>
                                  <td className="px-3 py-2">{row.prominent_remarks || '-'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        {reportPreview.rows.length > 30 && <p className="mt-2 text-xs text-slate-500">Showing first 30 rows in preview.</p>}
                      </>
                    ) : reportPreview.statistics ? (
                      <div className="grid gap-3 md:grid-cols-4">
                        {Object.entries(reportPreview.statistics).map(([key, value]) => (
                          <div key={key} className="stat-card">
                            <p className="stat-card-label">{key.replace(/_/g, ' ')}</p>
                            <p className="stat-card-value">{value ?? '-'}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <pre className="text-xs whitespace-pre-wrap text-slate-700">{JSON.stringify(reportPreview, null, 2)}</pre>
                    )}
                  </div>
                )}
              </div>

              <div className="bg-white rounded-xl shadow p-6 border border-slate-200">
                <h2 className="text-lg font-semibold text-slate-800 mb-2">Report generation (CSV/Excel)</h2>
                <p className="text-slate-600 text-sm mb-4">
                  Generate and download reports. Use filters below and click Download CSV or Download Excel.
                </p>

                <div className="border-t border-slate-200 pt-6 mt-6">
                  <h3 className="text-base font-semibold text-slate-800 mb-2">1) Student feedback report</h3>
                  <p className="text-slate-600 text-sm mb-4">
                    One row per student who has submitted feedback: roll number, name, department, year of study, submission count, last submitted at. Filter by cycle or status (PENDING/APPROVED).
                  </p>
                  <div className="flex flex-wrap gap-3 items-center mb-4">
                    <label className="text-sm text-slate-600">Cycle</label>
                    <select
                      value={studentFeedbackReportCycleId}
                      onChange={(e) => { setStudentFeedbackReportCycleId(e.target.value); setStudentFeedbackReportPage(1); }}
                      className="input text-base text-slate-900 text-left bg-white min-w-[200px]"
                    >
                      <option value="">All cycles</option>
                      {reportCycles.map((c) => (
                        <option key={c.id} value={c.id}>{c.name || academicYearLabel(c.academic_year)} | {academicYearLabel(c.academic_year)} | Sem {c.semester}</option>
                      ))}
                    </select>
                    <label className="text-sm text-slate-600">Status</label>
                    <select
                      value={studentFeedbackReportStatus}
                      onChange={(e) => { setStudentFeedbackReportStatus(e.target.value); setStudentFeedbackReportPage(1); }}
                      className="input text-base text-slate-900 text-left bg-white min-w-[120px]"
                    >
                      <option value="">All</option>
                      <option value="PENDING">PENDING</option>
                      <option value="APPROVED">APPROVED</option>
                    </select>
                    <button
                      type="button"
                      onClick={() => downloadStudentFeedbackReport('csv')}
                      className="btn-secondary px-4 py-2 text-sm font-medium"
                    >
                      Download CSV
                    </button>
                    <button
                      type="button"
                      onClick={() => downloadStudentFeedbackReport('xlsx')}
                      className="btn-secondary px-4 py-2 text-sm font-medium"
                    >
                      Download Excel
                    </button>
                  </div>
                  {studentFeedbackReportLoading ? (
                    <p className="text-slate-500 text-sm">Loading reportâ€¦</p>
                  ) : studentFeedbackReport.students.length === 0 ? (
                    <p className="text-slate-500 text-sm">No students with submitted feedback for the selected filters.</p>
                  ) : (
                    <>
                      <div className="overflow-x-auto rounded-lg border border-slate-200">
                        <table className="min-w-full text-sm">
                          <thead className="bg-slate-50 text-left text-slate-700">
                            <tr>
                              <th className="px-3 py-2 font-medium">Roll number</th>
                              <th className="px-3 py-2 font-medium">Name</th>
                              <th className="px-3 py-2 font-medium">Department</th>
                              <th className="px-3 py-2 font-medium">Year</th>
                              <th className="px-3 py-2 font-medium">Submission count</th>
                              <th className="px-3 py-2 font-medium">Last submitted at</th>
                            </tr>
                          </thead>
                          <tbody className="text-slate-800">
                            {studentFeedbackReport.students.map((s) => (
                              <tr key={s.student_id} className="border-t border-slate-100">
                                <td className="px-3 py-2">{s.roll_number ?? 'â€”'}</td>
                                <td className="px-3 py-2">{s.full_name || `${s.first_name || ''} ${s.last_name || ''}`.trim() || 'â€”'}</td>
                                <td className="px-3 py-2">{s.department ?? 'â€”'}</td>
                                <td className="px-3 py-2">{s.year_of_study ?? 'â€”'}</td>
                                <td className="px-3 py-2">{s.submission_count ?? 0}</td>
                                <td className="px-3 py-2">{s.last_submitted_at ? new Date(s.last_submitted_at).toLocaleString() : 'â€”'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {studentFeedbackReport.totalPages > 1 && (
                        <div className="flex flex-wrap items-center gap-2 mt-3 text-sm text-slate-600">
                          <span>Page {studentFeedbackReport.page} of {studentFeedbackReport.totalPages} ({studentFeedbackReport.total} students)</span>
                          <button
                            type="button"
                            onClick={() => setStudentFeedbackReportPage((p) => Math.max(1, p - 1))}
                            disabled={studentFeedbackReport.page <= 1}
                            className="px-2 py-1 border border-slate-300 rounded bg-white text-slate-800 disabled:opacity-50"
                          >
                            Previous
                          </button>
                          <button
                            type="button"
                            onClick={() => setStudentFeedbackReportPage((p) => Math.min(studentFeedbackReport.totalPages, p + 1))}
                            disabled={studentFeedbackReport.page >= studentFeedbackReport.totalPages}
                            className="px-2 py-1 border border-slate-300 rounded bg-white text-slate-800 disabled:opacity-50"
                          >
                            Next
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
            );
                case 'faculty-feedback':
                  return (
                    <FacultyFeedbackCyclePanel
                      instituteId={instituteId}
                      isSuperAdmin={isSuperAdmin}
                      semesters={facultyFeedbackSemesters}
                    />
                  );
                case 'assign':
                  return (
            <div className="admin-wide-page">
            {renderMasterUploadPanel('allocations')}
            <AssignSubjectFacultyPanel
              activeCycle={activeCycle}
              assignError={assignError}
              assignSubjectFacultyForm={assignSubjectFacultyForm}
              setAssignSubjectFacultyForm={setAssignSubjectFacultyForm}
              subjects={subjects}
              facultyList={facultyList}
              divisions={divisions}
              submitAssignSubjectFaculty={submitAssignSubjectFaculty}
              assignSubmitting={assignSubmitting}
              activeSemesterAssignments={activeSemesterAssignments}
              browseMode
              departments={departments}
              programs={degrees}
              academicYears={[OPERATIONAL_ACADEMIC_YEAR]}
              allocationFilters={allocationFilters}
              onAllocationFiltersChange={setAllocationFilters}
              allocationCanShowList={allocationCanShowList}
              allocationListShown={allocationListShown}
              allocationListLoading={allocationListLoading}
              allocationResultCount={allocationRoster.length}
              onShowAllocationList={handleShowAllocationList}
              browsedAssignments={allocationRoster}
            />
            </div>
            );
                case 'degrees':
                  return (
            <div className="admin-wide-page">
            <div className="admin-master-panel"><div className="admin-master-panel-body">
              <div className="admin-master-header">
                <h2 className="admin-master-title">Program Master</h2>
                <button
                  type="button"
                  onClick={() => setShowAddDegree(true)}
                  disabled={isSuperAdmin && !instituteId}
                  className="btn-primary text-base font-medium disabled:cursor-not-allowed"
                >
                  Add Program
                </button>
              </div>
              {renderMasterUploadPanel('programs')}
              {showAddDegree && (
                <form onSubmit={submitAddDegree} className="admin-master-form">
                  <h3 className="text-sm font-semibold text-slate-700">Add program</h3>
                  {renderProgramMasterFields(degreeForm, setDegreeForm)}
                  <div className="flex gap-2">
                    <button
                      type="submit"
                      disabled={degreeSubmitting}
                      className="btn-primary text-base font-medium"
                    >
                      {degreeSubmitting ? 'Adding...' : 'Add program'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowAddDegree(false)}
                      className="btn-secondary text-base font-medium"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}
              {editingDegreeId && (
                <form onSubmit={submitEditDegree} className="admin-master-form">
                  <h3 className="text-sm font-semibold text-slate-700">Edit program</h3>
                  {renderProgramMasterFields(editDegreeForm, setEditDegreeForm)}
                  <div className="flex gap-2">
                    <button
                      type="submit"
                      disabled={degreeEditSubmitting}
                      className="btn-primary text-base font-medium"
                    >
                      {degreeEditSubmitting ? 'Savingâ€¦' : 'Save'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditingDegreeId(null)}
                      className="btn-secondary text-base font-medium"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}
              {degrees.length === 0 ? (
                <p className="text-slate-500">No programs yet. Add programs above or run seed; Subject Master will use a default list until then.</p>
              ) : (
                <div className="admin-master-table-wrap mb-6">
                  <table className="admin-master-table">
                    <thead>
                      <tr className="border-b border-slate-200 text-left text-slate-600">
                        <th className="pb-2 pr-4">Program Code</th>
                        <th className="pb-2 pr-4">Program Title</th>
                        <th className="pb-2 pr-4">Level</th>
                        <th className="pb-2 pr-4">Department</th>
                        <th className="pb-2 pr-4">Semesters</th>
                        <th className="pb-2 pr-4">Duration</th>
                        <th className="pb-2 pr-4">Intake</th>
                        <th className="pb-2">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {degrees.map((d) => (
                        <tr key={d.id} className="border-b border-slate-100">
                          <td className="py-2 pr-4 font-medium text-slate-800">{d.program_code || d.code || '-'}</td>
                          <td className="py-2 pr-4 text-slate-800">{d.program_title || d.name}</td>
                          <td className="py-2 pr-4 text-slate-600">{d.degree_level || '-'}</td>
                          <td className="py-2 pr-4 text-slate-600">{d.department_code || '-'}</td>
                          <td className="py-2 pr-4 text-slate-600">{d.number_of_semesters || '-'}</td>
                          <td className="py-2 pr-4 text-slate-600">{d.duration_years ? d.duration_years + ' years' : '-'}</td>
                          <td className="py-2 pr-4 text-slate-600">{d.intake_capacity || '-'}</td>
                          <td className="py-2">
                            <div className="flex gap-2">
                              <button
                                type="button"
                                onClick={() => startEditDegree(d)}
                                className="text-primary-600 hover:underline text-xs font-medium"
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                onClick={() => deleteDegree(d.id)}
                                disabled={deletingDegreeId === d.id}
                                className="text-red-600 hover:underline text-xs font-medium disabled:opacity-50"
                              >
                                {deletingDegreeId === d.id ? 'Deletingâ€¦' : 'Delete'}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            </div>
            </div>
            );
                case 'departments':
                  return (
            <div className="admin-wide-page">
            <div className="admin-master-panel"><div className="admin-master-panel-body">
              <div className="admin-master-header">
                <h2 className="admin-master-title">Department Master</h2>
                <button
                  type="button"
                  onClick={() => setShowAddDepartment(true)}
                  disabled={isSuperAdmin && !instituteId}
                  className="btn-primary text-base font-medium disabled:cursor-not-allowed"
                >
                  Add Department
                </button>
              </div>
              {renderMasterUploadPanel('departments')}
              {showAddDepartment && (
                <form onSubmit={submitAddDepartment} className="admin-master-form">
                  <h3 className="text-sm font-semibold text-slate-700">Add department</h3>
                  {renderDepartmentMasterFields(departmentForm, setDepartmentForm)}
                  <div className="flex gap-2">
                    <button
                      type="submit"
                      disabled={departmentSubmitting}
                      className="btn-primary text-base font-medium"
                    >
                      {departmentSubmitting ? 'Addingâ€¦' : 'Add department'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowAddDepartment(false)}
                      className="btn-secondary text-base font-medium"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}
              {editingDepartmentId && (
                <form onSubmit={submitEditDepartment} className="admin-master-form">
                  <h3 className="text-sm font-semibold text-slate-700">Edit department</h3>
                  {renderDepartmentMasterFields(editDepartmentForm, setEditDepartmentForm)}
                  <div className="flex gap-2">
                    <button
                      type="submit"
                      disabled={departmentEditSubmitting}
                      className="btn-primary text-base font-medium"
                    >
                      {departmentEditSubmitting ? 'Savingâ€¦' : 'Save'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditingDepartmentId(null)}
                      className="btn-secondary text-base font-medium"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}
              {departments.length === 0 ? (
                <p className="text-slate-500">No departments yet. Add departments above or run migration; Subject/Faculty forms will use a default list until then.</p>
              ) : (
                <div className="admin-master-table-wrap mb-6">
                  <table className="admin-master-table">
                    <thead>
                      <tr className="border-b border-slate-200 text-left text-slate-600">
                        <th className="pb-2 pr-4">Institute Code</th>
                        <th className="pb-2 pr-4">Department Code</th>
                        <th className="pb-2 pr-4">Department Name</th>
                        <th className="pb-2 pr-4">Established</th>
                        <th className="pb-2 pr-4">HOD</th>
                        <th className="pb-2 pr-4">Programs</th>
                        <th className="pb-2 pr-4">Status</th>
                        <th className="pb-2">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {departments.map((d) => (
                        <tr key={d.id} className="border-b border-slate-100">
                          <td className="py-2 pr-4 text-slate-600">{d.institute_code || instituteProfile?.institute_code || '-'}</td>
                          <td className="py-2 pr-4 font-medium text-slate-800">{d.code || '-'}</td>
                          <td className="py-2 pr-4 text-slate-800">{d.name}</td>
                          <td className="py-2 pr-4 text-slate-600">{d.establishment_year || '-'}</td>
                          <td className="py-2 pr-4 text-slate-600">{d.hod_faculty_code || '-'} {d.hod_faculty_name ? `| ${d.hod_faculty_name}` : ''}</td>
                          <td className="py-2 pr-4 text-slate-600">
                            {(Array.isArray(d.linked_programs) && d.linked_programs.length > 0
                              ? d.linked_programs.map((p) => p.name || p.code).filter(Boolean)
                              : Array.isArray(d.programs_offered) ? d.programs_offered : []
                            ).join(', ') || '-'}
                          </td>
                          <td className="py-2 pr-4 text-slate-600">{d.is_active !== false ? 'Active' : 'Inactive'}</td>
                          <td className="py-2">
                            <div className="flex gap-2">
                              <button
                                type="button"
                                onClick={() => startEditDepartment(d)}
                                className="text-primary-600 hover:underline text-xs font-medium"
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                onClick={() => deleteDepartment(d.id)}
                                disabled={deletingDepartmentId === d.id}
                                className="text-red-600 hover:underline text-xs font-medium disabled:opacity-50"
                              >
                                {deletingDepartmentId === d.id ? 'Deletingâ€¦' : 'Delete'}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            </div>
            </div>
            );
                case 'divisions':
                  return (
            <div className="admin-wide-page">
            <div className="admin-master-panel"><div className="admin-master-panel-body">
              <div className="admin-master-header">
                <h2 className="admin-master-title">Division Master</h2>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddDivision(true);
                    if (degrees.length > 0 && !divisionForm.degreeId) {
                      setDivisionForm((f) => ({ ...f, degreeId: degrees[0].id, academicYear: f.academicYear || ACADEMIC_YEARS[0], semester: f.semester || 1 }));
                    }
                  }}
                  disabled={isSuperAdmin && !instituteId}
                  className="btn-primary text-base font-medium disabled:cursor-not-allowed"
                >
                  Add Division
                </button>
              </div>
              {renderMasterUploadPanel('divisions')}
              <p className="text-slate-600 text-sm mb-4">
                Divisions are created for a course (degree) and semester. Each course + semester can have one or many divisions (e.g. A, B, C).
              </p>
              {showAddDivision && (
                <form onSubmit={submitAddDivision} className="admin-master-form">
                  <h3 className="text-sm font-semibold text-slate-700">Add division for semester</h3>
                  <div className="admin-master-grid">
                    <div>
                      <label className="input-label">Course (degree) *</label>
                      <select
                        value={divisionForm.degreeId}
                        onChange={(e) => setDivisionForm((f) => ({ ...f, degreeId: e.target.value }))}
                        required
                        className="input text-base text-slate-900 text-left"
                      >
                        <option value="">Select course</option>
                        {degrees.map((deg) => (
                          <option key={deg.id} value={deg.id}>{deg.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="input-label">Academic year *</label>
                      <select
                        value={divisionForm.academicYear}
                        onChange={(e) => setDivisionForm((f) => ({ ...f, academicYear: e.target.value }))}
                        required
                        className="input text-base text-slate-900 text-left"
                      >
                        {ACADEMIC_YEARS.map((y) => (
                          <option key={y} value={y}>{y}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="input-label">Semester *</label>
                      <select
                        value={divisionForm.semester}
                        onChange={(e) => setDivisionForm((f) => ({ ...f, semester: Number(e.target.value) }))}
                        required
                        className="input text-base text-slate-900 text-left"
                      >
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="input-label">Division name *</label>
                      <input
                        type="text"
                        value={divisionForm.name}
                        onChange={(e) => setDivisionForm((f) => ({ ...f, name: e.target.value }))}
                        required
                        maxLength={100}
                        className="input text-base text-slate-900 text-left"
                        placeholder="e.g. A, B, Section 1"
                      />
                    </div>
                    <div>
                      <label className="input-label">Code (optional)</label>
                      <input
                        type="text"
                        value={divisionForm.code}
                        onChange={(e) => setDivisionForm((f) => ({ ...f, code: e.target.value }))}
                        maxLength={20}
                        className="input text-base text-slate-900 text-left"
                        placeholder="e.g. A"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="submit"
                      disabled={divisionSubmitting}
                      className="btn-primary text-base font-medium"
                    >
                      {divisionSubmitting ? 'Addingâ€¦' : 'Add division'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowAddDivision(false)}
                      className="btn-secondary text-base font-medium"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}
              {editingDivisionId && (
                <form onSubmit={submitEditDivision} className="admin-master-form">
                  <h3 className="text-sm font-semibold text-slate-700">Edit division</h3>
                  <div className="admin-master-grid">
                    <div>
                      <label className="input-label">Name *</label>
                      <input
                        type="text"
                        value={editDivisionForm.name}
                        onChange={(e) => setEditDivisionForm((f) => ({ ...f, name: e.target.value }))}
                        required
                        maxLength={100}
                        className="input text-base text-slate-900 text-left"
                      />
                    </div>
                    <div>
                      <label className="input-label">Code (optional)</label>
                      <input
                        type="text"
                        value={editDivisionForm.code}
                        onChange={(e) => setEditDivisionForm((f) => ({ ...f, code: e.target.value }))}
                        maxLength={20}
                        className="input text-base text-slate-900 text-left"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="submit"
                      disabled={divisionEditSubmitting}
                      className="btn-primary text-base font-medium"
                    >
                      {divisionEditSubmitting ? 'Savingâ€¦' : 'Save'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditingDivisionId(null)}
                      className="btn-secondary text-base font-medium"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}
              <div className="flex flex-wrap gap-2 mb-4">
                <span className="text-xs text-slate-500 self-center">Filter:</span>
                <select
                  value={divisionFilters.degreeId}
                  onChange={(e) => setDivisionFilters((f) => ({ ...f, degreeId: e.target.value }))}
                  className="input px-2 py-1 text-base text-slate-900 text-left min-h-0"
                >
                  <option value="">All courses</option>
                  {degrees.map((deg) => (
                    <option key={deg.id} value={deg.id}>{deg.name}</option>
                  ))}
                </select>
                <select
                  value={divisionFilters.academicYear}
                  onChange={(e) => setDivisionFilters((f) => ({ ...f, academicYear: e.target.value }))}
                  className="input px-2 py-1 text-base text-slate-900 text-left min-h-0"
                >
                  <option value="">All years</option>
                  {ACADEMIC_YEARS.slice(0, 10).map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
                <select
                  value={divisionFilters.semester}
                  onChange={(e) => setDivisionFilters((f) => ({ ...f, semester: e.target.value === '' ? '' : Number(e.target.value) }))}
                  className="input px-2 py-1 text-base text-slate-900 text-left min-h-0"
                >
                  <option value="">All semesters</option>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              {divisions.length === 0 ? (
                <p className="text-slate-500">No divisions yet. Add divisions for a course and semester above.</p>
              ) : (
                <div className="admin-master-table-wrap mb-6">
                  <table className="admin-master-table">
                    <thead>
                      <tr className="border-b border-slate-200 text-left text-slate-600">
                        <th className="pb-2 pr-4">Division Code</th>
                        <th className="pb-2 pr-4">Program</th>
                        <th className="pb-2 pr-4">Semester</th>
                        <th className="pb-2 pr-4">Division</th>
                        <th className="pb-2 pr-4">Academic Year</th>
                        <th className="pb-2 pr-4">Batch Year</th>
                        <th className="pb-2 pr-4">Department</th>
                        <th className="pb-2 pr-4">Status</th>
                        <th className="pb-2">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {divisions.map((d) => (
                        <tr key={d.id} className="border-b border-slate-100">
                          <td className="py-2 pr-4 font-medium text-slate-800">{d.division_code || d.code || '-'}</td>
                          <td className="py-2 pr-4 text-slate-700">{d.program_code || d.degree_name || '-'}</td>
                          <td className="py-2 pr-4 text-slate-600">{d.semester_code || ('Sem ' + d.semester)}</td>
                          <td className="py-2 pr-4 text-slate-800">{d.division || d.name}</td>
                          <td className="py-2 pr-4 text-slate-600">{d.academic_year}</td>
                          <td className="py-2 pr-4 text-slate-600">{d.batch_year || '-'}</td>
                          <td className="py-2 pr-4 text-slate-600">{d.department_code || '-'}</td>
                          <td className="py-2 pr-4 text-slate-600">{d.is_active !== false ? 'Active' : 'Inactive'}</td>
                          <td className="py-2">
                            <div className="flex gap-2">
                              <button
                                type="button"
                                onClick={() => startEditDivision(d)}
                                className="text-primary-600 hover:underline text-xs font-medium"
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                onClick={() => deleteDivision(d.id)}
                                disabled={deletingDivisionId === d.id}
                                className="text-red-600 hover:underline text-xs font-medium disabled:opacity-50"
                              >
                                {deletingDivisionId === d.id ? 'Deletingâ€¦' : 'Delete'}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="mt-6 pt-6 border-t border-slate-200">
                <h3 className="text-sm font-semibold text-slate-700 mb-3">Students in division (for that semester)</h3>
                <p className="text-slate-600 text-sm mb-3">Select a division to view and manage students added to it for that semester.</p>
                <div className="flex flex-wrap gap-3 items-end mb-4">
                  <div>
                    <label className="input-label">Division</label>
                    <select
                      value={selectedDivisionForStudents}
                      onChange={(e) => setSelectedDivisionForStudents(e.target.value)}
                      className="input text-base text-slate-900 text-left min-w-[200px]"
                    >
                      <option value="">Select division</option>
                      {divisions.map((d) => (
                        <option key={d.id} value={d.id}>{d.name} Â· {d.degree_name} Â· {d.academic_year} Sem {d.semester}</option>
                      ))}
                    </select>
                  </div>
                </div>
                {selectedDivisionForStudents && (
                  <>
                    <div className="mb-4 p-4 rounded-lg border border-slate-200 bg-white">
                      <h4 className="text-sm font-semibold text-slate-700 mb-2">Remove student from division</h4>
                      <p className="text-slate-600 text-xs mb-2">Students currently in this division. Click to remove.</p>
                      {divisionStudents.length === 0 ? (
                        <p className="text-slate-500 text-sm">No students in this division yet. Add students below.</p>
                      ) : (
                        <ul className="space-y-1 text-sm">
                          {divisionStudents.map((s) => (
                            <li key={s.student_id} className="flex items-center justify-between py-2 px-2 rounded border-b border-slate-100 last:border-0 hover:bg-slate-50">
                              <span className="text-slate-800">{s.roll_number} â€” {s.first_name} {s.last_name}</span>
                              <button
                                type="button"
                                onClick={() => removeStudentFromDivision(s.student_id)}
                                disabled={removingStudentFromDivisionId === s.student_id}
                                title="Remove student from division"
                                className="px-2 py-1 text-red-600 hover:bg-red-50 rounded text-xs font-medium disabled:opacity-50"
                              >
                                {removingStudentFromDivisionId === s.student_id ? 'Removingâ€¦' : 'Remove from division'}
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                    <form onSubmit={addStudentsToDivision} className="p-4 rounded-lg border border-slate-200 bg-slate-50">
                      <h4 className="text-sm font-semibold text-slate-700 mb-2">Add students to this division</h4>
                      <p className="text-slate-600 text-xs mb-3">Select one or more students (Ctrl+click for multiple), then click the button.</p>
                      <div className="flex flex-wrap gap-3 items-end">
                        <div className="min-w-[220px]">
                          <label className="input-label">Students (multi-select)</label>
                          <select
                            multiple
                            value={addStudentsToDivisionIds}
                            onChange={(e) => setAddStudentsToDivisionIds(Array.from(e.target.selectedOptions, (o) => o.value))}
                            className="input text-base text-slate-900 text-left h-24 resize-y"
                          >
                            {studentListForDivision
                              .filter((st) => !divisionStudents.some((ds) => ds.student_id === st.id))
                              .map((st) => (
                                <option key={st.id} value={st.id}>{st.roll_number} â€” {st.name}</option>
                              ))}
                          </select>
                          <p className="text-xs text-slate-500 mt-0.5">Ctrl+click to select multiple</p>
                        </div>
                        <button
                          type="submit"
                          disabled={addStudentsToDivisionSubmitting || addStudentsToDivisionIds.length === 0}
                          className="px-3 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-500 disabled:opacity-50"
                        >
                          {addStudentsToDivisionSubmitting ? 'Addingâ€¦' : 'Add students to this division'}
                        </button>
                      </div>
                    </form>
                  </>
                )}
              </div>
            </div>
            </div>
            </div>
            );
                case 'subjects':
                  return (
            <div className="admin-wide-page">
            <div className="admin-master-panel"><div className="admin-master-panel-body">
              <div className="admin-master-header">
                <h2 className="admin-master-title">
                  Subject Master
                </h2>
                <button
                  type="button"
                  onClick={() => {
                    const firstProgram = degrees[0];
                    setSubjectForm((prev) => ({
                      ...prev,
                      degree: firstProgram?.name || (degreeOptions.length > 0 ? degreeOptions[0] : prev.degree),
                      degreeId: firstProgram?.id || prev.degreeId,
                      programCode: firstProgram?.program_code || firstProgram?.code || prev.programCode,
                      departmentCode: firstProgram?.department_code || prev.departmentCode,
                      department: getProgramDepartmentName(firstProgram) || prev.department,
                      semester: 1,
                      semesterCode: firstProgram?.program_code ? `${firstProgram.program_code}-S1` : prev.semesterCode,
                    }));
                    setShowAddSubject(true);
                  }}
                  disabled={isSuperAdmin && !instituteId}
                  className="btn-primary text-base font-medium disabled:cursor-not-allowed"
                >
                  Add Subject
                </button>
              </div>
              {renderMasterUploadPanel('subjects')}
              <SubjectMasterFilters
                subjectFilters={subjectFilters}
                onFiltersChange={setSubjectFilters}
                departments={departments.length > 0
                  ? departments
                  : departmentOptions.map((name) => ({ id: name, name, code: name }))}
                programs={degrees}
                canShowList={subjectCanShowRoster}
                listShown={subjectRosterShown}
                resultCount={subjectRosterList.length}
                onShowList={handleShowSubjectRoster}
                listLoading={subjectRosterLoading}
              />
              {showAddSubject && (
                <form onSubmit={submitAddSubject} className="admin-master-form">
                  <h3 className="text-sm font-semibold text-slate-700">Add subject</h3>
                  <div className="admin-master-grid">
                    <div>
                      <label className="input-label">Subject Code *</label>
                      <input
                        type="text"
                        value={subjectForm.code}
                        onChange={(e) => setSubjectForm((f) => ({ ...f, code: e.target.value }))}
                        required
                        maxLength={20}
                        className="input text-base text-slate-900 text-left"
                        placeholder="e.g. CS301"
                      />
                    </div>
                    <div>
                      <label className="input-label">Program *</label>
                      <select
                        value={subjectForm.degreeId}
                        onChange={(e) => applyProgramToSubjectForm(e.target.value, setSubjectForm)}
                        required
                        className="input text-base text-slate-900 text-left"
                      >
                        <option value="">Select program</option>
                        {degrees.map((d) => (
                          <option key={d.id} value={d.id}>{d.program_code || d.code} - {d.program_title || d.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="input-label">Department *</label>
                      <select
                        value={subjectForm.department}
                        onChange={(e) => setSubjectForm((f) => ({ ...f, department: e.target.value }))}
                        className="input text-base text-slate-900 text-left"
                      >
                        {departmentOptions.map((d) => (
                          <option key={d} value={d}>{d}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-span-2">
                      <label className="input-label">Subject Name *</label>
                      <input
                        type="text"
                        value={subjectForm.name}
                        onChange={(e) => setSubjectForm((f) => ({ ...f, name: e.target.value }))}
                        required
                        maxLength={200}
                        className="input text-base text-slate-900 text-left"
                        placeholder="e.g. Data Structures"
                      />
                    </div>
                    <div>
                      <label className="input-label">Semester *</label>
                      <select
                        value={subjectForm.semester}
                        onChange={(e) => applySemesterToSubjectForm(e.target.value, setSubjectForm)}
                        className="input text-base text-slate-900 text-left"
                      >
                        {semesterNumbersForProgram(degrees.find((d) => d.id === subjectForm.degreeId)).map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="input-label">Semester Code</label>
                      <input value={subjectForm.semesterCode} readOnly className="input text-base text-slate-900 text-left bg-slate-100" />
                    </div>
                    <div>
                      <label className="input-label">Division</label>
                      <select value={subjectForm.divisionCode} onChange={(e) => setSubjectForm((f) => ({ ...f, divisionCode: e.target.value }))} className="input text-base text-slate-900 text-left">
                        <option value="">Common for all divisions</option>
                        {divisions
                          .filter((d) => !subjectForm.programCode || d.program_code === subjectForm.programCode)
                          .filter((d) => !subjectForm.semesterCode || d.semester_code === subjectForm.semesterCode)
                          .map((d) => <option key={d.id} value={d.division_code}>{d.division_code || d.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="input-label">Credits *</label>
                      <input
                        type="number"
                        min={1}
                        value={subjectForm.credits}
                        onChange={(e) => setSubjectForm((f) => ({ ...f, credits: Number(e.target.value) || 1 }))}
                        required
                        className="input text-base text-slate-900 text-left"
                      />
                    </div>
                    <div>
                      <label className="input-label">Subject Type</label>
                      <select value={subjectForm.subjectType} onChange={(e) => setSubjectForm((f) => ({ ...f, subjectType: e.target.value, isElective: /Elective/i.test(e.target.value) }))} className="input text-base text-slate-900 text-left">
                        {['Core', 'Open Elective', 'Free Elective', 'Practical', 'Project', 'Seminar', 'Audit', 'Other'].map((type) => <option key={type} value={type}>{type}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="input-label">Theory Hrs</label>
                      <input type="number" min="0" value={subjectForm.workloadTheoryHrs} onChange={(e) => setSubjectForm((f) => ({ ...f, workloadTheoryHrs: e.target.value }))} className="input text-base text-slate-900 text-left" />
                    </div>
                    <div>
                      <label className="input-label">Practical Hrs</label>
                      <input type="number" min="0" value={subjectForm.workloadPracticalHrs} onChange={(e) => setSubjectForm((f) => ({ ...f, workloadPracticalHrs: e.target.value }))} className="input text-base text-slate-900 text-left" />
                    </div>
                    <div>
                      <label className="input-label">Units</label>
                      <input type="number" min="1" value={subjectForm.numberOfUnits} onChange={(e) => setSubjectForm((f) => ({ ...f, numberOfUnits: e.target.value }))} className="input text-base text-slate-900 text-left" />
                    </div>
                    <div className="col-span-2 flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="subjectElective"
                        checked={subjectForm.isElective}
                        onChange={(e) => setSubjectForm((f) => ({ ...f, isElective: e.target.checked }))}
                        className="rounded border-slate-300 text-primary-600"
                      />
                      <label htmlFor="subjectElective" className="text-sm text-slate-700">Elective</label>
                    </div>
                    <div className="col-span-2">
                      <label className="input-label">Description (optional)</label>
                      <input
                        type="text"
                        value={subjectForm.description}
                        onChange={(e) => setSubjectForm((f) => ({ ...f, description: e.target.value }))}
                        className="input text-base text-slate-900 text-left"
                        placeholder="Brief description"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="input-label">Syllabus Content</label>
                      <textarea value={subjectForm.syllabusContent} onChange={(e) => setSubjectForm((f) => ({ ...f, syllabusContent: e.target.value }))} className="input text-base text-slate-900 text-left min-h-[120px]" placeholder="Unit-wise syllabus content" />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="submit"
                      disabled={subjectSubmitting}
                      className="btn-primary text-base font-medium"
                    >
                      {subjectSubmitting ? 'Addingâ€¦' : 'Add subject'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowAddSubject(false)}
                      className="btn-secondary text-base font-medium"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}
              {editingSubjectId && (
                <form onSubmit={submitEditSubject} className="admin-master-form">
                  <h3 className="text-sm font-semibold text-slate-700">
                    Edit subject {editingSubject?.code && `(${editingSubject.code})`}
                  </h3>
                  <div className="admin-master-grid">
                    <div className="col-span-2">
                      <label className="input-label">Subject Name *</label>
                      <input
                        type="text"
                        value={editSubjectForm.name}
                        onChange={(e) => setEditSubjectForm((f) => ({ ...f, name: e.target.value }))}
                        required
                        maxLength={200}
                        className="input text-base text-slate-900 text-left"
                      />
                    </div>
                    <div>
                      <label className="input-label">Degree *</label>
                      <select
                        value={editSubjectForm.degree}
                        onChange={(e) => setEditSubjectForm((f) => ({ ...f, degree: e.target.value }))}
                        className="input text-base text-slate-900 text-left"
                      >
                        {degreeOptions.map((d) => (
                          <option key={d} value={d}>{d}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="input-label">Department *</label>
                      <select
                        value={editSubjectForm.department}
                        onChange={(e) => setEditSubjectForm((f) => ({ ...f, department: e.target.value }))}
                        className="input text-base text-slate-900 text-left"
                      >
                        {departmentOptions.map((d) => (
                          <option key={d} value={d}>{d}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="input-label">Semester *</label>
                      <select
                        value={editSubjectForm.semester}
                        onChange={(e) => setEditSubjectForm((f) => ({ ...f, semester: Number(e.target.value) }))}
                        className="input text-base text-slate-900 text-left"
                      >
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                          <option key={n} value={n}>{n}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="input-label">Credits *</label>
                      <input
                        type="number"
                        min={1}
                        value={editSubjectForm.credits}
                        onChange={(e) => setEditSubjectForm((f) => ({ ...f, credits: Number(e.target.value) || 1 }))}
                        className="input text-base text-slate-900 text-left"
                      />
                    </div>
                    <div className="col-span-2 flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="editSubjectElective"
                        checked={editSubjectForm.isElective}
                        onChange={(e) => setEditSubjectForm((f) => ({ ...f, isElective: e.target.checked }))}
                        className="rounded border-slate-300 text-primary-600"
                      />
                      <label htmlFor="editSubjectElective" className="text-sm text-slate-700">Elective</label>
                    </div>
                    <div className="col-span-2">
                      <label className="input-label">Description (optional)</label>
                      <input
                        type="text"
                        value={editSubjectForm.description}
                        onChange={(e) => setEditSubjectForm((f) => ({ ...f, description: e.target.value }))}
                        className="input text-base text-slate-900 text-left"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="submit"
                      disabled={subjectEditSubmitting}
                      className="btn-primary text-base font-medium"
                    >
                      {subjectEditSubmitting ? 'Savingâ€¦' : 'Save changes'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditingSubjectId(null)}
                      className="btn-secondary text-base font-medium"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}
              {!subjectRosterShown ? (
                <p className="text-slate-500 text-sm">Select department and program, then click <strong>Show</strong> to load subjects.</p>
              ) : subjectRosterList.length === 0 ? (
                <p className="text-slate-500 text-sm">No subjects match these filters. Try another semester or run the program subject seed.</p>
              ) : (
                <div className="admin-master-table-wrap">
                  {subjectAppliedFilters ? (
                    <p className="text-xs text-slate-600 mb-2">
                      {subjectAppliedFilters.department}
                      {' · '}
                      {degrees.find((d) => d.id === subjectAppliedFilters.degreeId)?.name || 'Program'}
                      {subjectAppliedFilters.semester !== '' && subjectAppliedFilters.semester != null
                        ? ` · Semester ${subjectAppliedFilters.semester}`
                        : ' · All semesters'}
                      {' · '}
                      {subjectRosterList.length} subject{subjectRosterList.length === 1 ? '' : 's'}
                    </p>
                  ) : null}
                  <table className="admin-master-table">
                    <thead>
                      <tr className="border-b border-slate-200 text-left text-slate-600">
                        <th className="pb-2 pr-4">Subject Code</th>
                        <th className="pb-2 pr-4">Subject Name</th>
                        <th className="pb-2 pr-4">Degree</th>
                        <th className="pb-2 pr-4">Department</th>
                        <th className="pb-2 pr-4">Semester</th>
                        <th className="pb-2 pr-4">Credits</th>
                        <th className="pb-2">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {subjectRosterList.map((s) => (
                        <tr key={s.id} className="border-b border-slate-100">
                          <td className="py-2 pr-4 font-medium text-slate-800">{s.code}</td>
                          <td className="py-2 pr-4 text-slate-700">{s.name || 'â€”'}</td>
                          <td className="py-2 pr-4 text-slate-600">{s.degree ?? 'â€”'}</td>
                          <td className="py-2 pr-4 text-slate-600">{s.department || 'â€”'}</td>
                          <td className="py-2 pr-4 text-slate-600">{s.semester ?? 'â€”'}</td>
                          <td className="py-2 pr-4 text-slate-600">{s.credits ?? 'â€”'}</td>
                          <td className="py-2">
                            <div className="flex gap-2">
                              <button
                                type="button"
                                onClick={() => startEditSubject(s)}
                                className="text-primary-600 hover:underline text-xs font-medium"
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                onClick={() => deleteSubject(s.id)}
                                disabled={deletingSubjectId === s.id}
                                className="text-red-600 hover:underline text-xs font-medium disabled:opacity-50"
                              >
                                {deletingSubjectId === s.id ? 'Deletingâ€¦' : 'Delete'}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            </div>
            </div>
            );
                case 'students':
                  return (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            <div className="admin-card md:col-span-2 lg:col-span-3 student-master-page">
              <div className="admin-card-body student-master-body">
              <div className="student-master-header">
                <div>
                  <h2 className="student-master-title">Students</h2>
                  <p className="student-master-hint">
                    Current feedback year <strong>{OPERATIONAL_ACADEMIC_YEAR}</strong> · odd semesters {OPERATIONAL_ODD_SEMESTERS.join(', ')} only · prior years in <strong>Reports</strong>
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setShowStudentUploadPanel((open) => !open);
                    setStudentUploadError('');
                  }}
                  disabled={isSuperAdmin && !instituteId}
                  className="btn-primary student-master-add-btn shrink-0 disabled:cursor-not-allowed"
                >
                  {showStudentUploadPanel ? 'Hide upload' : 'Upload students'}
                </button>
              </div>

              <StudentBulkUpload
                open={showStudentUploadPanel}
                onToggle={() => setShowStudentUploadPanel(false)}
                onDownloadTemplate={downloadStudentsTemplate}
                downloadDisabled={isSuperAdmin && !instituteId}
                uploadFile={studentUploadFile}
                onFileChange={setStudentUploadFile}
                onSubmit={submitUploadStudents}
                uploadSubmitting={studentUploadSubmitting}
                uploadResult={studentUploadResult}
                uploadError={studentUploadError}
                inputId="student-master-upload-input"
              />

              <StudentMasterFilters
                searchInput={studentSearchInput}
                onSearchInputChange={setStudentSearchInput}
                onSearchApply={() => setStudentHighlightTerm(studentSearchInput.trim())}
                onSearchClear={() => {
                  setStudentHighlightTerm('');
                  setStudentSearchInput('');
                }}
                highlightTerm={studentHighlightTerm}
                studentFilters={studentFilters}
                onFiltersChange={(updater) => {
                  setStudentFilters(updater);
                  setStudentRosterShown(false);
                  setStudentPagination((p) => ({ ...p, page: 1 }));
                }}
                departments={departments.length > 0
                  ? departments
                  : departmentOptions.map((name) => ({ id: name, name, code: name }))}
                programs={degrees}
                divisions={studentOperationalDivisions}
                academicYears={studentAcademicYears}
                semesterOptions={studentSemesterOptions}
                activeAcademicYear={activeCycle?.academic_year || currentTerm?.academic_year}
                activeSemester={activeCycle?.semester || currentSemesterNumbers[0]}
                canShowRoster={studentCanShowRoster}
                rosterShown={studentRosterShown}
                resultCount={studentsList.length}
                highlightMatchCount={studentHighlightMatchCount}
                onShowRoster={handleShowStudentRoster}
                rosterLoading={studentRosterLoading}
                divisionsCatalogEmpty={studentOperationalDivisions.length === 0}
                academicYearLocked
              />
              {editingStudentId && (
                <form onSubmit={submitEditStudent} className="mb-6 p-4 rounded-lg border border-primary-200 bg-primary-50/50 space-y-3">
                  <h3 className="text-sm font-semibold text-slate-700">Edit student</h3>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="input-label">Roll number *</label>
                      <input type="text" value={editStudentForm.rollNumber} onChange={(e) => setEditStudentForm((f) => ({ ...f, rollNumber: e.target.value }))} required className="input text-base text-slate-900 text-left" />
                    </div>
                    <div>
                      <label className="input-label">Department *</label>
                      <select value={editStudentForm.department} onChange={(e) => setEditStudentForm((f) => ({ ...f, department: e.target.value }))} className="input text-base text-slate-900 text-left">
                        {departmentOptions.map((d) => (
                          <option key={d} value={d}>{d}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="input-label">First name *</label>
                      <input type="text" value={editStudentForm.firstName} onChange={(e) => setEditStudentForm((f) => ({ ...f, firstName: e.target.value }))} required className="input text-base text-slate-900 text-left" />
                    </div>
                    <div>
                      <label className="input-label">Last name *</label>
                      <input type="text" value={editStudentForm.lastName} onChange={(e) => setEditStudentForm((f) => ({ ...f, lastName: e.target.value }))} required className="input text-base text-slate-900 text-left" />
                    </div>
                    <div>
                      <label className="input-label">Year of study *</label>
                      <select value={editStudentForm.yearOfStudy} onChange={(e) => setEditStudentForm((f) => ({ ...f, yearOfStudy: Number(e.target.value) }))} className="input text-base text-slate-900 text-left">
                        {[1, 2, 3, 4, 5].map((y) => (
                          <option key={y} value={y}>{y}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="input-label">Section / Contact</label>
                      <input type="text" value={editStudentForm.section} onChange={(e) => setEditStudentForm((f) => ({ ...f, section: e.target.value }))} maxLength={10} className="input text-base text-slate-900 text-left" />
                    </div>
                    <div>
                      <label className="input-label">Batch</label>
                      <input type="text" value={editStudentForm.batch} onChange={(e) => setEditStudentForm((f) => ({ ...f, batch: e.target.value }))} maxLength={20} className="input text-base text-slate-900 text-left" />
                    </div>
                    <div>
                      <label className="input-label">Contact number</label>
                      <input type="text" value={editStudentForm.contactNumber} onChange={(e) => setEditStudentForm((f) => ({ ...f, contactNumber: e.target.value }))} maxLength={15} className="input text-base text-slate-900 text-left" />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button type="submit" disabled={editStudentSubmitting} className="btn-primary text-base font-medium">{editStudentSubmitting ? 'Savingâ€¦' : 'Save'}</button>
                    <button type="button" onClick={() => setEditingStudentId(null)} className="btn-secondary text-base font-medium">Cancel</button>
                  </div>
                </form>
              )}
              <StudentMasterTable
                students={studentsList}
                onEdit={startEditStudent}
                onDelete={deleteStudent}
                deletingStudentId={deletingStudentId}
                rosterShown={studentRosterShown}
                highlightTerm={studentHighlightTerm}
              />
              {studentRosterShown && studentPagination.totalPages > 1 && (
                <div className="student-pagination">
                  <span>Page {studentPagination.page} of {studentPagination.totalPages} ({studentPagination.total} total)</span>
                  <button
                    type="button"
                    disabled={studentPagination.page <= 1}
                    onClick={() => {
                      const nextPage = studentPagination.page - 1;
                      setStudentPagination((p) => ({ ...p, page: nextPage }));
                      if (studentAppliedFilters) fetchStudents(studentAppliedFilters, { page: nextPage });
                    }}
                    className="student-pagination-btn"
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    disabled={studentPagination.page >= studentPagination.totalPages}
                    onClick={() => {
                      const nextPage = studentPagination.page + 1;
                      setStudentPagination((p) => ({ ...p, page: nextPage }));
                      if (studentAppliedFilters) fetchStudents(studentAppliedFilters, { page: nextPage });
                    }}
                    className="student-pagination-btn"
                  >
                    Next
                  </button>
                </div>
              )}
              </div>
            </div>
            </div>
            );
                case 'faculty':
                  return (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            <div className="admin-card md:col-span-2 lg:col-span-3 student-master-page">
              <div className="admin-card-body student-master-body">
              <div className="student-master-header">
                <div>
                  <h2 className="student-master-title">Faculty</h2>
                  <p className="student-master-hint">
                    Year <strong>{OPERATIONAL_ACADEMIC_YEAR}</strong> · filter by department &amp; program · semester/division optional · default password <strong>Faculty@123</strong>
                  </p>
                  {facultySeedNotice ? (
                    <p className="text-sm text-green-700 mt-1">{facultySeedNotice}</p>
                  ) : null}
                </div>
                <div className="flex flex-wrap gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={handleSeedProgramFaculty}
                    disabled={(isSuperAdmin && !instituteId) || facultySeedSubmitting}
                    className="btn-secondary student-master-add-btn disabled:cursor-not-allowed"
                    title="Creates 5 faculty per active program (password Faculty@123)"
                  >
                    {facultySeedSubmitting ? 'Creating…' : 'Create program faculty'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setActiveMenu('users'); setShowAddFaculty(true); }}
                    disabled={isSuperAdmin && !instituteId}
                    className="btn-primary student-master-add-btn disabled:cursor-not-allowed"
                  >
                    Add faculty
                  </button>
                </div>
              </div>

              <FacultyMasterFilters
                searchInput={facultySearchInput}
                onSearchInputChange={setFacultySearchInput}
                onSearchApply={() => setFacultyHighlightTerm(facultySearchInput.trim())}
                onSearchClear={() => {
                  setFacultyHighlightTerm('');
                  setFacultySearchInput('');
                }}
                highlightTerm={facultyHighlightTerm}
                facultyFilters={facultyFilters}
                onFiltersChange={(updater) => {
                  setFacultyFilters(updater);
                  setFacultyPagination((p) => ({ ...p, page: 1 }));
                }}
                departments={departments.length > 0
                  ? departments
                  : departmentOptions.map((name) => ({ id: name, name, code: name }))}
                programs={degrees}
                divisions={facultyOperationalDivisions}
                academicYears={facultyAcademicYears}
                semesterOptions={facultySemesterOptions}
                canShowRoster={facultyCanShowRoster}
                rosterShown={facultyRosterShown}
                resultCount={facultyRosterList.length}
                highlightMatchCount={facultyHighlightMatchCount}
                onShowRoster={handleShowFacultyRoster}
                rosterLoading={facultyRosterLoading}
                academicYearLocked
              />

              <FacultyMasterTable
                faculty={facultyRosterList}
                rosterShown={facultyRosterShown}
                highlightTerm={facultyHighlightTerm}
                departmentLabel={facultyAppliedFilters?.department}
                programLabel={facultyAppliedProgramLabel}
                groupByProgram={facultyAppliedFilters?.degreeId === FACULTY_ALL_PROGRAMS}
              />
              {facultyRosterShown && facultyPagination.totalPages > 1 && (
                <div className="student-pagination">
                  <span>Page {facultyPagination.page} of {facultyPagination.totalPages} ({facultyPagination.total} total)</span>
                  <button
                    type="button"
                    disabled={facultyPagination.page <= 1}
                    onClick={() => {
                      const nextPage = facultyPagination.page - 1;
                      setFacultyPagination((p) => ({ ...p, page: nextPage }));
                      if (facultyAppliedFilters) fetchFacultyRoster(facultyAppliedFilters, { page: nextPage });
                    }}
                    className="student-pagination-btn"
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    disabled={facultyPagination.page >= facultyPagination.totalPages}
                    onClick={() => {
                      const nextPage = facultyPagination.page + 1;
                      setFacultyPagination((p) => ({ ...p, page: nextPage }));
                      if (facultyAppliedFilters) fetchFacultyRoster(facultyAppliedFilters, { page: nextPage });
                    }}
                    className="student-pagination-btn"
                  >
                    Next
                  </button>
                </div>
              )}
              </div>
            </div>
            </div>
            );
                case 'upload':
                  return (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            <div className="admin-card md:col-span-2 lg:col-span-3 student-master-page">
              <div className="admin-card-body student-master-body">
                <div className="student-master-header">
                  <div>
                    <h2 className="student-master-title">Upload students</h2>
                    <p className="student-master-hint">
                      Bulk import from Excel — same flow as the <strong>Students</strong> page.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setActiveMenu('students')}
                    className="btn-secondary student-master-add-btn shrink-0"
                  >
                    Open Students roster
                  </button>
                </div>
                <StudentBulkUpload
                  open
                  onDownloadTemplate={downloadStudentsTemplate}
                  downloadDisabled={isSuperAdmin && !instituteId}
                  uploadFile={studentUploadFile}
                  onFileChange={setStudentUploadFile}
                  onSubmit={submitUploadStudents}
                  uploadSubmitting={studentUploadSubmitting}
                  uploadResult={studentUploadResult}
                  uploadError={studentUploadError}
                  inputId="student-upload-input"
                />
              </div>
            </div>
            </div>
            );
                case 'users':
                  return (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            <div className="bg-white rounded-xl shadow p-6 border border-slate-200 md:col-span-2">
              <h2 className="text-lg font-semibold text-slate-800 mb-1">User Management</h2>
              <p className="text-slate-600 text-sm mb-4">Manage institute users. Each user is either <strong>Faculty</strong> or <strong>Student</strong>. Add, reset password, or remove users.</p>
              <div className="flex flex-wrap gap-3 items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <label className="input-label mb-0">Role:</label>
                  <select
                    value={userManagementRoleFilter}
                    onChange={(e) => setUserManagementRoleFilter(e.target.value)}
                    className="input text-base text-slate-900 text-left"
                  >
                    <option value="">All users</option>
                    <option value="FACULTY">Faculty only</option>
                    <option value="STUDENT">Student only</option>
                  </select>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setShowAddFaculty(true)}
                    disabled={isSuperAdmin && !instituteId}
                    className="btn-primary text-base font-medium disabled:cursor-not-allowed"
                  >
                    Add Faculty
                  </button>
                  <button
                    type="button"
                    onClick={() => { setActiveMenu('students'); setShowStudentUploadPanel(true); }}
                    disabled={isSuperAdmin && !instituteId}
                    className="px-3 py-1.5 border border-primary-600 text-primary-600 rounded-lg text-sm font-medium hover:bg-primary-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Upload students
                  </button>
                </div>
              </div>
              {showAddFaculty && (
                <form onSubmit={submitAddFaculty} className="mb-6 p-4 rounded-lg border border-slate-200 bg-slate-50 space-y-3">
                  <h3 className="text-sm font-semibold text-slate-700">Add faculty (credentials will be generated)</h3>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="input-label">Employee ID *</label>
                      <input
                        type="text"
                        value={facultyForm.employeeId}
                        onChange={(e) => setFacultyForm((f) => ({ ...f, employeeId: e.target.value }))}
                        required
                        className="input text-base text-slate-900 text-left"
                        placeholder="e.g. FAC003"
                      />
                    </div>
                    <div>
                      <label className="input-label">Department *</label>
                      <select
                        value={facultyForm.department}
                        onChange={(e) => setFacultyForm((f) => ({ ...f, department: e.target.value }))}
                        className="input text-base text-slate-900 text-left"
                      >
                        {departmentOptions.map((d) => (
                          <option key={d} value={d}>{d}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="input-label">First name *</label>
                      <input
                        type="text"
                        value={facultyForm.firstName}
                        onChange={(e) => setFacultyForm((f) => ({ ...f, firstName: e.target.value }))}
                        required
                        className="input text-base text-slate-900 text-left"
                        placeholder="First name"
                      />
                    </div>
                    <div>
                      <label className="input-label">Last name *</label>
                      <input
                        type="text"
                        value={facultyForm.lastName}
                        onChange={(e) => setFacultyForm((f) => ({ ...f, lastName: e.target.value }))}
                        required
                        className="input text-base text-slate-900 text-left"
                        placeholder="Last name"
                      />
                    </div>
                    <div>
                      <label className="input-label">Designation</label>
                      <input
                        type="text"
                        value={facultyForm.designation}
                        onChange={(e) => setFacultyForm((f) => ({ ...f, designation: e.target.value }))}
                        className="input text-base text-slate-900 text-left"
                        placeholder="e.g. Associate Professor"
                      />
                    </div>
                    <div>
                      <label className="input-label">Contact (10 digits)</label>
                      <input
                        type="text"
                        value={facultyForm.contactNumber}
                        onChange={(e) => setFacultyForm((f) => ({ ...f, contactNumber: e.target.value }))}
                        maxLength={10}
                        pattern="[0-9]{10}"
                        className="input text-base text-slate-900 text-left"
                        placeholder="10-digit mobile"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="input-label">Qualification / Specialization</label>
                      <input
                        type="text"
                        value={facultyForm.qualification}
                        onChange={(e) => setFacultyForm((f) => ({ ...f, qualification: e.target.value }))}
                        className="input text-base text-slate-900 text-left"
                        placeholder="e.g. Ph.D. / Machine Learning"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="submit"
                      disabled={facultySubmitting}
                      className="btn-primary text-base font-medium"
                    >
                      {facultySubmitting ? 'Addingâ€¦' : 'Add faculty'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowAddFaculty(false)}
                      className="btn-secondary text-base font-medium"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}
              {generatedCredentials && (
                <div className="mb-4 p-4 rounded-lg border-2 border-green-200 bg-green-50">
                  <p className="text-sm font-semibold text-green-800 mb-2">Credentials generated â€” share with the faculty member:</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-slate-600">User ID (email):</span>
                      <p className="font-mono font-medium text-slate-800 break-all">{generatedCredentials.userId}</p>
                    </div>
                    <div>
                      <span className="text-slate-600">Password (auto-generated):</span>
                      <p className="font-mono font-medium text-slate-800">{generatedCredentials.password}</p>
                    </div>
                  </div>
                  <p className="text-xs text-slate-600 mt-2">Auto-generated password. Share with the faculty â€” they can log in with email and this password.</p>
                  <button
                    type="button"
                    onClick={() => setGeneratedCredentials(null)}
                    className="mt-2 text-sm text-primary-600 hover:underline"
                  >
                    Dismiss
                  </button>
                </div>
              )}
              {resetPasswordResult && (
                <div className="mb-4 p-4 rounded-lg border-2 border-green-200 bg-green-50">
                  <p className="text-sm font-semibold text-green-800 mb-2">Password reset â€” share with the user:</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-slate-600">Email:</span>
                      <p className="font-mono font-medium text-slate-800 break-all">{resetPasswordResult.email}</p>
                    </div>
                    <div>
                      <span className="text-slate-600">New password (auto-generated):</span>
                      <p className="font-mono font-medium text-slate-800">{resetPasswordResult.newPassword || 'â€”'}</p>
                    </div>
                  </div>
                  <p className="text-xs text-slate-600 mt-2">Auto-generated password. Share with the user â€” it will not be shown again.</p>
                  <button
                    type="button"
                    onClick={() => setResetPasswordResult(null)}
                    className="mt-2 text-sm text-primary-600 hover:underline"
                  >
                    Dismiss
                  </button>
                </div>
              )}
              {users.length === 0 ? (
                <p className="text-slate-500">No users to display.{userManagementRoleFilter ? ' Try changing the role filter.' : ''}</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 text-left text-slate-600">
                        <th className="pb-2 pr-4">Name</th>
                        <th className="pb-2 pr-4">Email</th>
                        <th className="pb-2 pr-4">Role</th>
                        <th className="pb-2 pr-4">Status</th>
                        <th className="pb-2">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.slice(0, 50).map((u, idx) => (
                        <tr key={u.id ?? `user-${idx}`} className="border-b border-slate-100">
                          <td className="py-2 pr-4 text-slate-800 font-medium">
                            {[u.details?.first_name, u.details?.last_name].filter(Boolean).join(' ') || u.details?.roll_number || u.email}
                          </td>
                          <td className="py-2 pr-4 text-slate-700">{u.email}</td>
                          <td className="py-2 pr-4">
                            <span className={`font-medium ${u.role === 'FACULTY' ? 'text-blue-600' : u.role === 'STUDENT' ? 'text-green-600' : 'text-slate-600'}`}>
                              {u.role}
                            </span>
                          </td>
                          <td className="py-2 pr-4">
                            {u.is_active !== false ? (
                              <span className="text-green-600">Active</span>
                            ) : (
                              <span className="text-slate-400">Inactive</span>
                            )}
                          </td>
                          <td className="py-2">
                            <button
                              type="button"
                              onClick={() => resetUserPassword(u.id)}
                              disabled={resettingPasswordUserId === u.id}
                              className="text-amber-600 hover:underline text-xs font-medium disabled:opacity-50"
                            >
                              {resettingPasswordUserId === u.id ? 'Resettingâ€¦' : 'Reset password'}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            </div>
            );
                case 'institute-profile':
                  return (
            <div className="space-y-6">
              <div className="admin-card">
                <div className="admin-card-body">
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-5">
                    <div>
                      <h2 className="admin-section-title">Institute Profile</h2>
                      <p className="admin-section-subtitle mb-0">Grand master information used for institute branding, stakeholder login screens and reports.</p>
                    </div>
                    {instituteProfile?.logo_url && (
                      <img src={instituteProfile.logo_url} alt="Institute logo" className="h-20 w-20 rounded-lg border border-slate-200 object-cover bg-white" />
                    )}
                  </div>
                </div>
              </div>

              {instituteProfileMessage && <div className="alert-success">{instituteProfileMessage}</div>}

              <form onSubmit={submitInstituteProfile} className="admin-card">
                <div className="admin-card-body space-y-6">
                  <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                    <div>
                      <label className="input-label">Institute name</label>
                      <input className="input text-base text-slate-900 text-left w-full" value={instituteProfileForm.name} onChange={(e) => setInstituteProfileForm((f) => ({ ...f, name: e.target.value }))} required />
                    </div>
                    <div>
                      <label className="input-label">Institute code</label>
                      <input className="input text-base text-slate-900 text-left w-full" value={instituteProfileForm.instituteCode} onChange={(e) => setInstituteProfileForm((f) => ({ ...f, instituteCode: e.target.value }))} />
                    </div>
                    <div>
                      <label className="input-label">Title</label>
                      <input className="input text-base text-slate-900 text-left w-full" value={instituteProfileForm.title} onChange={(e) => setInstituteProfileForm((f) => ({ ...f, title: e.target.value }))} />
                    </div>
                    <div>
                      <label className="input-label">Admin email</label>
                      <input type="email" className="input text-base text-slate-900 text-left w-full" value={instituteProfileForm.email} onChange={(e) => setInstituteProfileForm((f) => ({ ...f, email: e.target.value }))} required />
                    </div>
                    <div>
                      <label className="input-label">Contact person</label>
                      <input className="input text-base text-slate-900 text-left w-full" value={instituteProfileForm.contactName} onChange={(e) => setInstituteProfileForm((f) => ({ ...f, contactName: e.target.value }))} />
                    </div>
                    <div>
                      <label className="input-label">Parent society</label>
                      <input className="input text-base text-slate-900 text-left w-full" value={instituteProfileForm.parentSociety} onChange={(e) => setInstituteProfileForm((f) => ({ ...f, parentSociety: e.target.value }))} />
                    </div>
                    <div>
                      <label className="input-label">Established on</label>
                      <input type="date" className="input text-base text-slate-900 text-left w-full" value={instituteProfileForm.establishedOn} onChange={(e) => setInstituteProfileForm((f) => ({ ...f, establishedOn: e.target.value }))} />
                    </div>
                    <div>
                      <label className="input-label">NAAC grade</label>
                      <input className="input text-base text-slate-900 text-left w-full" value={instituteProfileForm.naacGrade} onChange={(e) => setInstituteProfileForm((f) => ({ ...f, naacGrade: e.target.value }))} placeholder="A+" />
                    </div>
                    <div>
                      <label className="input-label">NAAC accreditation year</label>
                      <input type="number" className="input text-base text-slate-900 text-left w-full" value={instituteProfileForm.naacAccreditationYear} onChange={(e) => setInstituteProfileForm((f) => ({ ...f, naacAccreditationYear: e.target.value }))} />
                    </div>
                    <label className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-3 text-sm text-slate-700">
                      <input type="checkbox" checked={instituteProfileForm.naacAccredited} onChange={(e) => setInstituteProfileForm((f) => ({ ...f, naacAccredited: e.target.checked }))} />
                      NAAC accredited
                    </label>
                    <label className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-3 text-sm text-slate-700">
                      <input type="checkbox" checked={instituteProfileForm.nbaAccredited} onChange={(e) => setInstituteProfileForm((f) => ({ ...f, nbaAccredited: e.target.checked }))} />
                      NBA accredited
                    </label>
                    <div>
                      <label className="input-label">NBA accreditation year</label>
                      <input type="number" className="input text-base text-slate-900 text-left w-full" value={instituteProfileForm.nbaAccreditationYear} onChange={(e) => setInstituteProfileForm((f) => ({ ...f, nbaAccreditationYear: e.target.value }))} />
                    </div>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                    <div className="xl:col-span-2">
                      <label className="input-label">Address</label>
                      <textarea className="input text-base text-slate-900 text-left w-full min-h-[96px]" value={instituteProfileForm.address} onChange={(e) => setInstituteProfileForm((f) => ({ ...f, address: e.target.value }))} />
                    </div>
                    <div>
                      <label className="input-label">City</label>
                      <input className="input text-base text-slate-900 text-left w-full" value={instituteProfileForm.city} onChange={(e) => setInstituteProfileForm((f) => ({ ...f, city: e.target.value }))} />
                    </div>
                    <div>
                      <label className="input-label">State</label>
                      <input className="input text-base text-slate-900 text-left w-full" value={instituteProfileForm.state} onChange={(e) => setInstituteProfileForm((f) => ({ ...f, state: e.target.value }))} />
                    </div>
                    <div>
                      <label className="input-label">Country</label>
                      <input className="input text-base text-slate-900 text-left w-full" value={instituteProfileForm.country} onChange={(e) => setInstituteProfileForm((f) => ({ ...f, country: e.target.value }))} />
                    </div>
                    <div>
                      <label className="input-label">Pincode</label>
                      <input className="input text-base text-slate-900 text-left w-full" value={instituteProfileForm.pincode} onChange={(e) => setInstituteProfileForm((f) => ({ ...f, pincode: e.target.value }))} />
                    </div>
                    <div className="xl:col-span-2">
                      <label className="input-label">Logo URL</label>
                      <input className="input text-base text-slate-900 text-left w-full" value={instituteProfileForm.logoUrl} onChange={(e) => setInstituteProfileForm((f) => ({ ...f, logoUrl: e.target.value }))} placeholder="https://..." />
                    </div>
                  </div>

                  <div className="grid gap-4 lg:grid-cols-2">
                    <div>
                      <label className="input-label">Branding sentence</label>
                      <input className="input text-base text-slate-900 text-left w-full" value={instituteProfileForm.brandingSentence} onChange={(e) => setInstituteProfileForm((f) => ({ ...f, brandingSentence: e.target.value }))} />
                    </div>
                    <div>
                      <label className="input-label">Programs offered</label>
                      <textarea className="input text-base text-slate-900 text-left w-full min-h-[96px]" value={instituteProfileForm.programsOfferedText} onChange={(e) => setInstituteProfileForm((f) => ({ ...f, programsOfferedText: e.target.value }))} placeholder="One program per line" />
                    </div>
                    <div className="lg:col-span-2">
                      <label className="input-label">Short profile</label>
                      <textarea className="input text-base text-slate-900 text-left w-full min-h-[120px]" value={instituteProfileForm.shortProfile} onChange={(e) => setInstituteProfileForm((f) => ({ ...f, shortProfile: e.target.value }))} />
                    </div>
                  </div>

                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                    <h3 className="text-sm font-semibold text-slate-800 mb-2">Program master linked to institute</h3>
                    <div className="flex flex-wrap gap-2">
                      {(instituteProfile?.program_master || degrees || []).map((program) => (
                        <span key={program.id || program.code || program.name} className="admin-badge">{program.name || program}</span>
                      ))}
                    </div>
                  </div>

                  <button type="submit" disabled={instituteProfileLoading} className="btn-primary px-5 py-2.5 text-base font-medium">
                    {instituteProfileLoading ? 'Saving...' : 'Save Institute Profile'}
                  </button>
                </div>
              </form>
            </div>
            );
                case 'billing':
                  return (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 text-left">
            <div className="card card-body md:col-span-2 space-y-6 text-left">
              <h2 className="text-lg font-semibold text-slate-800 tracking-tight text-left">Subscription & Billing</h2>
              {billingLoading ? (
                <div className="flex items-center gap-2 text-slate-500 text-left">
                  <span className="inline-block w-5 h-5 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin shrink-0" />
                  <span>Loadingâ€¦</span>
                </div>
              ) : billingSubscription ? (
                <div className="space-y-4 text-left">
                  <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 text-left">
                    <h3 className="text-sm font-medium text-slate-600 mb-2 text-left">Current plan</h3>
                    <p className="text-lg font-semibold text-slate-800 text-left">{billingSubscription.plan_name || billingSubscription.plan_slug || 'â€”'}</p>
                    <div className="mt-4 pt-4 border-t border-slate-200">
                      <h4 className="text-sm font-medium text-slate-600 mb-2">Pay with Razorpay</h4>
                      <div className="flex flex-wrap items-end gap-3">
                        <div>
                          <label className="block text-xs text-slate-500 mb-1">Amount (â‚¹)</label>
                          <input
                            type="number"
                            min="1"
                            step="0.01"
                            placeholder="e.g. 999"
                            value={razorpayAmount}
                            onChange={(e) => setRazorpayAmount(e.target.value)}
                            className="w-32 px-3 py-2 border border-slate-300 rounded-lg text-sm"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={handlePayWithRazorpay}
                          disabled={razorpayPaying}
                          className="px-4 py-2 bg-slate-800 text-white text-sm font-medium rounded-lg hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {razorpayPaying ? 'Openingâ€¦' : 'Pay with Razorpay'}
                        </button>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-4 mt-3 text-sm text-slate-600 text-left items-baseline">
                      <span>Status: <strong className="text-slate-800 font-semibold">{billingSubscription.status || 'â€”'}</strong></span>
                      <span>Billing: <strong className="text-slate-800 font-semibold">{billingSubscription.billing_cycle || 'â€”'}</strong></span>
                      <span>Expires: <strong className="text-slate-800 font-semibold">{billingSubscription.expires_at ? new Date(billingSubscription.expires_at).toLocaleDateString() : 'â€”'}</strong></span>
                    </div>
                    {(billingSubscription.max_students != null || billingSubscription.max_faculty != null) && (
                      <p className="text-xs text-slate-500 mt-2 text-left">Limits: {billingSubscription.max_students ?? 'â€”'} students, {billingSubscription.max_faculty ?? 'â€”'} faculty</p>
                    )}
                  </div>
                  {Array.isArray(billingSubscription.features) && billingSubscription.features.length > 0 && (
                    <div className="text-left">
                      <h3 className="text-sm font-medium text-slate-600 mb-2 text-left">Plan features</h3>
                      <ul className="list-disc list-inside text-sm text-slate-700 space-y-1 text-left pl-0">
                        {billingSubscription.features.map((f, i) => (
                          <li key={i} className="text-left">{typeof f === 'string' ? f : JSON.stringify(f)}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-slate-500 text-sm text-left">No subscription assigned for this institute. Contact your platform administrator.</p>
                  <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                    <h4 className="text-sm font-medium text-slate-600 mb-2">Pay with Razorpay</h4>
                    <div className="flex flex-wrap items-end gap-3">
                      <div>
                        <label className="block text-xs text-slate-500 mb-1">Amount (â‚¹)</label>
                        <input
                          type="number"
                          min="1"
                          step="0.01"
                          placeholder="e.g. 999"
                          value={razorpayAmount}
                          onChange={(e) => setRazorpayAmount(e.target.value)}
                          className="w-32 px-3 py-2 border border-slate-300 rounded-lg text-sm"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={handlePayWithRazorpay}
                        disabled={razorpayPaying}
                        className="px-4 py-2 bg-slate-800 text-white text-sm font-medium rounded-lg hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {razorpayPaying ? 'Openingâ€¦' : 'Pay with Razorpay'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
              <div className="text-left">
                <h3 className="text-sm font-medium text-slate-600 mb-3 text-left">Payment history</h3>
                {billingLoading ? (
                  <p className="text-slate-500 text-sm text-left">Loadingâ€¦</p>
                ) : billingPayments.length === 0 ? (
                  <p className="text-slate-500 text-sm text-left">No payments recorded yet.</p>
                ) : (
                  <div className="table-wrap overflow-x-auto text-left">
                    <table className="table min-w-full text-sm text-left">
                      <thead>
                        <tr className="border-b border-slate-200 text-left text-slate-600">
                          <th className="pb-2 pr-4 text-left">Amount</th>
                          <th className="pb-2 pr-4 text-left">Method</th>
                          <th className="pb-2 pr-4 text-left">Reference</th>
                          <th className="pb-2 pr-4 text-left">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {billingPayments.map((pay) => (
                          <tr key={pay.id} className="border-b border-slate-100 text-left">
                            <td className="py-2 pr-4 text-slate-800 font-medium text-left">{pay.currency} {Number(pay.amount).toLocaleString()}</td>
                            <td className="py-2 pr-4 text-slate-600 text-left">{pay.payment_method === 'credit_card' ? 'Credit Card' : pay.payment_method === 'upi' ? 'UPI' : pay.payment_method === 'cheque' ? 'Cheque' : pay.payment_method === 'cash' ? 'Cash' : pay.payment_method === 'razorpay' ? 'Razorpay' : pay.payment_method}</td>
                            <td className="py-2 pr-4 text-slate-600 text-left">{pay.reference || 'â€”'}</td>
                            <td className="py-2 pr-4 text-slate-600 text-left">{pay.paid_at ? new Date(pay.paid_at).toLocaleString() : (pay.created_at ? new Date(pay.created_at).toLocaleString() : 'â€”')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
            </div>
            );
                default:
                  return null;
              }
            })()}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}





